/**
 * AfterCare — Internationalization (i18n) system.
 * Supports 6 languages for the Bridgeport, CT patient population.
 *
 * Languages (by % of Bridgeport population):
 *   en - English (default)
 *   es - Spanish (35%)
 *   pt - Portuguese (3.6%)
 *   ht - Haitian Creole (3.1%)
 *   fr - French (2.3%)
 *   ar - Arabic (1.5%)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LangCode = 'en' | 'es' | 'pt' | 'ht' | 'fr' | 'ar';

export interface LangMeta {
  code: LangCode;
  name: string;       // English name
  nativeName: string; // Name in own language
  flag: string;       // Emoji flag
  dir: 'ltr' | 'rtl';
  speechLang: string; // BCP 47 tag for Web Speech API
  speechFallback?: string; // Fallback if primary isn't available
}

// ---------------------------------------------------------------------------
// Language metadata
// ---------------------------------------------------------------------------

export const LANGUAGES: Record<LangCode, LangMeta> = {
  en: { code: 'en', name: 'English',        nativeName: 'English',        flag: '🇺🇸', dir: 'ltr', speechLang: 'en-US' },
  es: { code: 'es', name: 'Spanish',        nativeName: 'Español',        flag: '🇲🇽', dir: 'ltr', speechLang: 'es-MX', speechFallback: 'es-US' },
  pt: { code: 'pt', name: 'Portuguese',     nativeName: 'Português',      flag: '🇧🇷', dir: 'ltr', speechLang: 'pt-BR', speechFallback: 'pt-PT' },
  ht: { code: 'ht', name: 'Haitian Creole', nativeName: 'Kreyòl Ayisyen', flag: '🇭🇹', dir: 'ltr', speechLang: 'fr-FR', speechFallback: 'fr-HT' },
  fr: { code: 'fr', name: 'French',         nativeName: 'Français',       flag: '🇫🇷', dir: 'ltr', speechLang: 'fr-FR', speechFallback: 'fr-CA' },
  ar: { code: 'ar', name: 'Arabic',         nativeName: 'العربية',         flag: '🇸🇦', dir: 'rtl', speechLang: 'ar-SA' },
};

export const LANG_ORDER: LangCode[] = ['en', 'es', 'pt', 'ht', 'fr', 'ar'];

// ---------------------------------------------------------------------------
// Current language state
// ---------------------------------------------------------------------------

const LS_KEY_LANG = 'aftercare:language';
let currentLang: LangCode = 'en';

export function getLang(): LangCode {
  return currentLang;
}

export function setLang(code: LangCode): void {
  currentLang = code;
  localStorage.setItem(LS_KEY_LANG, code);
  // Update document direction for RTL languages
  document.documentElement.dir = LANGUAGES[code].dir;
  document.documentElement.lang = code;
  // Notify listeners
  document.dispatchEvent(new CustomEvent('aftercare:lang-change', { detail: { lang: code } }));
}

export function initLang(): void {
  const saved = localStorage.getItem(LS_KEY_LANG) as LangCode | null;
  if (saved && LANGUAGES[saved]) {
    currentLang = saved;
  }
  document.documentElement.dir = LANGUAGES[currentLang].dir;
  document.documentElement.lang = currentLang;
}

export function getLangMeta(): LangMeta {
  return LANGUAGES[currentLang];
}

// ---------------------------------------------------------------------------
// Translation function
// ---------------------------------------------------------------------------

/**
 * Get a translated string by key. Supports {placeholder} interpolation.
 * Falls back to English if key is missing in current language.
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const table = STRINGS[currentLang] || STRINGS.en;
  let str = table[key] ?? STRINGS.en[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return str;
}

// ---------------------------------------------------------------------------
// Translation strings — organized by screen/feature
// ---------------------------------------------------------------------------

type StringTable = Record<string, string>;

const en: StringTable = {
  // --- Nav bar ---
  'nav.home': 'Home',
  'nav.medications': 'Medications',
  'nav.schedule': 'Schedule',
  'nav.ask': 'Ask',
  'nav.records': 'Records',

  // --- Home screen ---
  'home.tagline': 'Your guide to healing at home',
  'home.greeting.morning': 'Good morning',
  'home.greeting.afternoon': 'Good afternoon',
  'home.greeting.evening': 'Good evening',
  'home.greeting.night': 'Good night',
  'home.doses': 'doses',
  'home.dosesTaken': '{taken} of {total} doses taken today',
  'home.tookIt': '✓ Took it',
  'home.undo': 'Undo',
  'home.upNext': 'Up next',
  'home.now': 'Now',
  'home.minutes': '{n} min',
  'home.hours': '{n}h',
  'home.hoursMinutes': '{h}h {m}m',
  'home.yourMedications': 'Your {n} Medications',
  'home.askAnything': 'Ask me anything',
  'home.voiceHint': '"What do I take next?" or "Tell me about Metformin"',
  'home.howPossible': 'How Is This Possible?',
  'home.healthRecord': 'My Health Record',
  'home.healthRecordDesc': 'View your diagnoses, labs, and care plan',
  'home.selectLanguage': 'Language',

  // --- Greeting (TTS fallback) ---
  'greeting.withName': '{greeting}, {name}. Welcome to Bridgepoint AfterCare. I have your medications ready. You can ask me anything about your medications.',
  'greeting.withoutName': '{greeting}. Welcome to Bridgepoint AfterCare, your guide to healing at home. You can ask me anything about your medications.',

  // --- Tap overlay ---
  'overlay.tapToBegin': 'Tap anywhere to begin',

  // --- Med list ---
  'medList.title': 'My Medications',
  'medList.empty': 'No medications added yet.',
  'medList.emptyHint': 'Tap "Add Medication" below to get started.',
  'medList.addMedication': 'Add Medication',
  'medList.activeLabel': 'Active medications',
  'medList.addLabel': 'Add a new medication',
  'medList.frequency.once': 'Once daily',
  'medList.frequency.twice': 'Twice daily',
  'medList.frequency.thrice': '3 times daily',
  'medList.frequency.four': '4 times daily',
  'medList.frequency.everyOther': 'Every other day',
  'medList.frequency.weekly': 'Weekly',
  'medList.frequency.asNeeded': 'As needed',

  // --- Schedule ---
  'schedule.title': "Today's Schedule",
  'schedule.empty': 'No medications scheduled for today.',
  'schedule.morning': 'Morning',
  'schedule.afternoon': 'Afternoon',
  'schedule.evening': 'Evening',
  'schedule.bedtime': 'Bedtime',
  'schedule.missed': 'Missed',
  'schedule.upcoming': 'Upcoming',
  'schedule.taken': 'Taken',
  'schedule.markTaken': '✓ Mark as Taken',
  'schedule.timelineLabel': 'Medication schedule timeline',
  'schedule.markTakenLabel': 'Mark {drug} as taken',
  'schedule.iTookThis': 'I Took This',
  'schedule.skip': 'Skip',
  'schedule.skipDose': 'Skip {drug} dose',
  'schedule.noMedsToday': 'No medications scheduled for today.',
  'visual.pillAlt': '{name} pill: {color} {shape}',
  'visual.doesThisLookRight': 'Does this look right?',
  'visual.pillCard': 'Pill card for {name}',
  'visual.pillId': 'Pill identification for {name}',
  'schedule.statusLabel': 'Status: {status}',
  'timer.timeToTake': 'Time to take {drug}',
  'timer.snoozedTitle': 'Medication Reminder (Snoozed)',
  'timer.snoozedBody': 'You snoozed this reminder 15 minutes ago.',
  'voice.holdToSpeak': 'Hold to speak',
  'voice.listening': 'Listening, release to finish',
  'voice.processing': 'Processing your speech',
  'voice.errorTryAgain': 'Something went wrong. Tap to try again.',
  'home.tapToStart': 'Tap anywhere to start',
  'nav.mainNavigation': 'Main navigation',
  'notify.notifications': 'Notifications',
  'notify.dismiss': 'Dismiss notification',
  'install.title': 'Install AfterCare',
  'install.subtitle': 'Access your medications offline, anytime',
  'install.button': 'Install',
  'install.ariaLabel': 'Install AfterCare app',
  'install.dismiss': 'Dismiss install prompt',
  'install.iosStep1Prefix': '1. Tap the ',
  'install.iosShare': 'Share',
  'install.iosInSafari': ' in Safari',
  'install.iosStep2Prefix': '2. Scroll down and tap ',
  'install.iosAddToHome': '"Add to Home Screen"',
  'home.allRightsReserved': 'All Rights Reserved',

  // --- Chat ---
  'chat.title': 'Your Medical AI Specialist',
  'chat.subtitle': 'Ask about medications, labs, diagnoses, or your care plan',
  'chat.placeholder': 'Ask about your health...',
  'chat.send': 'Send',
  'chat.holdToSpeak': 'Hold to speak',
  'chat.welcomeWithMeds': 'I\'m Dr. AfterCare, your personal medical AI. I have access to your complete health record — {n} medication{s}, lab results, diagnoses, allergies, and care plan. Ask me anything.',
  'chat.welcomeNoMeds': 'I\'m Dr. AfterCare, your personal medical AI. Ask me anything about medications, side effects, interactions, or your health.',
  'chat.thinking': 'Thinking...',
  'chat.llmLoading': 'AI assistant is loading in the background. You can ask basic questions now — complex questions will use AI once ready.',
  'chat.llmReady': 'AI assistant is ready. I can now answer complex questions about your medications and health record.',
  'chat.aiDisclaimer': 'AI-generated response. Always confirm with your doctor.',
  'model_download': 'Downloading your AI assistant...',
  'model_download_once': 'This only happens once.',
  'model_ready': 'AI assistant ready',
  'model_ready_detail': 'Your questions will now be answered by AI.',
  'model_download_failed': 'AI download failed',
  'chat.lookingUp': 'Looking that up...',
  'chat.error': 'Sorry, something went wrong while looking that up. Please try again.',
  'chat.disclaimer': 'Please confirm with your doctor or pharmacist before making any changes to your care.',

  // --- Chat quick actions ---
  'chat.quick.whatDoes': 'What does {drug} do?',
  'chat.quick.sideEffects': 'Side effects of {drug}?',
  'chat.quick.howTake': 'How do I take {drug}?',
  'chat.quick.missedDose': 'What if I miss a dose of {drug}?',
  'chat.quick.whenTake': 'When do I take {drug}?',
  'chat.quick.alcohol': 'Can I drink alcohol with {drug}?',
  'chat.suggest.myMeds': 'What does my HbA1c mean?',
  'chat.suggest.sideEffects': 'Why am I taking Metformin?',
  'chat.suggest.alcohol': 'What are my allergies?',
  'chat.suggest.missedDose': 'When is my next appointment?',

  // --- Chat response strings (template fallback system) ---
  'chat.noMedsOnFile': 'You have no medications on file yet. Ask your pharmacist or doctor to help add them.',
  'chat.takingCount': 'You are currently taking {count} medication{s}:',
  'chat.whichSideEffects': 'Which medication would you like to know the side effects of? Tap one of your medications above, or say the name.',
  'chat.drugNotFound': 'I could not find a medication called "{drug}". Please check the spelling or try the generic name.',
  'chat.noSideEffectsListed': 'No side effects are listed for {drug}. This does not mean there are none -- ask your pharmacist.',
  'chat.sideEffectsOf': 'Common side effects of {drug}:',
  'chat.sideEffectsAdvice': 'If any side effect is severe or does not go away, contact your doctor.',
  'chat.drugNotFoundShort': 'I could not find a medication called "{drug}". Please check the spelling.',
  'chat.interactionWarning': 'WARNING: {drugA} and {drugB} have a known interaction.',
  'chat.severity': 'Severity: {level}',
  'chat.interactionAdvice': 'Talk to your doctor or pharmacist before combining these medications.',
  'chat.noInteractionFound': 'No known interaction was found between {drugA} and {drugB} in our database.',
  'chat.noInteractionDisclaimer': 'This does not guarantee safety. Always confirm with your pharmacist.',
  'chat.foodInteractionWarning': 'WARNING: {drug} has a known interaction with {food}.',
  'chat.foodInteractionAdvice': 'Talk to your doctor or pharmacist for guidance.',
  'chat.noFoodInteraction': 'No known {food} interaction was found for {drug} in our database.',
  'chat.askPharmacist': 'When in doubt, ask your pharmacist.',
  'chat.someMedsInteractFood': 'WARNING: Some of your medications interact with {food}:',
  'chat.noFoodInteractionAny': 'No known {food} interactions were found for any of your current medications.',
  'chat.whichDescription': 'Which medication would you like to know about? Tap one above or say the name.',
  'chat.noDescription': 'No description available for this medication.',
  'chat.whichDosage': 'Which medication? Tap one above or say "how do I take metformin?"',
  'chat.noDosageInstructions': 'No dosage instructions available. Please consult your pharmacist.',
  'chat.howToTake': 'How to take {drug}:',
  'chat.whichMissedDose': 'Which medication did you miss a dose of? Tap one above or say the name.',
  'chat.noMissedDoseGuidance': 'No specific missed-dose guidance is available. Contact your pharmacist for advice.',
  'chat.ifMissedDose': 'If you missed a dose of {drug}:',
  'chat.noMedsSchedule': 'You have no medications on file yet.',
  'chat.yourSchedule': 'Your medication schedule:',
  'chat.scheduleFrequency': 'Frequency: {frequency}',
  'chat.scheduleLabel': 'Schedule: {times}',
  'chat.drugNotInList': '"{drug}" is not in your medication list. You can ask "what are my medications?" to see what is on file.',
  'chat.unknownHelp': 'I\'m your personal medical AI specialist. I can help with your medications, lab results, diagnoses, allergies, and care plan. Try asking:\n\n  - "What does my HbA1c mean?"\n  - "Why am I taking Metformin?"\n  - "What are my allergies?"\n  - "When is my next appointment?"\n  - "Tell me about my discharge instructions"\n  - "What are the side effects of Lisinopril?"\n  - "Can I drink alcohol with my medications?"',
  'chat.basedOn': 'Based on: {sources}',
  'chat.ariaConversation': 'Conversation messages',
  'chat.ariaHoldToSpeak': 'Hold to speak',
  'chat.ariaInputPlaceholder': 'Type a question about your medications',
  'chat.ariaUserMessage': 'Your message',
  'chat.ariaAssistantMessage': 'Assistant response',
  'chat.ariaReplay': 'Replay this answer',
  'chat.andMore': '...and {count} more.',

  // --- Drug info ---
  'drugInfo.back': '← Back',
  'drugInfo.backAriaLabel': 'Go back to medications list',
  'drugInfo.notFound': 'Drug information not found.',
  'drugInfo.overviewAriaLabel': 'Drug overview for {drug}',
  'drugInfo.alsoKnownAs': 'Also known as: {brands}',
  'drugInfo.readAll': '🔊 Read All Information',
  'drugInfo.readAllAriaLabel': 'Read all drug information aloud',
  'drugInfo.description': 'Description',
  'drugInfo.howToTake': 'How to Take',
  'drugInfo.sideEffects': 'Side Effects',
  'drugInfo.warnings': 'Warnings',
  'drugInfo.foodInteractions': 'Food Interactions',
  'drugInfo.storage': 'Storage',
  'drugInfo.missedDose': 'Missed Dose',
  'drugInfo.noInfo': 'No information available.',
  'drugInfo.speakThis': '🔊 Speak this',
  'drugInfo.speakAriaLabel': 'Read {section} aloud',
  'drugInfo.expandAriaLabel': 'Expand {section} section',
  'drugInfo.collapseAriaLabel': 'Collapse {section} section',
  'drugInfo.tapEnlarge': 'Tap to enlarge',
  'drugInfo.tapClose': 'Tap anywhere to close',
  // TTS prefixes for read-all
  'drugInfo.tts.howToTake': 'How to take: ',
  'drugInfo.tts.sideEffects': 'Side effects include: ',
  'drugInfo.tts.warnings': 'Warnings: ',
  'drugInfo.tts.foodInteractions': 'Food interactions: ',
  'drugInfo.tts.storage': 'Storage: ',
  'drugInfo.tts.missedDose': 'If you miss a dose: ',
  'drugInfo.tts.noInstructions': 'No instructions available.',

  // --- Setup ---
  'setup.step1.title': 'What medication do you take?',
  'setup.step1.hint': 'Type the name or hold the microphone button to say it.',
  'setup.step1.placeholder': 'e.g. Metformin, Lisinopril...',
  'setup.step2.title': 'Is this your medication?',
  'setup.step2.yes': 'Yes, this is correct',
  'setup.step2.no': 'No, search again',
  'setup.step3.title': 'How often do you take it?',
  'setup.step3.once': 'Once a day',
  'setup.step3.twice': 'Twice a day',
  'setup.step3.thrice': '3 times a day',
  'setup.step3.four': '4 times a day',
  'setup.step3.everyOther': 'Every other day',
  'setup.step3.weekly': 'Once a week',
  'setup.step3.asNeeded': 'As needed',
  'setup.step4.title': 'What time do you take it?',
  'setup.step4.asNeededNote': 'Since this is taken as needed, no scheduled times are required.',
  'setup.step4.continue': 'Continue',
  'setup.step4.hint': 'Select a preset or set a custom time for each dose.',
  'setup.step4.morning': 'Morning (8:00 AM)',
  'setup.step4.noon': 'Noon (12:00 PM)',
  'setup.step4.afternoon': 'Afternoon (2:00 PM)',
  'setup.step4.evening': 'Evening (6:00 PM)',
  'setup.step4.bedtime': 'Bedtime (10:00 PM)',
  'setup.step4.custom': 'Custom time:',
  'setup.step5.title': 'Any special instructions?',
  'setup.step5.hint': 'For example: "Take with food" or "Take on empty stomach"',
  'setup.step5.placeholder': 'e.g. Take with food, avoid grapefruit...',
  'setup.step5.skip': 'Skip',
  'setup.step6.title': 'Confirm Your Medication',
  'setup.step6.medication': 'Medication',
  'setup.step6.strength': 'Strength',
  'setup.step6.frequency': 'Frequency',
  'setup.step6.times': 'Times',
  'setup.step6.instructions': 'Instructions',
  'setup.step6.notSpecified': 'Not specified',
  'setup.step6.save': 'Save Medication',
  'setup.step6.saving': 'Saving...',
  'setup.step6.edit': 'Edit Details',
  'setup.progress': 'Step {n} of {total}',
  'setup.back': '← Back',
  'setup.cancel': 'Cancel',
  'setup.noResults': 'No medications found. Try a different spelling.',
  'setup.success': '{drug} has been added!',
  'setup.successSpeak': '{drug} has been added to your medications.',
  'setup.error': 'Failed to save medication. Please try again.',
  'setup.dose': 'Dose {n}',
  'setup.step1.speak': 'What medication do you take? Say the name or type it in.',
  'setup.step2.speak': 'Is {drug} your medication?',
  'setup.step3.speak': 'How often do you take {drug}?',
  'setup.step4.speak': 'What time do you take your medication?',
  'setup.step5.speak': 'Do you have any special instructions for this medication? You can skip this step.',
  'setup.step6.speak': 'Please review and confirm your medication details.',
  'setup.step6.asNeeded': 'As needed',
  'setup.step6.freqOnce': 'Once daily',
  'setup.step6.freqTwice': 'Twice daily',
  'setup.step6.freqThrice': '3 times daily',
  'setup.step6.freqFour': '4 times daily',
  'setup.step6.freqEveryOther': 'Every other day',
  'setup.step6.freqWeekly': 'Weekly',
  'setup.step6.freqAsNeeded': 'As needed',
  'setup.backAriaLabel': 'Go back to previous step',
  'setup.cancelAriaLabel': 'Cancel medication setup',
  'setup.searchResults': 'Search results',

  // --- Camera ---
  'camera.title': 'Identify a Pill',
  'camera.instruction': 'Point your camera at the pill and tap the capture button.',
  'camera.starting': 'Starting camera...',
  'camera.ready': 'Camera ready. Position the pill in the center.',
  'camera.noAccess': 'Unable to access camera. Please check permissions.',
  'camera.capture': 'Capture',
  'camera.captureAgain': 'Capture Again',
  'camera.analyzing': 'Analyzing...',
  'camera.looksLike': 'This looks like:',
  'camera.couldNotIdentify': 'Could not identify this pill.',
  'camera.viewDrugInfo': 'View Full Drug Info',
  'camera.disclaimer': 'This identification is for reference only. Always verify with your pharmacist.',
  'camera.tips': 'Tips for better results:',
  'camera.tip1': 'Place pill on a plain, light surface',
  'camera.tip2': 'Ensure good lighting',
  'camera.tip3': 'Hold camera steady and close',
  'camera.tip4': 'Show any imprint or markings',
  'camera.analyzingStatus': 'Analyzing pill image...',
  'camera.identifiedStatus': 'Pill identified. See results below.',
  'camera.couldNotIdentifyStatus': 'Could not identify the pill. Try again with better lighting.',
  'camera.timeoutStatus': 'Analysis timed out. Try again.',
  'camera.noAccessSpeak': 'Unable to access the camera. Please check your camera permissions.',
  'camera.noAccessNotify': 'Camera access denied or unavailable.',
  'camera.looksLikeSpeak': 'This looks like {drug} with {confidence} percent confidence.',
  'camera.couldNotIdentifySpeak': 'I could not identify this pill. Please try again with better lighting.',
  'camera.confidence': '{n}% confidence',
  'camera.capturedAlt': 'Captured pill image',

  // --- EMR screen ---
  'emr.title': 'Health Record',
  'emr.allergies': 'Allergies',
  'emr.diagnoses': 'Diagnoses',
  'emr.labResults': 'Lab Results',
  'emr.vitals': 'Vital Signs',
  'emr.discharge': 'Discharge Summary',
  'emr.careGoals': 'Care Goals',
  'emr.appointments': 'Appointments',
  'emr.immunizations': 'Immunizations',
  'emr.insurance': 'Insurance',
  'emr.emergency': 'Emergency Contacts',
  'emr.directives': 'Advance Directives',
  'emr.noData': 'No health record data available.',
  'emr.noRecord': 'No medical record loaded.',
  'emr.noRecordHint': 'Your EMR data will appear here once loaded.',

  // --- EMR detail strings ---
  'emr.dob': 'DOB: ',
  'emr.mrn': 'MRN: ',
  'emr.nkda': 'NKDA (No Known Drug Allergies)',
  'emr.active': 'active',
  'emr.resolved': 'resolved',
  'emr.inProgress': 'in progress',
  'emr.notStarted': 'not started',
  'emr.severity.mild': 'mild',
  'emr.severity.moderate': 'moderate',
  'emr.severity.severe': 'severe',
  'emr.severity.life-threatening': 'life-threatening',
  'emergency.call911': 'This sounds like it could be a medical emergency. Please call 911 right now. Do not wait. If someone is near you, ask them to call. Your safety comes first.',
  'emergency.crisis': 'I hear you, and I am glad you told me. Please call or text 988 right now. That is the Suicide and Crisis Lifeline. They are available all day and all night, and they can help. You are not alone.',
  'emergency.callDoctor': "I'm concerned about what you're describing. This is something your doctor should know about right away. Please call your doctor's office as soon as possible. If you cannot reach them, please go to urgent care. Do not wait to see if it gets worse.",
  'camera.color': 'Color',
  'camera.shape': 'Shape',
  'camera.imprint': 'Imprint',
  'emr.onset': 'Onset: ',
  'emr.abnormal': 'abnormal',
  'emr.allNormal': 'All normal',
  'emr.ref': 'Ref: ',
  'emr.noVitals': 'No vital signs recorded.',
  'emr.none': 'None',
  'emr.dose': 'Dose',
  'emr.other': 'Other',

  // --- Lab badges ---
  'emr.lab.normal': 'Normal',
  'emr.lab.high': 'High',
  'emr.lab.low': 'Low',
  'emr.lab.criticalHigh': 'Critical High',
  'emr.lab.criticalLow': 'Critical Low',

  // --- Vital sign labels ---
  'emr.vital.bloodPressure': 'Blood Pressure',
  'emr.vital.heartRate': 'Heart Rate',
  'emr.vital.temperature': 'Temperature',
  'emr.vital.o2Saturation': 'O2 Saturation',
  'emr.vital.weight': 'Weight',
  'emr.vital.bmi': 'BMI',
  'emr.vital.respRate': 'Resp. Rate',
  'emr.vital.painLevel': 'Pain Level',

  // --- Discharge summary ---
  'emr.hospitalCourse': 'Hospital Course',
  'emr.dischargeCondition': 'Discharge Condition',
  'emr.warningSigns': 'Warning Signs to Watch',

  // --- Care plan ---
  'emr.goals': 'goals',
  'emr.current': 'Current: ',
  'emr.target': 'Target: ',
  'emr.achieved': 'achieved',

  // --- Insurance fields ---
  'emr.ins.provider': 'Provider',
  'emr.ins.plan': 'Plan',
  'emr.ins.memberId': 'Member ID',
  'emr.ins.groupNumber': 'Group #',
  'emr.ins.effective': 'Effective',
  'emr.ins.copayPrimary': 'Copay (Primary)',
  'emr.ins.copaySpecialist': 'Copay (Specialist)',
  'emr.ins.copayRx': 'Copay (Rx)',

  // --- Emergency contacts ---
  'emr.primaryContact': 'Primary',
  'emr.callContact': 'Call {name}',

  // --- Advance directives ---
  'emr.codeStatus': 'Code Status',
  'emr.livingWill': 'Living Will',
  'emr.powerOfAttorney': 'Power of Attorney',
  'emr.poaName': 'POA Name',
  'emr.poaPhone': 'POA Phone',
  'emr.lastReviewed': 'Last Reviewed',
  'emr.yes': 'Yes',
  'emr.no': 'No',

  // --- Errors ---
  'error.somethingWrong': 'Something went wrong',
  'error.tryAgain': 'Please try again or go back to the home screen.',
  'error.goHome': 'Go Home',
  'error.voiceNotReady': 'Voice system is not ready yet. Please wait.',
  'error.micAccess': 'Could not access microphone.',
  'error.notUnderstood': 'Could not understand. Please try again.',

  // --- Model download ---
  'model_downloading': 'Downloading AI assistant...',

  // --- LLM retry ---
  'llm.retry': 'Try Again',

  // --- Voice/TTS feedback ---
  'chat.voiceNotUnderstood': "I didn't catch that. Please try again.",
  'chat.audioUnavailable': 'Audio unavailable',

  // --- Drug purposes (for home screen pill tiles) ---
  'purpose.bloodSugar': 'Blood sugar',
  'purpose.bloodPressure': 'Blood pressure',
  'purpose.cholesterol': 'Cholesterol',
  'purpose.stomachAcid': 'Stomach acid',
  'purpose.nervePain': 'Nerve pain',
  'purpose.moodAnxiety': 'Mood & anxiety',
  'purpose.thyroid': 'Thyroid',
  'purpose.painRelief': 'Pain relief',
  'purpose.painSwelling': 'Pain & swelling',
  'purpose.heartProtection': 'Heart protection',
  'purpose.bloodClots': 'Blood clots',
  'purpose.fluidRemoval': 'Fluid removal',
  'purpose.inflammation': 'Inflammation',
  'purpose.breathing': 'Breathing',

  // --- Voice responder ---
  'voice.noMedsForDose': 'You have no medications set up yet. Would you like to add some?',
  'voice.allDoneToday': "You've completed all your doses for today. Great job! Your next doses start tomorrow morning.",
  'voice.takeNow': "It's time to take your {drug} right now.",
  'voice.nextIn': 'Your next medication is {drug}, coming up in about {n} minutes at {time}.',
  'voice.nextAt': 'Your next medication is {drug} at {time}, about {n} hour{s} from now.',
  'voice.scheduleCount': 'You have {n} medication{s} scheduled today.',
  'voice.loggedTaken': "Got it! I've recorded that you took your {drug}. Well done!",
  'voice.loggedSkipped': "Okay, I've noted that you skipped your {drug}. If you're having trouble with it, please talk to your doctor.",
  'voice.help': "Here's what I can help you with. You can ask me: What do I take next? Tell me about a medication. What are the side effects? Can I eat certain foods with this? I forgot to take my dose. Why am I taking this? You can also say: I took my medication, to log your dose.",
  'voice.unknown': "I'm sorry, I didn't quite understand that. You can ask me things like: What do I take next? Tell me about my medications. What are the side effects? Or just say Help.",
  'voice.sideEffects': 'The common side effects of {drug} include: {effects}. If any of these bother you or don\'t go away, please talk to your doctor.',
  'voice.noSideEffects': "I don't have specific side effect information for {drug}. Please check with your pharmacist or doctor.",
  'voice.missedDose': 'If you missed a dose of {drug}: {advice}',
  'voice.missedDoseFallback': "If you missed a dose of {drug}, take it as soon as you remember. But if it's almost time for your next dose, skip the missed one. Never take a double dose to make up for it.",
  'voice.drugNotFound': "I couldn't find information about {drug}. Could you try saying the name again?",
  'voice.whichMed': 'Which medication are you asking about? Just say the name and I\'ll look it up.',
  'voice.stopTaking': "Please don't stop taking any medication without talking to your doctor first. Stopping suddenly can sometimes cause problems.",
  'voice.alcohol': "It's generally recommended to avoid alcohol while taking {drug}. Please check with your doctor for specific guidance about your situation.",
  'voice.gettingReady': "I'm getting ready to help you with that. Please try again in a moment.",
  'voice.drugInfoHowToTake': "Here's how to take it: {instructions}",
  'voice.foodInteractions': 'For {drug}, be aware of these food interactions: {foods}.',
  'voice.noFoodInteractions': "I don't have specific food interaction information for {drug}. In general, follow the instructions your pharmacist gave you.",
  'voice.storage': 'For {drug}: {info}',
  'voice.storageFallback': 'Store {drug} at room temperature, away from moisture and heat. Keep it in the original container with the lid tightly closed.',
  'voice.warnings': 'Important warnings for {drug}: {warnings}. If you have any concerns, please contact your doctor.',
  'voice.noWarnings': "I don't have specific warnings for {drug}. Always follow your doctor's instructions.",
  'voice.whyTaking': "You're taking {drug} because {reason}",
  'voice.whyTakingFallback': '{drug} was prescribed by your doctor to help with your condition. If you\'d like to know more about why you\'re taking it, please ask your doctor or pharmacist.',
  'voice.howWorks': "Here's how {drug} works: {description}",
  'voice.howWorksFallback': "I don't have detailed information about how {drug} works in your body. Your pharmacist would be a great person to ask about this.",
  'voice.graceful': "That's a great question about {topic}. I don't have detailed information about that right now, but your pharmacist or doctor would be the best person to ask. Is there anything else about your medications I can help with?",
  'voice.didntHear': "I didn't hear anything. Could you try again?",
  'voice.youSaid': 'You said: "{text}"',
  'voice.somethingWrong': "I'm sorry, something went wrong. Please try asking again.",
  'voice.thatMedication': 'that medication',
  'voice.yourMedication': 'your medication',
  'voice.whichMedFood': 'Which medication are you asking about food interactions for?',
  'voice.whichMedStorage': 'Which medication would you like storage instructions for?',
  'voice.whichMedInteractions': 'Which medication are you asking about drug interactions for?',
  'voice.whichMedWhy': 'Which medication are you wondering about?',
  'voice.whichMedHow': 'Which medication would you like to know about?',
  'voice.whichMedPillId': "I'd need to know which pill you're looking at. Can you tell me the name on the pill?",
  'voice.pillIdentify': '{drug} is a {color} {shape}{imprint}. Does that match what you\'re looking at?',
  'voice.pill': 'pill',
  'voice.pillImprint': ' with "{imprint}" on it',
  'voice.driving': 'Some medications can cause drowsiness. Check the label on your {drug} or ask your pharmacist if it\'s safe to drive while taking it.',
  'voice.genericVsBrand': '{drug} is the generic name. The brand names include: {brands}. They contain the same active ingredient and work the same way.',
  'voice.addMedication': 'To add a new medication, go to the Settings tab. Your doctor or pharmacist can help you set up new medications in the app.',
  'voice.topicOverdose': 'overdose concerns',
  'voice.topicPregnancy': 'medication safety during pregnancy',
  'voice.topicAlcohol': 'alcohol interactions',
  'voice.topicDriving': 'driving while on medication',
  'voice.topicGenericVsBrand': 'generic vs brand medications',
  'voice.topicCost': 'medication costs',
  'voice.andJoiner': ' and ',
  'voice.scheduleItem': '{drug} at {times}.',

  // --- Conversation state machine (voice.conv.*) ---
  'voice.conv.cancelled': 'Okay, cancelled.',
  'voice.conv.logTakenConfirm': 'Got it. Logging that you took your {drug}.',
  'voice.conv.logSkippedConfirm': 'Understood. Logging that you skipped your {drug}.',
  'voice.conv.confirmed': 'Confirmed.',
  'voice.conv.showEmr': 'Opening your health record.',
  'voice.conv.labQuestion': 'Let me look at your lab results.',
  'voice.conv.emrDiagnosis': 'Here are your diagnoses.',
  'voice.conv.emrFollowUp': 'Let me check your upcoming appointments.',
  'voice.conv.emrDischargeInfo': 'Here is your discharge information.',
  'voice.conv.nextDose': 'Let me check your next dose.',
  'voice.conv.whichMedInfo': 'Which medication would you like to know about?',
  'voice.conv.drugInfo': 'Pulling up information on {drug}.',
  'voice.conv.whichMedSideEffects': 'Which medication are you asking about side effects for?',
  'voice.conv.sideEffectsNav': 'Here are the side effects for {drug}.',
  'voice.conv.whichMedInteractions': 'Which medication would you like to check for interactions?',
  'voice.conv.interactionsNav': 'Checking interactions for {drug}.',
  'voice.conv.whichMedFoodInteractions': 'Which medication are you asking about food interactions for?',
  'voice.conv.foodInteractionsNav': 'Here are the food interactions for {drug}.',
  'voice.conv.whichMedTaken': 'Which medication did you take?',
  'voice.conv.confirmTaken': 'Did you say you took your {drug}? Say yes to confirm.',
  'voice.conv.whichMedSkipped': 'Which medication are you skipping?',
  'voice.conv.confirmSkipped': 'You want to skip your {drug}? Say yes to confirm.',
  'voice.conv.pillShowDrug': 'Showing you what {drug} looks like.',
  'voice.conv.pillCamera': 'Opening the pill identifier camera.',
  'voice.conv.showSchedule': 'Here is your medication schedule.',
  'voice.conv.addMedication': 'Opening the medication setup.',
  'voice.conv.help': 'Here is what I can do. Say "next dose" to see your upcoming medication. Say "tell me about" followed by a medication name for details. Say "I took my" followed by the medication to log a dose. Say "skip" to mark a dose as skipped. Say "schedule" to see all your medications. Say "side effects" or "interactions" for safety information. Say "add" to set up a new medication. You can also ask me things like "why am I taking this," "what if I miss a dose," "can I drink alcohol with this," "can I drive on this," "how should I store this," or "is the generic the same." I am here to help you understand your medications.',
  'voice.conv.unknown': 'I did not quite understand that. Say "help" to hear what I can do.',
  'voice.conv.whichMedWhyTaking': 'Which medication are you asking about? Say the name and I can tell you what it is commonly used for.',
  'voice.conv.drugNotInDb': 'I do not have {drug} in my database right now. Your pharmacist can explain what it is prescribed for.',
  'voice.conv.noDescriptionOnFile': 'I do not have a detailed description for {drug} on file',
  'voice.conv.whyTaking': '{drug} is commonly prescribed for the following. {description}. Your doctor prescribed this based on your specific health needs. If you have questions about why you are taking it, that is a great conversation to have at your next visit.',
  'voice.conv.whichMedMissed': 'Which medication did you miss? Tell me the name and I can look up the guidance.',
  'voice.conv.missedDoseInfo': 'Here is the guidance for a missed dose of {drug}. {advice}. If you are not sure what to do, your pharmacist can help you figure it out with a quick call.',
  'voice.conv.missedDoseFallback': 'I do not have specific missed-dose instructions for {drug}. As a general rule, take it as soon as you remember unless it is almost time for your next dose. Do not double up. If you are unsure, call your pharmacist.',
  'voice.conv.whichMedConcern': 'Which medication are you concerned about? Tell me the name and the symptom, and I will check.',
  'voice.conv.drugNotInDbConcern': 'I do not have {drug} in my database. If you are experiencing something that worries you, contact your doctor or pharmacist.',
  'voice.conv.symptomKnown': '{symptom} is a known side effect of {drug}. This is usually mild and may get better as your body adjusts. If it is severe or does not go away, contact your doctor. That is a good question to bring up.',
  'voice.conv.symptomUnknown': '{symptom} is not a commonly listed side effect of {drug}. It could be unrelated, but it is worth mentioning to your doctor at your next visit. If it feels serious, do not wait.',
  'voice.conv.sideEffectsList': 'The most common side effects of {drug} include {effects}. If you are experiencing something specific, tell me the symptom and I can check. And if anything feels serious, contact your doctor right away.',
  'voice.conv.noSideEffectsList': 'I do not have a side effects list for {drug} on file. Your pharmacist can walk you through what to watch for.',
  'voice.conv.whichMedHowWorks': 'Which medication would you like to understand better? Say the name and I will explain how it works.',
  'voice.conv.howWorksFallback': 'I do not have a detailed explanation of how {drug} works. Your pharmacist is a great resource for understanding your medications.',
  'voice.conv.howWorks': 'Here is how {drug} works. {description}. If you want to understand more about how it helps you specifically, your doctor can explain based on your health history.',
  'voice.conv.whichMedStorage': 'Which medication are you asking about storage for?',
  'voice.conv.storageInfo': 'Here is how to store {drug}. {info}. Proper storage helps make sure your medication stays effective.',
  'voice.conv.storageFallback': 'I do not have specific storage instructions for {drug}. Most medications should be kept at room temperature, away from moisture and direct sunlight. Check the label or ask your pharmacist to be sure.',
  'voice.conv.yourMedication': 'your medication',
  'voice.conv.overdose': 'If you think you have taken too much {drug}, call Poison Control right away at 1-800-222-1222. If you feel seriously unwell or are having trouble breathing, call 911. Do not wait for symptoms to appear. It is always better to call and be safe.',
  'voice.conv.whichMedPregnancy': 'Which medication are you asking about? This is an important question, and I want to give you the right information.',
  'voice.conv.pregnancyWarning': 'Here is what the label says about {drug} and pregnancy. {warning}. Please talk to your doctor about this as soon as possible. They can help you weigh the benefits and risks for your situation.',
  'voice.conv.pregnancyFallback': 'Talk to your doctor about taking {drug} during pregnancy or while breastfeeding. This is an important conversation to have as soon as possible. Your doctor can help you decide what is safest for you and your baby.',
  'voice.conv.whichMedAlcohol': 'Which medication are you asking about? I can check if there are any alcohol interactions.',
  'voice.conv.alcoholInteraction': 'Here is what you should know about alcohol and {drug}. {info}. If you have questions about how much is safe, your pharmacist can give you practical guidance.',
  'voice.conv.alcoholWarning': 'There is a warning about alcohol with {drug}. {warning}. Talk to your pharmacist if you want more details about what this means for you.',
  'voice.conv.alcoholFallback': 'Check with your pharmacist about drinking alcohol with {drug}. Many medications can interact with alcohol in ways that are not obvious. They can give you clear guidance.',
  'voice.conv.whichMedDriving': 'Which medication are you asking about? I can check if it might affect your ability to drive.',
  'voice.conv.drivingCaution': '{drug} may cause drowsiness or dizziness. Be careful driving or operating machinery until you know how it affects you. This is especially important when you first start taking it or after a dose change.',
  'voice.conv.drivingOk': '{drug} is not typically known to affect driving, but everyone responds differently. Be cautious until you know how it affects you, especially when you first start taking it. If you feel drowsy or dizzy, do not drive.',
  'voice.conv.anyMedication': 'any medication',
  'voice.conv.stopTaking': 'Never stop taking {drug} without talking to your doctor first. Some medications need to be reduced gradually. Stopping suddenly could cause withdrawal effects or your condition could get worse. If cost or side effects are the reason you are thinking about stopping, tell your doctor. There may be other options.',
  'voice.conv.whichMedGeneric': 'Which medication are you asking about? I can tell you about the generic and brand name versions.',
  'voice.conv.genericVsBrand': 'Generic and brand-name medications contain the same active ingredient and work the same way. The FDA requires generics to be equally safe and effective. {drug} is the same medicine as {brands}. The pills might look different in color or shape, but the medicine inside is the same. If you have concerns, your pharmacist can explain the differences.',
  'voice.conv.genericVsBrandFallback': 'Generic and brand-name medications contain the same active ingredient and work the same way. The FDA requires generics to be equally safe and effective. The pills might look different, but the medicine is the same. Your pharmacist can show you exactly what to expect.',
  'voice.conv.cost': 'I do not have pricing information, but your pharmacist can help you find the best price. Ask them about generic options, manufacturer coupons, or patient assistance programs. Many drug makers offer savings programs that your pharmacist can help you find.',

  'chat.gettingReady': "I'm getting ready to help you with that. Please try again in a moment.",
};

const es: StringTable = {
  // --- Nav bar ---
  'nav.home': 'Inicio',
  'nav.medications': 'Medicamentos',
  'nav.schedule': 'Horario',
  'nav.ask': 'Preguntar',
  'nav.records': 'Historial',

  // --- Home screen ---
  'home.tagline': 'Tu guía para sanar en casa',
  'home.greeting.morning': 'Buenos días',
  'home.greeting.afternoon': 'Buenas tardes',
  'home.greeting.evening': 'Buenas noches',
  'home.greeting.night': 'Buenas noches',
  'home.doses': 'dosis',
  'home.dosesTaken': '{taken} de {total} dosis tomadas hoy',
  'home.tookIt': '✓ Lo tomé',
  'home.undo': 'Deshacer',
  'home.upNext': 'Próximo',
  'home.now': 'Ahora',
  'home.minutes': '{n} min',
  'home.hours': '{n}h',
  'home.hoursMinutes': '{h}h {m}m',
  'home.yourMedications': 'Tus {n} Medicamentos',
  'home.askAnything': 'Pregúntame lo que quieras',
  'home.voiceHint': '"¿Qué tomo después?" o "Háblame del Metformín"',
  'home.howPossible': '¿Cómo Es Esto Posible?',
  'home.healthRecord': 'Mi Historial Médico',
  'home.healthRecordDesc': 'Ver diagnósticos, laboratorios y plan de cuidado',
  'home.selectLanguage': 'Idioma',

  // --- Greeting ---
  'greeting.withName': '{greeting}, {name}. Bienvenida a Bridgepoint AfterCare. Tus medicamentos están listos. Puedes preguntarme lo que quieras sobre tus medicamentos.',
  'greeting.withoutName': '{greeting}. Bienvenido a Bridgepoint AfterCare, tu guía para sanar en casa. Puedes preguntarme lo que quieras sobre tus medicamentos.',

  // --- Tap overlay ---
  'overlay.tapToBegin': 'Toca en cualquier lugar para comenzar',

  // --- Med list ---
  'medList.title': 'Mis Medicamentos',
  'medList.empty': 'No se han agregado medicamentos todavía.',
  'medList.emptyHint': 'Toca "Agregar Medicamento" para comenzar.',
  'medList.addMedication': 'Agregar Medicamento',
  'medList.activeLabel': 'Medicamentos activos',
  'medList.addLabel': 'Agregar un nuevo medicamento',
  'medList.frequency.once': 'Una vez al día',
  'medList.frequency.twice': 'Dos veces al día',
  'medList.frequency.thrice': '3 veces al día',
  'medList.frequency.four': '4 veces al día',
  'medList.frequency.everyOther': 'Cada dos días',
  'medList.frequency.weekly': 'Semanal',
  'medList.frequency.asNeeded': 'Según sea necesario',

  // --- Schedule ---
  'schedule.title': 'Horario de Hoy',
  'schedule.empty': 'No hay medicamentos programados para hoy.',
  'schedule.morning': 'Mañana',
  'schedule.afternoon': 'Tarde',
  'schedule.evening': 'Noche',
  'schedule.bedtime': 'Hora de dormir',
  'schedule.missed': 'Perdido',
  'schedule.upcoming': 'Próximo',
  'schedule.taken': 'Tomado',
  'schedule.markTaken': '✓ Marcar como Tomado',
  'schedule.timelineLabel': 'Cronograma de medicamentos',
  'schedule.markTakenLabel': 'Marcar {drug} como tomado',
  'schedule.iTookThis': 'Lo Tomé',
  'schedule.skip': 'Omitir',
  'schedule.skipDose': 'Omitir dosis de {drug}',
  'schedule.noMedsToday': 'No hay medicamentos programados para hoy.',
  'visual.pillAlt': 'Pastilla de {name}: {color} {shape}',
  'visual.doesThisLookRight': '¿Se ve correcto?',
  'visual.pillCard': 'Tarjeta de pastilla para {name}',
  'visual.pillId': 'Identificación de pastilla para {name}',
  'schedule.statusLabel': 'Estado: {status}',
  'timer.timeToTake': 'Hora de tomar {drug}',
  'timer.snoozedTitle': 'Recordatorio de Medicamento (Pospuesto)',
  'timer.snoozedBody': 'Pospusiste este recordatorio hace 15 minutos.',
  'voice.holdToSpeak': 'Mantén presionado para hablar',
  'voice.listening': 'Escuchando, suelta para terminar',
  'voice.processing': 'Procesando tu voz',
  'voice.errorTryAgain': 'Algo salió mal. Toca para intentar de nuevo.',
  'home.tapToStart': 'Toca en cualquier lugar para comenzar',
  'nav.mainNavigation': 'Navegación principal',
  'notify.notifications': 'Notificaciones',
  'notify.dismiss': 'Descartar notificación',
  'install.title': 'Instalar AfterCare',
  'install.subtitle': 'Accede a tus medicamentos sin conexión, en cualquier momento',
  'install.button': 'Instalar',
  'install.ariaLabel': 'Instalar la aplicación AfterCare',
  'install.dismiss': 'Cerrar aviso de instalación',
  'install.iosStep1Prefix': '1. Toca el botón ',
  'install.iosShare': 'Compartir',
  'install.iosInSafari': ' en Safari',
  'install.iosStep2Prefix': '2. Desplaza hacia abajo y toca ',
  'install.iosAddToHome': '"Agregar a la pantalla de inicio"',
  'home.allRightsReserved': 'Todos los Derechos Reservados',

  // --- Chat ---
  'chat.title': 'Tu Especialista Médico con IA',
  'chat.subtitle': 'Pregunta sobre medicamentos, laboratorios, diagnósticos o tu plan de cuidado',
  'chat.placeholder': 'Pregunta sobre tu salud...',
  'chat.send': 'Enviar',
  'chat.holdToSpeak': 'Mantén presionado para hablar',
  'chat.welcomeWithMeds': 'Soy la Dra. AfterCare, tu médica personal con IA. Tengo acceso a tu historial médico completo — {n} medicamento{s}, resultados de laboratorio, diagnósticos, alergias y plan de cuidado. Pregúntame lo que necesites.',
  'chat.welcomeNoMeds': 'Soy la Dra. AfterCare, tu médica personal con IA. Pregúntame sobre medicamentos, efectos secundarios, interacciones o tu salud.',
  'chat.thinking': 'Pensando...',
  'chat.llmLoading': 'El asistente de IA se está cargando. Puede hacer preguntas básicas ahora.',
  'chat.llmReady': 'El asistente de IA está listo. Puedo responder preguntas complejas sobre sus medicamentos.',
  'chat.lookingUp': 'Buscando...',
  'chat.error': 'Lo siento, algo salió mal. Por favor intenta de nuevo.',
  'chat.disclaimer': 'Por favor confirme con su médico o farmacéutico antes de hacer cambios en su tratamiento.',

  // --- Chat quick actions ---
  'chat.quick.whatDoes': '¿Qué hace {drug}?',
  'chat.quick.sideEffects': '¿Efectos secundarios de {drug}?',
  'chat.quick.howTake': '¿Cómo tomo {drug}?',
  'chat.quick.missedDose': '¿Qué pasa si no tomo {drug}?',
  'chat.quick.whenTake': '¿Cuándo tomo {drug}?',
  'chat.quick.alcohol': '¿Puedo tomar alcohol con {drug}?',
  'chat.suggest.myMeds': '¿Qué significa mi HbA1c?',
  'chat.suggest.sideEffects': '¿Por qué tomo Metformín?',
  'chat.suggest.alcohol': '¿Cuáles son mis alergias?',
  'chat.suggest.missedDose': '¿Cuándo es mi próxima cita?',

  // --- Chat response strings (template fallback system) ---
  'chat.noMedsOnFile': 'No tiene medicamentos registrados aún. Pida a su farmacéutico o médico que le ayude a agregarlos.',
  'chat.takingCount': 'Actualmente está tomando {count} medicamento{s}:',
  'chat.whichSideEffects': '¿De qué medicamento desea conocer los efectos secundarios? Toque uno de sus medicamentos arriba o diga el nombre.',
  'chat.drugNotFound': 'No pude encontrar un medicamento llamado "{drug}". Verifique la ortografía o intente con el nombre genérico.',
  'chat.noSideEffectsListed': 'No se han registrado efectos secundarios para {drug}. Esto no significa que no los tenga -- consulte a su farmacéutico.',
  'chat.sideEffectsOf': 'Efectos secundarios comunes de {drug}:',
  'chat.sideEffectsAdvice': 'Si algún efecto secundario es grave o no desaparece, contacte a su médico.',
  'chat.drugNotFoundShort': 'No pude encontrar un medicamento llamado "{drug}". Verifique la ortografía.',
  'chat.interactionWarning': 'ADVERTENCIA: {drugA} y {drugB} tienen una interacción conocida.',
  'chat.severity': 'Gravedad: {level}',
  'chat.interactionAdvice': 'Hable con su médico o farmacéutico antes de combinar estos medicamentos.',
  'chat.noInteractionFound': 'No se encontró ninguna interacción conocida entre {drugA} y {drugB} en nuestra base de datos.',
  'chat.noInteractionDisclaimer': 'Esto no garantiza seguridad. Siempre confirme con su farmacéutico.',
  'chat.foodInteractionWarning': 'ADVERTENCIA: {drug} tiene una interacción conocida con {food}.',
  'chat.foodInteractionAdvice': 'Hable con su médico o farmacéutico para orientación.',
  'chat.noFoodInteraction': 'No se encontró interacción conocida de {food} para {drug} en nuestra base de datos.',
  'chat.askPharmacist': 'En caso de duda, consulte a su farmacéutico.',
  'chat.someMedsInteractFood': 'ADVERTENCIA: Algunos de sus medicamentos interactúan con {food}:',
  'chat.noFoodInteractionAny': 'No se encontraron interacciones conocidas de {food} con ninguno de sus medicamentos actuales.',
  'chat.whichDescription': '¿Sobre qué medicamento desea saber? Toque uno arriba o diga el nombre.',
  'chat.noDescription': 'No hay descripción disponible para este medicamento.',
  'chat.whichDosage': '¿Qué medicamento? Toque uno arriba o diga "¿cómo tomo metformina?"',
  'chat.noDosageInstructions': 'No hay instrucciones de dosificación disponibles. Consulte a su farmacéutico.',
  'chat.howToTake': 'Cómo tomar {drug}:',
  'chat.whichMissedDose': '¿De qué medicamento se olvidó una dosis? Toque uno arriba o diga el nombre.',
  'chat.noMissedDoseGuidance': 'No hay orientación específica para dosis olvidada. Contacte a su farmacéutico para consejo.',
  'chat.ifMissedDose': 'Si se olvidó una dosis de {drug}:',
  'chat.noMedsSchedule': 'No tiene medicamentos registrados aún.',
  'chat.yourSchedule': 'Su horario de medicamentos:',
  'chat.scheduleFrequency': 'Frecuencia: {frequency}',
  'chat.scheduleLabel': 'Horario: {times}',
  'chat.drugNotInList': '"{drug}" no está en su lista de medicamentos. Puede preguntar "¿cuáles son mis medicamentos?" para ver los que tiene registrados.',
  'chat.unknownHelp': 'Soy su especialista médico personal con IA. Puedo ayudarle con sus medicamentos, resultados de laboratorio, diagnósticos, alergias y plan de cuidado. Intente preguntar:\n\n  - "¿Qué significa mi HbA1c?"\n  - "¿Por qué tomo Metformina?"\n  - "¿Cuáles son mis alergias?"\n  - "¿Cuándo es mi próxima cita?"\n  - "Cuéntame sobre mis instrucciones de alta"\n  - "¿Cuáles son los efectos secundarios del Lisinopril?"\n  - "¿Puedo tomar alcohol con mis medicamentos?"',
  'chat.basedOn': 'Basado en: {sources}',
  'chat.ariaConversation': 'Mensajes de conversación',
  'chat.ariaHoldToSpeak': 'Mantener presionado para hablar',
  'chat.ariaInputPlaceholder': 'Escriba una pregunta sobre sus medicamentos',
  'chat.ariaUserMessage': 'Su mensaje',
  'chat.ariaAssistantMessage': 'Respuesta del asistente',
  'chat.ariaReplay': 'Repetir esta respuesta',
  'chat.andMore': '...y {count} más.',

  // --- Drug info ---
  'drugInfo.back': '← Volver',
  'drugInfo.backAriaLabel': 'Volver a la lista de medicamentos',
  'drugInfo.notFound': 'Información del medicamento no encontrada.',
  'drugInfo.overviewAriaLabel': 'Resumen del medicamento {drug}',
  'drugInfo.alsoKnownAs': 'También conocido como: {brands}',
  'drugInfo.readAll': '🔊 Leer Toda la Información',
  'drugInfo.readAllAriaLabel': 'Leer toda la información del medicamento en voz alta',
  'drugInfo.description': 'Descripción',
  'drugInfo.howToTake': 'Cómo Tomarlo',
  'drugInfo.sideEffects': 'Efectos Secundarios',
  'drugInfo.warnings': 'Advertencias',
  'drugInfo.foodInteractions': 'Interacciones con Alimentos',
  'drugInfo.storage': 'Almacenamiento',
  'drugInfo.missedDose': 'Dosis Perdida',
  'drugInfo.noInfo': 'No hay información disponible.',
  'drugInfo.speakThis': '🔊 Leer esto',
  'drugInfo.speakAriaLabel': 'Leer {section} en voz alta',
  'drugInfo.expandAriaLabel': 'Expandir sección {section}',
  'drugInfo.collapseAriaLabel': 'Contraer sección {section}',
  'drugInfo.tapEnlarge': 'Toca para ampliar',
  'drugInfo.tapClose': 'Toca en cualquier lugar para cerrar',
  'drugInfo.tts.howToTake': 'Cómo tomar: ',
  'drugInfo.tts.sideEffects': 'Los efectos secundarios incluyen: ',
  'drugInfo.tts.warnings': 'Advertencias: ',
  'drugInfo.tts.foodInteractions': 'Interacciones con alimentos: ',
  'drugInfo.tts.storage': 'Almacenamiento: ',
  'drugInfo.tts.missedDose': 'Si olvida una dosis: ',
  'drugInfo.tts.noInstructions': 'No hay instrucciones disponibles.',

  // --- Setup ---
  'setup.step1.title': '¿Qué medicamento tomas?',
  'setup.step1.hint': 'Escribe el nombre o mantén presionado el micrófono para decirlo.',
  'setup.step1.placeholder': 'ej. Metformín, Lisinopril...',
  'setup.step2.title': '¿Es este tu medicamento?',
  'setup.step2.yes': 'Sí, es correcto',
  'setup.step2.no': 'No, buscar de nuevo',
  'setup.step3.title': '¿Con qué frecuencia lo tomas?',
  'setup.step3.once': 'Una vez al día',
  'setup.step3.twice': 'Dos veces al día',
  'setup.step3.thrice': '3 veces al día',
  'setup.step3.four': '4 veces al día',
  'setup.step3.everyOther': 'Cada dos días',
  'setup.step3.weekly': 'Una vez a la semana',
  'setup.step3.asNeeded': 'Según sea necesario',
  'setup.step4.title': '¿A qué hora lo tomas?',
  'setup.step4.asNeededNote': 'Como se toma según sea necesario, no se requieren horarios.',
  'setup.step4.continue': 'Continuar',
  'setup.step4.hint': 'Selecciona un horario predeterminado o establece una hora personalizada.',
  'setup.step4.morning': 'Mañana (8:00 AM)',
  'setup.step4.noon': 'Mediodía (12:00 PM)',
  'setup.step4.afternoon': 'Tarde (2:00 PM)',
  'setup.step4.evening': 'Noche (6:00 PM)',
  'setup.step4.bedtime': 'Antes de dormir (10:00 PM)',
  'setup.step4.custom': 'Hora personalizada:',
  'setup.step5.title': '¿Instrucciones especiales?',
  'setup.step5.hint': 'Por ejemplo: "Tomar con comida" o "Tomar con el estómago vacío"',
  'setup.step5.placeholder': 'ej. Tomar con comida, evitar toronja...',
  'setup.step5.skip': 'Omitir',
  'setup.step6.title': 'Confirma Tu Medicamento',
  'setup.step6.medication': 'Medicamento',
  'setup.step6.strength': 'Dosis',
  'setup.step6.frequency': 'Frecuencia',
  'setup.step6.times': 'Horarios',
  'setup.step6.instructions': 'Instrucciones',
  'setup.step6.notSpecified': 'No especificado',
  'setup.step6.save': 'Guardar Medicamento',
  'setup.step6.saving': 'Guardando...',
  'setup.step6.edit': 'Editar Detalles',
  'setup.progress': 'Paso {n} de {total}',
  'setup.back': '← Volver',
  'setup.cancel': 'Cancelar',
  'setup.noResults': 'No se encontraron medicamentos. Intenta con otra ortografía.',
  'setup.success': '¡{drug} ha sido agregado!',
  'setup.successSpeak': '{drug} ha sido agregado a tus medicamentos.',
  'setup.error': 'Error al guardar el medicamento. Por favor intenta de nuevo.',
  'setup.dose': 'Dosis {n}',
  'setup.step1.speak': '¿Qué medicamento tomas? Di el nombre o escríbelo.',
  'setup.step2.speak': '¿Es {drug} tu medicamento?',
  'setup.step3.speak': '¿Con qué frecuencia tomas {drug}?',
  'setup.step4.speak': '¿A qué hora tomas tu medicamento?',
  'setup.step5.speak': '¿Tienes instrucciones especiales para este medicamento? Puedes omitir este paso.',
  'setup.step6.speak': 'Por favor revisa y confirma los detalles de tu medicamento.',
  'setup.step6.asNeeded': 'Según sea necesario',
  'setup.step6.freqOnce': 'Una vez al día',
  'setup.step6.freqTwice': 'Dos veces al día',
  'setup.step6.freqThrice': '3 veces al día',
  'setup.step6.freqFour': '4 veces al día',
  'setup.step6.freqEveryOther': 'Cada dos días',
  'setup.step6.freqWeekly': 'Semanal',
  'setup.step6.freqAsNeeded': 'Según sea necesario',
  'setup.backAriaLabel': 'Volver al paso anterior',
  'setup.cancelAriaLabel': 'Cancelar configuración de medicamento',
  'setup.searchResults': 'Resultados de búsqueda',

  // --- Camera ---
  'camera.title': 'Identificar una Pastilla',
  'camera.instruction': 'Apunta tu cámara a la pastilla y toca el botón de captura.',
  'camera.starting': 'Iniciando cámara...',
  'camera.ready': 'Cámara lista. Coloca la pastilla en el centro.',
  'camera.noAccess': 'No se puede acceder a la cámara. Verifica los permisos.',
  'camera.capture': 'Capturar',
  'camera.captureAgain': 'Capturar de Nuevo',
  'camera.analyzing': 'Analizando...',
  'camera.looksLike': 'Esto parece ser:',
  'camera.couldNotIdentify': 'No se pudo identificar esta pastilla.',
  'camera.viewDrugInfo': 'Ver Información Completa',
  'camera.disclaimer': 'Esta identificación es solo como referencia. Siempre verifica con tu farmacéutico.',
  'camera.tips': 'Consejos para mejores resultados:',
  'camera.tip1': 'Coloca la pastilla sobre una superficie clara y plana',
  'camera.tip2': 'Asegura buena iluminación',
  'camera.tip3': 'Mantén la cámara estable y cerca',
  'camera.tip4': 'Muestra cualquier marca o inscripción',
  'camera.analyzingStatus': 'Analizando imagen de la pastilla...',
  'camera.identifiedStatus': 'Pastilla identificada. Ver resultados abajo.',
  'camera.couldNotIdentifyStatus': 'No se pudo identificar la pastilla. Intenta con mejor iluminación.',
  'camera.timeoutStatus': 'El análisis expiró. Intenta de nuevo.',
  'camera.noAccessSpeak': 'No se puede acceder a la cámara. Por favor verifica los permisos de tu cámara.',
  'camera.noAccessNotify': 'Acceso a la cámara denegado o no disponible.',
  'camera.looksLikeSpeak': 'Esto parece ser {drug} con {confidence} por ciento de confianza.',
  'camera.couldNotIdentifySpeak': 'No pude identificar esta pastilla. Por favor intenta de nuevo con mejor iluminación.',
  'camera.confidence': '{n}% de confianza',
  'camera.capturedAlt': 'Imagen capturada de la pastilla',

  // --- Chat (cont) ---
  'chat.aiDisclaimer': 'Respuesta generada por IA. Siempre confirme con su médico.',
  'chat.voiceNotUnderstood': 'No escuché eso. Por favor intenta de nuevo.',
  'chat.audioUnavailable': 'Audio no disponible',

  // --- Model download ---
  'model_downloading': 'Descargando asistente de IA...',
  'model_download_once': 'Esto solo ocurre una vez.',
  'model_ready': 'Asistente de IA listo',
  'model_ready_detail': 'Tus preguntas ahora serán respondidas por IA.',
  'model_download_failed': 'La descarga de IA falló',

  // --- LLM retry ---
  'llm.retry': 'Intentar de Nuevo',

  // --- EMR screen ---
  'emr.title': 'Historial Médico',
  'emr.allergies': 'Alergias',
  'emr.diagnoses': 'Diagnósticos',
  'emr.labResults': 'Resultados de Laboratorio',
  'emr.vitals': 'Signos Vitales',
  'emr.discharge': 'Resumen de Alta',
  'emr.careGoals': 'Metas de Cuidado',
  'emr.appointments': 'Citas',
  'emr.immunizations': 'Vacunas',
  'emr.insurance': 'Seguro',
  'emr.emergency': 'Contactos de Emergencia',
  'emr.directives': 'Directivas Anticipadas',
  'emr.noData': 'No hay datos de historial médico.',
  'emr.noRecord': 'No hay historial médico cargado.',
  'emr.noRecordHint': 'Los datos de su historial médico aparecerán aquí una vez cargados.',

  // --- EMR detail strings ---
  'emr.dob': 'Fecha de nac.: ',
  'emr.mrn': 'NHC: ',
  'emr.nkda': 'SDCA (Sin Alergias Conocidas a Medicamentos)',
  'emr.active': 'activo',
  'emr.resolved': 'resuelto',
  'emr.inProgress': 'en progreso',
  'emr.notStarted': 'no iniciado',
  'emr.severity.mild': 'leve',
  'emr.severity.moderate': 'moderada',
  'emr.severity.severe': 'grave',
  'emr.severity.life-threatening': 'potencialmente mortal',
  'emergency.call911': 'Esto suena como una emergencia médica. Por favor llame al 911 ahora mismo. No espere. Si hay alguien cerca, pídales que llamen. Su seguridad es lo primero.',
  'emergency.crisis': 'Te escucho y me alegra que me lo hayas dicho. Por favor llama o envía un mensaje de texto al 988 ahora mismo. Es la Línea de Prevención del Suicidio y Crisis. Están disponibles las 24 horas del día, los 7 días de la semana, y pueden ayudarte. No estás solo.',
  'emergency.callDoctor': 'Me preocupa lo que describes. Esto es algo que su médico debe saber de inmediato. Por favor llame al consultorio de su médico lo antes posible. Si no puede comunicarse, vaya a atención urgente. No espere a ver si empeora.',
  'camera.color': 'Color',
  'camera.shape': 'Forma',
  'camera.imprint': 'Inscripción',
  'emr.onset': 'Inicio: ',
  'emr.abnormal': 'anormal',
  'emr.allNormal': 'Todo normal',
  'emr.ref': 'Ref: ',
  'emr.noVitals': 'No hay signos vitales registrados.',
  'emr.none': 'Ninguno',
  'emr.dose': 'Dosis',
  'emr.other': 'Otros',

  // --- Lab badges ---
  'emr.lab.normal': 'Normal',
  'emr.lab.high': 'Alto',
  'emr.lab.low': 'Bajo',
  'emr.lab.criticalHigh': 'Crítico Alto',
  'emr.lab.criticalLow': 'Crítico Bajo',

  // --- Vital sign labels ---
  'emr.vital.bloodPressure': 'Presión Arterial',
  'emr.vital.heartRate': 'Frecuencia Cardíaca',
  'emr.vital.temperature': 'Temperatura',
  'emr.vital.o2Saturation': 'Saturación O2',
  'emr.vital.weight': 'Peso',
  'emr.vital.bmi': 'IMC',
  'emr.vital.respRate': 'Frec. Respiratoria',
  'emr.vital.painLevel': 'Nivel de Dolor',

  // --- Discharge summary ---
  'emr.hospitalCourse': 'Evolución Hospitalaria',
  'emr.dischargeCondition': 'Condición al Alta',
  'emr.warningSigns': 'Signos de Alarma',

  // --- Care plan ---
  'emr.goals': 'metas',
  'emr.current': 'Actual: ',
  'emr.target': 'Objetivo: ',
  'emr.achieved': 'logrado',

  // --- Insurance fields ---
  'emr.ins.provider': 'Proveedor',
  'emr.ins.plan': 'Plan',
  'emr.ins.memberId': 'ID de Miembro',
  'emr.ins.groupNumber': 'Nro. de Grupo',
  'emr.ins.effective': 'Vigente desde',
  'emr.ins.copayPrimary': 'Copago (Primario)',
  'emr.ins.copaySpecialist': 'Copago (Especialista)',
  'emr.ins.copayRx': 'Copago (Rx)',

  // --- Emergency contacts ---
  'emr.primaryContact': 'Principal',
  'emr.callContact': 'Llamar a {name}',

  // --- Advance directives ---
  'emr.codeStatus': 'Estado de Código',
  'emr.livingWill': 'Testamento Vital',
  'emr.powerOfAttorney': 'Poder Notarial',
  'emr.poaName': 'Nombre del Apoderado',
  'emr.poaPhone': 'Teléfono del Apoderado',
  'emr.lastReviewed': 'Última Revisión',
  'emr.yes': 'Sí',
  'emr.no': 'No',

  // --- Errors ---
  'error.somethingWrong': 'Algo salió mal',
  'error.tryAgain': 'Por favor intenta de nuevo o regresa a la pantalla principal.',
  'error.goHome': 'Ir al Inicio',
  'error.voiceNotReady': 'El sistema de voz no está listo todavía. Por favor espera.',
  'error.micAccess': 'No se pudo acceder al micrófono.',
  'error.notUnderstood': 'No se pudo entender. Por favor intenta de nuevo.',

  // --- Drug purposes ---
  'purpose.bloodSugar': 'Azúcar en sangre',
  'purpose.bloodPressure': 'Presión arterial',
  'purpose.cholesterol': 'Colesterol',
  'purpose.stomachAcid': 'Ácido estomacal',
  'purpose.nervePain': 'Dolor nervioso',
  'purpose.moodAnxiety': 'Ánimo y ansiedad',
  'purpose.thyroid': 'Tiroides',
  'purpose.painRelief': 'Alivio del dolor',
  'purpose.painSwelling': 'Dolor e hinchazón',
  'purpose.heartProtection': 'Protección del corazón',
  'purpose.bloodClots': 'Coágulos de sangre',
  'purpose.fluidRemoval': 'Eliminación de líquidos',
  'purpose.inflammation': 'Inflamación',
  'purpose.breathing': 'Respiración',

  // --- Voice responder ---
  'voice.noMedsForDose': 'No tienes medicamentos configurados todavía. ¿Te gustaría agregar algunos?',
  'voice.allDoneToday': '¡Has completado todas tus dosis de hoy! ¡Muy bien! Tus próximas dosis comienzan mañana por la mañana.',
  'voice.takeNow': 'Es hora de tomar tu {drug} ahora mismo.',
  'voice.nextIn': 'Tu próximo medicamento es {drug}, en aproximadamente {n} minutos a las {time}.',
  'voice.nextAt': 'Tu próximo medicamento es {drug} a las {time}, aproximadamente en {n} hora{s}.',
  'voice.scheduleCount': 'Tienes {n} medicamento{s} programado{s} para hoy.',
  'voice.loggedTaken': '¡Listo! He registrado que tomaste tu {drug}. ¡Bien hecho!',
  'voice.loggedSkipped': 'Está bien, he notado que te saltaste tu {drug}. Si tienes problemas, habla con tu doctor.',
  'voice.help': 'Esto es lo que puedo hacer. Puedes preguntarme: ¿Qué tomo después? Háblame de un medicamento. ¿Cuáles son los efectos secundarios? ¿Puedo comer ciertos alimentos con esto? Olvidé tomar mi dosis. ¿Por qué tomo esto? También puedes decir: Tomé mi medicamento.',
  'voice.unknown': 'Lo siento, no entendí eso. Puedes preguntarme: ¿Qué tomo después? Háblame de mis medicamentos. ¿Cuáles son los efectos secundarios? O simplemente di Ayuda.',
  'voice.sideEffects': 'Los efectos secundarios comunes de {drug} incluyen: {effects}. Si alguno te molesta o no desaparece, habla con tu doctor.',
  'voice.noSideEffects': 'No tengo información específica sobre los efectos secundarios de {drug}. Consulta con tu farmacéutico o doctor.',
  'voice.missedDose': 'Si te saltaste una dosis de {drug}: {advice}',
  'voice.missedDoseFallback': 'Si te saltaste una dosis de {drug}, tómala tan pronto como lo recuerdes. Pero si ya casi es hora de tu próxima dosis, sáltala. Nunca tomes una dosis doble.',
  'voice.drugNotFound': 'No pude encontrar información sobre {drug}. ¿Podrías intentar decir el nombre de nuevo?',
  'voice.whichMed': '¿Sobre qué medicamento preguntas? Solo di el nombre y lo buscaré.',
  'voice.stopTaking': 'Por favor no dejes de tomar ningún medicamento sin hablar primero con tu doctor. Dejar de tomarlo de repente puede causar problemas.',
  'voice.alcohol': 'Generalmente se recomienda evitar el alcohol mientras tomas {drug}. Consulta con tu doctor para orientacion especifica.',
  'voice.gettingReady': 'Me estoy preparando para ayudarte con eso. Por favor intenta de nuevo en un momento.',
  'voice.drugInfoHowToTake': 'Asi es como debes tomarlo: {instructions}',
  'voice.foodInteractions': 'Para {drug}, ten en cuenta estas interacciones con alimentos: {foods}.',
  'voice.noFoodInteractions': 'No tengo informacion especifica sobre interacciones con alimentos para {drug}. En general, sigue las instrucciones de tu farmaceutico.',
  'voice.storage': 'Para {drug}: {info}',
  'voice.storageFallback': 'Guarda {drug} a temperatura ambiente, lejos de la humedad y el calor. Mantenlo en el envase original con la tapa bien cerrada.',
  'voice.warnings': 'Advertencias importantes para {drug}: {warnings}. Si tienes alguna preocupacion, por favor contacta a tu doctor.',
  'voice.noWarnings': 'No tengo advertencias especificas para {drug}. Siempre sigue las instrucciones de tu doctor.',
  'voice.whyTaking': 'Estas tomando {drug} porque {reason}',
  'voice.whyTakingFallback': '{drug} fue recetado por tu doctor para ayudar con tu condicion. Si deseas saber mas sobre por que lo tomas, consulta con tu doctor o farmaceutico.',
  'voice.howWorks': 'Asi funciona {drug}: {description}',
  'voice.howWorksFallback': 'No tengo informacion detallada sobre como funciona {drug} en tu cuerpo. Tu farmaceutico seria la mejor persona para preguntarle.',
  'voice.graceful': 'Es una gran pregunta sobre {topic}. No tengo informacion detallada sobre eso en este momento, pero tu farmaceutico o doctor seria la mejor persona para preguntar. Hay algo mas sobre tus medicamentos en lo que pueda ayudarte?',
  'voice.didntHear': 'No escuche nada. Podrias intentar de nuevo?',
  'voice.youSaid': 'Dijiste: "{text}"',
  'voice.somethingWrong': 'Lo siento, algo salio mal. Por favor intenta preguntar de nuevo.',
  'voice.thatMedication': 'ese medicamento',
  'voice.yourMedication': 'tu medicamento',
  'voice.whichMedFood': 'Sobre que medicamento preguntas respecto a interacciones con alimentos?',
  'voice.whichMedStorage': 'Para que medicamento deseas instrucciones de almacenamiento?',
  'voice.whichMedInteractions': 'Sobre que medicamento preguntas respecto a interacciones?',
  'voice.whichMedWhy': 'Sobre que medicamento tienes curiosidad?',
  'voice.whichMedHow': 'Sobre que medicamento te gustaria saber?',
  'voice.whichMedPillId': 'Necesitaria saber que pastilla estas mirando. Puedes decirme el nombre en la pastilla?',
  'voice.pillIdentify': '{drug} es una {shape} {color}{imprint}. Coincide con lo que estas mirando?',
  'voice.pill': 'pastilla',
  'voice.pillImprint': ' con "{imprint}" impreso',
  'voice.driving': 'Algunos medicamentos pueden causar somnolencia. Revisa la etiqueta de tu {drug} o preguntale a tu farmaceutico si es seguro conducir mientras lo tomas.',
  'voice.genericVsBrand': '{drug} es el nombre generico. Las marcas incluyen: {brands}. Contienen el mismo ingrediente activo y funcionan de la misma manera.',
  'voice.addMedication': 'Para agregar un nuevo medicamento, ve a la pestana de Configuracion. Tu doctor o farmaceutico puede ayudarte a configurar nuevos medicamentos en la aplicacion.',
  'voice.topicOverdose': 'preocupaciones por sobredosis',
  'voice.topicPregnancy': 'seguridad de medicamentos durante el embarazo',
  'voice.topicAlcohol': 'interacciones con alcohol',
  'voice.topicDriving': 'conducir mientras tomas medicamentos',
  'voice.topicGenericVsBrand': 'medicamentos genericos vs de marca',
  'voice.topicCost': 'costos de medicamentos',
  'voice.andJoiner': ' y ',
  'voice.scheduleItem': '{drug} a las {times}.',

  // --- Conversation state machine (voice.conv.*) ---
  'voice.conv.cancelled': 'Bien, cancelado.',
  'voice.conv.logTakenConfirm': 'Entendido. Registrando que tomaste tu {drug}.',
  'voice.conv.logSkippedConfirm': 'Entendido. Registrando que omitiste tu {drug}.',
  'voice.conv.confirmed': 'Confirmado.',
  'voice.conv.showEmr': 'Abriendo tu expediente de salud.',
  'voice.conv.labQuestion': 'Voy a revisar tus resultados de laboratorio.',
  'voice.conv.emrDiagnosis': 'Aqui estan tus diagnosticos.',
  'voice.conv.emrFollowUp': 'Voy a revisar tus proximas citas.',
  'voice.conv.emrDischargeInfo': 'Aqui esta tu informacion de alta.',
  'voice.conv.nextDose': 'Voy a revisar tu proxima dosis.',
  'voice.conv.whichMedInfo': 'Sobre cual medicamento te gustaria saber?',
  'voice.conv.drugInfo': 'Buscando informacion sobre {drug}.',
  'voice.conv.whichMedSideEffects': 'De cual medicamento preguntas sobre efectos secundarios?',
  'voice.conv.sideEffectsNav': 'Aqui estan los efectos secundarios de {drug}.',
  'voice.conv.whichMedInteractions': 'De cual medicamento te gustaria verificar interacciones?',
  'voice.conv.interactionsNav': 'Verificando interacciones de {drug}.',
  'voice.conv.whichMedFoodInteractions': 'De cual medicamento preguntas sobre interacciones con alimentos?',
  'voice.conv.foodInteractionsNav': 'Aqui estan las interacciones con alimentos de {drug}.',
  'voice.conv.whichMedTaken': 'Cual medicamento tomaste?',
  'voice.conv.confirmTaken': 'Dijiste que tomaste tu {drug}? Di si para confirmar.',
  'voice.conv.whichMedSkipped': 'Cual medicamento estas omitiendo?',
  'voice.conv.confirmSkipped': 'Quieres omitir tu {drug}? Di si para confirmar.',
  'voice.conv.pillShowDrug': 'Mostrandote como se ve {drug}.',
  'voice.conv.pillCamera': 'Abriendo la camara de identificacion de pastillas.',
  'voice.conv.showSchedule': 'Aqui esta tu horario de medicamentos.',
  'voice.conv.addMedication': 'Abriendo la configuracion de medicamentos.',
  'voice.conv.help': 'Esto es lo que puedo hacer. Di "proxima dosis" para ver tu proximo medicamento. Di "dime sobre" seguido del nombre del medicamento para mas detalles. Di "tome mi" seguido del medicamento para registrar una dosis. Di "omitir" para marcar una dosis como omitida. Di "horario" para ver todos tus medicamentos. Di "efectos secundarios" o "interacciones" para informacion de seguridad. Di "agregar" para configurar un nuevo medicamento. Tambien puedes preguntarme cosas como "por que tomo esto," "que pasa si pierdo una dosis," "puedo tomar alcohol con esto," "puedo conducir con esto," "como debo guardarlo," o "es igual el generico." Estoy aqui para ayudarte a entender tus medicamentos.',
  'voice.conv.unknown': 'No entendi bien eso. Di "ayuda" para escuchar lo que puedo hacer.',
  'voice.conv.whichMedWhyTaking': 'Sobre cual medicamento preguntas? Di el nombre y te dire para que se usa comunmente.',
  'voice.conv.drugNotInDb': 'No tengo {drug} en mi base de datos ahora. Tu farmaceutico puede explicarte para que se receta.',
  'voice.conv.noDescriptionOnFile': 'No tengo una descripcion detallada de {drug} en archivo',
  'voice.conv.whyTaking': '{drug} se receta comunmente para lo siguiente. {description}. Tu doctor lo receto basandose en tus necesidades de salud especificas. Si tienes preguntas sobre por que lo tomas, es una gran conversacion para tu proxima cita.',
  'voice.conv.whichMedMissed': 'Cual medicamento olvidaste? Dime el nombre y buscare las instrucciones.',
  'voice.conv.missedDoseInfo': 'Aqui estan las instrucciones para una dosis olvidada de {drug}. {advice}. Si no estas seguro de que hacer, tu farmaceutico puede ayudarte con una llamada rapida.',
  'voice.conv.missedDoseFallback': 'No tengo instrucciones especificas para una dosis olvidada de {drug}. Como regla general, tomala en cuanto recuerdes a menos que sea casi hora de tu proxima dosis. No dupliques la dosis. Si no estas seguro, llama a tu farmaceutico.',
  'voice.conv.whichMedConcern': 'Sobre cual medicamento te preocupas? Dime el nombre y el sintoma, y lo revisare.',
  'voice.conv.drugNotInDbConcern': 'No tengo {drug} en mi base de datos. Si estas experimentando algo que te preocupa, contacta a tu doctor o farmaceutico.',
  'voice.conv.symptomKnown': '{symptom} es un efecto secundario conocido de {drug}. Esto generalmente es leve y puede mejorar a medida que tu cuerpo se adapta. Si es severo o no desaparece, contacta a tu doctor. Es una buena pregunta para hacer.',
  'voice.conv.symptomUnknown': '{symptom} no es un efecto secundario comunmente listado de {drug}. Podria no estar relacionado, pero vale la pena mencionarlo a tu doctor en tu proxima cita. Si se siente grave, no esperes.',
  'voice.conv.sideEffectsList': 'Los efectos secundarios mas comunes de {drug} incluyen {effects}. Si estas experimentando algo especifico, dime el sintoma y puedo verificar. Y si algo se siente grave, contacta a tu doctor de inmediato.',
  'voice.conv.noSideEffectsList': 'No tengo una lista de efectos secundarios de {drug} en archivo. Tu farmaceutico puede guiarte sobre que vigilar.',
  'voice.conv.whichMedHowWorks': 'Cual medicamento te gustaria entender mejor? Di el nombre y te explicare como funciona.',
  'voice.conv.howWorksFallback': 'No tengo una explicacion detallada de como funciona {drug}. Tu farmaceutico es un gran recurso para entender tus medicamentos.',
  'voice.conv.howWorks': 'Asi es como funciona {drug}. {description}. Si quieres entender mas sobre como te ayuda especificamente, tu doctor puede explicarte basandose en tu historial de salud.',
  'voice.conv.whichMedStorage': 'De cual medicamento preguntas sobre almacenamiento?',
  'voice.conv.storageInfo': 'Asi se debe guardar {drug}. {info}. El almacenamiento adecuado ayuda a asegurar que tu medicamento siga siendo efectivo.',
  'voice.conv.storageFallback': 'No tengo instrucciones especificas de almacenamiento para {drug}. La mayoria de los medicamentos deben mantenerse a temperatura ambiente, lejos de la humedad y la luz solar directa. Revisa la etiqueta o pregunta a tu farmaceutico para estar seguro.',
  'voice.conv.yourMedication': 'tu medicamento',
  'voice.conv.overdose': 'Si crees que has tomado demasiado {drug}, llama al Centro de Control de Envenenamientos de inmediato al 1-800-222-1222. Si te sientes muy mal o tienes problemas para respirar, llama al 911. No esperes a que aparezcan sintomas. Siempre es mejor llamar y estar seguro.',
  'voice.conv.whichMedPregnancy': 'Sobre cual medicamento preguntas? Esta es una pregunta importante, y quiero darte la informacion correcta.',
  'voice.conv.pregnancyWarning': 'Esto es lo que dice la etiqueta sobre {drug} y el embarazo. {warning}. Por favor habla con tu doctor sobre esto lo antes posible. Pueden ayudarte a sopesar los beneficios y riesgos para tu situacion.',
  'voice.conv.pregnancyFallback': 'Habla con tu doctor sobre tomar {drug} durante el embarazo o la lactancia. Esta es una conversacion importante para tener lo antes posible. Tu doctor puede ayudarte a decidir que es mas seguro para ti y tu bebe.',
  'voice.conv.whichMedAlcohol': 'Sobre cual medicamento preguntas? Puedo verificar si hay interacciones con el alcohol.',
  'voice.conv.alcoholInteraction': 'Esto es lo que debes saber sobre el alcohol y {drug}. {info}. Si tienes preguntas sobre cuanto es seguro, tu farmaceutico puede darte orientacion practica.',
  'voice.conv.alcoholWarning': 'Hay una advertencia sobre el alcohol con {drug}. {warning}. Habla con tu farmaceutico si quieres mas detalles sobre lo que esto significa para ti.',
  'voice.conv.alcoholFallback': 'Consulta con tu farmaceutico sobre beber alcohol con {drug}. Muchos medicamentos pueden interactuar con el alcohol de maneras no obvias. Pueden darte orientacion clara.',
  'voice.conv.whichMedDriving': 'Sobre cual medicamento preguntas? Puedo verificar si puede afectar tu capacidad de conducir.',
  'voice.conv.drivingCaution': '{drug} puede causar somnolencia o mareo. Ten cuidado al conducir o manejar maquinaria hasta que sepas como te afecta. Esto es especialmente importante cuando empiezas a tomarlo o despues de un cambio de dosis.',
  'voice.conv.drivingOk': '{drug} normalmente no se conoce por afectar la conduccion, pero cada persona responde diferente. Ten precaucion hasta que sepas como te afecta, especialmente cuando empiezas a tomarlo. Si sientes somnolencia o mareo, no conduzcas.',
  'voice.conv.anyMedication': 'cualquier medicamento',
  'voice.conv.stopTaking': 'Nunca dejes de tomar {drug} sin hablar primero con tu doctor. Algunos medicamentos necesitan reducirse gradualmente. Detenerte de repente podria causar efectos de abstinencia o tu condicion podria empeorar. Si el costo o los efectos secundarios son la razon por la que piensas dejarlo, dile a tu doctor. Puede haber otras opciones.',
  'voice.conv.whichMedGeneric': 'Sobre cual medicamento preguntas? Puedo decirte sobre las versiones generica y de marca.',
  'voice.conv.genericVsBrand': 'Los medicamentos genericos y de marca contienen el mismo ingrediente activo y funcionan de la misma manera. La FDA requiere que los genericos sean igualmente seguros y efectivos. {drug} es el mismo medicamento que {brands}. Las pastillas pueden verse diferentes en color o forma, pero el medicamento es el mismo. Si tienes preocupaciones, tu farmaceutico puede explicar las diferencias.',
  'voice.conv.genericVsBrandFallback': 'Los medicamentos genericos y de marca contienen el mismo ingrediente activo y funcionan de la misma manera. La FDA requiere que los genericos sean igualmente seguros y efectivos. Las pastillas pueden verse diferentes, pero el medicamento es el mismo. Tu farmaceutico puede mostrarte exactamente que esperar.',
  'voice.conv.cost': 'No tengo informacion de precios, pero tu farmaceutico puede ayudarte a encontrar el mejor precio. Preguntales sobre opciones genericas, cupones del fabricante o programas de asistencia al paciente. Muchos fabricantes de medicamentos ofrecen programas de ahorro que tu farmaceutico puede ayudarte a encontrar.',

  'chat.gettingReady': 'Me estoy preparando para ayudarte con eso. Por favor intenta de nuevo en un momento.',
};

const pt: StringTable = {
  'nav.home': 'Início',
  'nav.medications': 'Medicamentos',
  'nav.schedule': 'Horário',
  'nav.ask': 'Perguntar',
  'nav.records': 'Prontuário',
  'home.tagline': 'Seu guia para curar em casa',
  'home.greeting.morning': 'Bom dia',
  'home.greeting.afternoon': 'Boa tarde',
  'home.greeting.evening': 'Boa noite',
  'home.greeting.night': 'Boa noite',
  'home.doses': 'doses',
  'home.dosesTaken': '{taken} de {total} doses tomadas hoje',
  'home.tookIt': '✓ Tomei',
  'home.undo': 'Desfazer',
  'home.upNext': 'Próximo',
  'home.now': 'Agora',
  'home.minutes': '{n} min',
  'home.hours': '{n}h',
  'home.hoursMinutes': '{h}h {m}m',
  'home.yourMedications': 'Seus {n} Medicamentos',
  'home.askAnything': 'Pergunte-me qualquer coisa',
  'home.voiceHint': '"O que tomo a seguir?" ou "Fale-me sobre Metformina"',
  'home.howPossible': 'Como Isso É Possível?',
  'home.healthRecord': 'Meu Prontuário',
  'home.healthRecordDesc': 'Ver diagnósticos, exames e plano de cuidados',
  'home.selectLanguage': 'Idioma',
  'greeting.withName': '{greeting}, {name}. Bem-vinda ao Bridgepoint AfterCare. Seus medicamentos estão prontos. Você pode me perguntar qualquer coisa sobre seus medicamentos.',
  'greeting.withoutName': '{greeting}. Bem-vindo ao Bridgepoint AfterCare, seu guia para curar em casa. Você pode me perguntar qualquer coisa sobre seus medicamentos.',
  'overlay.tapToBegin': 'Toque em qualquer lugar para começar',
  'medList.title': 'Meus Medicamentos',
  'medList.empty': 'Nenhum medicamento adicionado ainda.',
  'medList.emptyHint': 'Toque em "Adicionar Medicamento" para começar.',
  'medList.addMedication': 'Adicionar Medicamento',
  'medList.activeLabel': 'Medicamentos ativos',
  'medList.addLabel': 'Adicionar um novo medicamento',
  'schedule.title': 'Horário de Hoje',
  'schedule.empty': 'Nenhum medicamento agendado para hoje.',
  'schedule.morning': 'Manhã',
  'schedule.afternoon': 'Tarde',
  'schedule.evening': 'Noite',
  'schedule.bedtime': 'Hora de dormir',
  'schedule.missed': 'Perdido',
  'schedule.upcoming': 'Próximo',
  'schedule.taken': 'Tomado',
  'schedule.markTaken': '✓ Marcar como Tomado',
  'schedule.timelineLabel': 'Cronograma de medicamentos',
  'schedule.markTakenLabel': 'Marcar {drug} como tomado',
  'schedule.iTookThis': 'Eu Tomei',
  'schedule.skip': 'Pular',
  'schedule.skipDose': 'Pular dose de {drug}',
  'schedule.noMedsToday': 'Nenhum medicamento agendado para hoje.',
  'visual.pillAlt': 'Comprimido de {name}: {color} {shape}',
  'visual.doesThisLookRight': 'Isto parece correto?',
  'visual.pillCard': 'Cartão do comprimido de {name}',
  'visual.pillId': 'Identificação do comprimido de {name}',
  'schedule.statusLabel': 'Status: {status}',
  'timer.timeToTake': 'Hora de tomar {drug}',
  'timer.snoozedTitle': 'Lembrete de Medicamento (Adiado)',
  'timer.snoozedBody': 'Você adiou este lembrete há 15 minutos.',
  'voice.holdToSpeak': 'Segure para falar',
  'voice.listening': 'Ouvindo, solte para terminar',
  'voice.processing': 'Processando sua fala',
  'voice.errorTryAgain': 'Algo deu errado. Toque para tentar novamente.',
  'home.tapToStart': 'Toque em qualquer lugar para começar',
  'nav.mainNavigation': 'Navegação principal',
  'notify.notifications': 'Notificações',
  'notify.dismiss': 'Dispensar notificação',
  'install.title': 'Instalar AfterCare',
  'install.subtitle': 'Acesse seus medicamentos offline, a qualquer momento',
  'install.button': 'Instalar',
  'install.ariaLabel': 'Instalar o aplicativo AfterCare',
  'install.dismiss': 'Fechar aviso de instalação',
  'install.iosStep1Prefix': '1. Toque no botão ',
  'install.iosShare': 'Compartilhar',
  'install.iosInSafari': ' no Safari',
  'install.iosStep2Prefix': '2. Role para baixo e toque em ',
  'install.iosAddToHome': '"Adicionar à Tela Inicial"',
  'home.allRightsReserved': 'Todos os Direitos Reservados',
  'chat.title': 'Seu Especialista Médico com IA',
  'chat.subtitle': 'Pergunte sobre medicamentos, exames, diagnósticos ou seu plano de cuidado',
  'chat.placeholder': 'Pergunte sobre sua saúde...',
  'chat.send': 'Enviar',
  'chat.holdToSpeak': 'Segure para falar',
  'chat.thinking': 'Pensando...',
  'chat.llmLoading': 'O assistente de IA está carregando. Você pode fazer perguntas básicas agora.',
  'chat.llmReady': 'O assistente de IA está pronto. Posso responder perguntas complexas sobre seus medicamentos.',
  'chat.aiDisclaimer': 'Resposta gerada por IA. Sempre confirme com seu médico.',
  'chat.lookingUp': 'Procurando...',
  'chat.error': 'Desculpe, algo deu errado. Por favor tente novamente.',
  'chat.disclaimer': 'Por favor confirme com seu médico ou farmacêutico antes de fazer mudanças no seu tratamento.',

  // --- Chat response strings (template fallback system) ---
  'chat.noMedsOnFile': 'Você ainda não tem medicamentos registrados. Peça ao seu farmacêutico ou médico para ajudar a adicioná-los.',
  'chat.takingCount': 'Você está tomando atualmente {count} medicamento{s}:',
  'chat.whichSideEffects': 'De qual medicamento você gostaria de saber os efeitos colaterais? Toque em um dos seus medicamentos acima ou diga o nome.',
  'chat.drugNotFound': 'Não encontrei um medicamento chamado "{drug}". Verifique a ortografia ou tente o nome genérico.',
  'chat.noSideEffectsListed': 'Não há efeitos colaterais registrados para {drug}. Isso não significa que não existam -- consulte seu farmacêutico.',
  'chat.sideEffectsOf': 'Efeitos colaterais comuns de {drug}:',
  'chat.sideEffectsAdvice': 'Se algum efeito colateral for grave ou não desaparecer, entre em contato com seu médico.',
  'chat.drugNotFoundShort': 'Não encontrei um medicamento chamado "{drug}". Verifique a ortografia.',
  'chat.interactionWarning': 'ATENÇÃO: {drugA} e {drugB} têm uma interação conhecida.',
  'chat.severity': 'Gravidade: {level}',
  'chat.interactionAdvice': 'Fale com seu médico ou farmacêutico antes de combinar esses medicamentos.',
  'chat.noInteractionFound': 'Nenhuma interação conhecida foi encontrada entre {drugA} e {drugB} em nossa base de dados.',
  'chat.noInteractionDisclaimer': 'Isso não garante segurança. Sempre confirme com seu farmacêutico.',
  'chat.foodInteractionWarning': 'ATENÇÃO: {drug} tem uma interação conhecida com {food}.',
  'chat.foodInteractionAdvice': 'Fale com seu médico ou farmacêutico para orientação.',
  'chat.noFoodInteraction': 'Nenhuma interação conhecida de {food} foi encontrada para {drug} em nossa base de dados.',
  'chat.askPharmacist': 'Em caso de dúvida, consulte seu farmacêutico.',
  'chat.someMedsInteractFood': 'ATENÇÃO: Alguns dos seus medicamentos interagem com {food}:',
  'chat.noFoodInteractionAny': 'Nenhuma interação conhecida de {food} foi encontrada para nenhum dos seus medicamentos atuais.',
  'chat.whichDescription': 'Sobre qual medicamento você gostaria de saber? Toque em um acima ou diga o nome.',
  'chat.noDescription': 'Nenhuma descrição disponível para este medicamento.',
  'chat.whichDosage': 'Qual medicamento? Toque em um acima ou diga "como tomo metformina?"',
  'chat.noDosageInstructions': 'Nenhuma instrução de dosagem disponível. Consulte seu farmacêutico.',
  'chat.howToTake': 'Como tomar {drug}:',
  'chat.whichMissedDose': 'De qual medicamento você perdeu uma dose? Toque em um acima ou diga o nome.',
  'chat.noMissedDoseGuidance': 'Nenhuma orientação específica para dose perdida está disponível. Contacte seu farmacêutico para conselho.',
  'chat.ifMissedDose': 'Se você perdeu uma dose de {drug}:',
  'chat.noMedsSchedule': 'Você ainda não tem medicamentos registrados.',
  'chat.yourSchedule': 'Seu horário de medicamentos:',
  'chat.scheduleFrequency': 'Frequência: {frequency}',
  'chat.scheduleLabel': 'Horário: {times}',
  'chat.drugNotInList': '"{drug}" não está na sua lista de medicamentos. Você pode perguntar "quais são meus medicamentos?" para ver o que está registrado.',
  'chat.unknownHelp': 'Sou seu especialista médico pessoal com IA. Posso ajudar com seus medicamentos, resultados de exames, diagnósticos, alergias e plano de cuidado. Tente perguntar:\n\n  - "O que significa meu HbA1c?"\n  - "Por que estou tomando Metformina?"\n  - "Quais são minhas alergias?"\n  - "Quando é minha próxima consulta?"\n  - "Fale-me sobre minhas instruções de alta"\n  - "Quais são os efeitos colaterais do Lisinopril?"\n  - "Posso beber álcool com meus medicamentos?"',
  'chat.basedOn': 'Baseado em: {sources}',
  'chat.ariaConversation': 'Mensagens da conversa',
  'chat.ariaHoldToSpeak': 'Segure para falar',
  'chat.ariaInputPlaceholder': 'Digite uma pergunta sobre seus medicamentos',
  'chat.ariaUserMessage': 'Sua mensagem',
  'chat.ariaAssistantMessage': 'Resposta do assistente',
  'chat.ariaReplay': 'Repetir esta resposta',
  'chat.andMore': '...e mais {count}.',

  // --- Drug info ---
  'drugInfo.back': '← Voltar',
  'drugInfo.backAriaLabel': 'Voltar à lista de medicamentos',
  'drugInfo.notFound': 'Informação do medicamento não encontrada.',
  'drugInfo.overviewAriaLabel': 'Visão geral do medicamento {drug}',
  'drugInfo.readAll': '🔊 Ler Toda a Informação',
  'drugInfo.readAllAriaLabel': 'Ler todas as informações do medicamento em voz alta',
  'drugInfo.description': 'Descrição',
  'drugInfo.howToTake': 'Como Tomar',
  'drugInfo.sideEffects': 'Efeitos Colaterais',
  'drugInfo.warnings': 'Avisos',
  'drugInfo.foodInteractions': 'Interações com Alimentos',
  'drugInfo.storage': 'Armazenamento',
  'drugInfo.missedDose': 'Dose Perdida',
  'drugInfo.noInfo': 'Nenhuma informação disponível.',
  'drugInfo.speakThis': '🔊 Ler isto',
  'drugInfo.speakAriaLabel': 'Ler {section} em voz alta',
  'drugInfo.expandAriaLabel': 'Expandir seção {section}',
  'drugInfo.collapseAriaLabel': 'Recolher seção {section}',
  'drugInfo.tts.howToTake': 'Como tomar: ',
  'drugInfo.tts.sideEffects': 'Os efeitos colaterais incluem: ',
  'drugInfo.tts.warnings': 'Avisos: ',
  'drugInfo.tts.foodInteractions': 'Interações com alimentos: ',
  'drugInfo.tts.storage': 'Armazenamento: ',
  'drugInfo.tts.missedDose': 'Se esquecer uma dose: ',
  'drugInfo.tts.noInstructions': 'Nenhuma instrução disponível.',

  // --- Setup ---
  'setup.step1.title': 'Que medicamento você toma?',
  'setup.step1.hint': 'Digite o nome ou segure o botão do microfone para dizer.',
  'setup.step1.placeholder': 'ex. Metformina, Lisinopril...',
  'setup.step2.title': 'Este é o seu medicamento?',
  'setup.step2.yes': 'Sim, está correto',
  'setup.step2.no': 'Não, buscar novamente',
  'setup.step3.title': 'Com que frequência você toma?',
  'setup.step3.once': 'Uma vez ao dia',
  'setup.step3.twice': 'Duas vezes ao dia',
  'setup.step3.thrice': '3 vezes ao dia',
  'setup.step3.four': '4 vezes ao dia',
  'setup.step3.everyOther': 'A cada dois dias',
  'setup.step3.weekly': 'Uma vez por semana',
  'setup.step3.asNeeded': 'Quando necessário',
  'setup.step4.title': 'A que horas você toma?',
  'setup.step4.asNeededNote': 'Como é tomado quando necessário, não são necessários horários.',
  'setup.step4.continue': 'Continuar',
  'setup.step4.hint': 'Selecione um horário predefinido ou defina um horário personalizado.',
  'setup.step4.morning': 'Manhã (8:00)',
  'setup.step4.noon': 'Meio-dia (12:00)',
  'setup.step4.afternoon': 'Tarde (14:00)',
  'setup.step4.evening': 'Noite (18:00)',
  'setup.step4.bedtime': 'Hora de dormir (22:00)',
  'setup.step4.custom': 'Horário personalizado:',
  'setup.step5.title': 'Instruções especiais?',
  'setup.step5.hint': 'Por exemplo: "Tomar com comida" ou "Tomar com o estômago vazio"',
  'setup.step5.placeholder': 'ex. Tomar com comida, evitar toranja...',
  'setup.step5.skip': 'Pular',
  'setup.step6.title': 'Confirme Seu Medicamento',
  'setup.step6.medication': 'Medicamento',
  'setup.step6.strength': 'Dosagem',
  'setup.step6.frequency': 'Frequência',
  'setup.step6.times': 'Horários',
  'setup.step6.instructions': 'Instruções',
  'setup.step6.notSpecified': 'Não especificado',
  'setup.step6.save': 'Salvar Medicamento',
  'setup.step6.saving': 'Salvando...',
  'setup.step6.edit': 'Editar Detalhes',
  'setup.progress': 'Passo {n} de {total}',
  'setup.back': '← Voltar',
  'setup.cancel': 'Cancelar',
  'setup.noResults': 'Nenhum medicamento encontrado. Tente outra grafia.',
  'setup.success': '{drug} foi adicionado!',
  'setup.successSpeak': '{drug} foi adicionado aos seus medicamentos.',
  'setup.error': 'Falha ao salvar o medicamento. Por favor tente novamente.',
  'setup.dose': 'Dose {n}',
  'setup.step1.speak': 'Que medicamento você toma? Diga o nome ou digite.',
  'setup.step2.speak': '{drug} é o seu medicamento?',
  'setup.step3.speak': 'Com que frequência você toma {drug}?',
  'setup.step4.speak': 'A que horas você toma o seu medicamento?',
  'setup.step5.speak': 'Você tem instruções especiais para este medicamento? Pode pular este passo.',
  'setup.step6.speak': 'Por favor revise e confirme os detalhes do seu medicamento.',
  'setup.step6.asNeeded': 'Quando necessário',
  'setup.step6.freqOnce': 'Uma vez ao dia',
  'setup.step6.freqTwice': 'Duas vezes ao dia',
  'setup.step6.freqThrice': '3 vezes ao dia',
  'setup.step6.freqFour': '4 vezes ao dia',
  'setup.step6.freqEveryOther': 'A cada dois dias',
  'setup.step6.freqWeekly': 'Semanal',
  'setup.step6.freqAsNeeded': 'Quando necessário',
  'setup.backAriaLabel': 'Voltar ao passo anterior',
  'setup.cancelAriaLabel': 'Cancelar configuração do medicamento',
  'setup.searchResults': 'Resultados da pesquisa',

  // --- Camera ---
  'camera.title': 'Identificar um Comprimido',
  'camera.instruction': 'Aponte sua câmera para o comprimido e toque no botão de captura.',
  'camera.starting': 'Iniciando câmera...',
  'camera.ready': 'Câmera pronta. Posicione o comprimido no centro.',
  'camera.noAccess': 'Não é possível acessar a câmera. Verifique as permissões.',
  'camera.capture': 'Capturar',
  'camera.captureAgain': 'Capturar Novamente',
  'camera.analyzing': 'Analisando...',
  'camera.looksLike': 'Isto parece ser:',
  'camera.couldNotIdentify': 'Não foi possível identificar este comprimido.',
  'camera.viewDrugInfo': 'Ver Informação Completa',
  'camera.disclaimer': 'Esta identificação é apenas para referência. Sempre verifique com seu farmacêutico.',
  'camera.tips': 'Dicas para melhores resultados:',
  'camera.tip1': 'Coloque o comprimido em uma superfície clara e plana',
  'camera.tip2': 'Garanta boa iluminação',
  'camera.tip3': 'Segure a câmera firme e próxima',
  'camera.tip4': 'Mostre marcas ou inscrições',
  'camera.analyzingStatus': 'Analisando imagem do comprimido...',
  'camera.identifiedStatus': 'Comprimido identificado. Veja os resultados abaixo.',
  'camera.couldNotIdentifyStatus': 'Não foi possível identificar o comprimido. Tente com melhor iluminação.',
  'camera.timeoutStatus': 'A análise expirou. Tente novamente.',
  'camera.noAccessSpeak': 'Não é possível acessar a câmera. Por favor verifique as permissões da câmera.',
  'camera.noAccessNotify': 'Acesso à câmera negado ou indisponível.',
  'camera.looksLikeSpeak': 'Isto parece ser {drug} com {confidence} por cento de confiança.',
  'camera.couldNotIdentifySpeak': 'Não consegui identificar este comprimido. Por favor tente novamente com melhor iluminação.',
  'camera.confidence': '{n}% de confiança',
  'camera.capturedAlt': 'Imagem capturada do comprimido',

  'error.somethingWrong': 'Algo deu errado',
  'error.tryAgain': 'Por favor tente novamente ou volte para a tela inicial.',
  'error.goHome': 'Ir para Início',
  'error.voiceNotReady': 'O sistema de voz ainda não está pronto. Por favor aguarde.',
  'error.micAccess': 'Não foi possível acessar o microfone.',
  'error.notUnderstood': 'Não foi possível entender. Por favor tente novamente.',
  'purpose.bloodSugar': 'Açúcar no sangue',
  'purpose.bloodPressure': 'Pressão arterial',
  'purpose.cholesterol': 'Colesterol',
  'purpose.stomachAcid': 'Ácido estomacal',
  'purpose.nervePain': 'Dor nos nervos',
  'purpose.moodAnxiety': 'Humor e ansiedade',
  'purpose.thyroid': 'Tireoide',
  'purpose.painRelief': 'Alívio da dor',
  'emr.title': 'Prontuário Médico',
  'emr.allergies': 'Alergias',
  'emr.diagnoses': 'Diagnósticos',
  'emr.labResults': 'Resultados de Exames',
  'emr.vitals': 'Sinais Vitais',
  'emr.discharge': 'Resumo de Alta',
  'emr.careGoals': 'Metas de Cuidado',
  'emr.appointments': 'Consultas',
  'emr.immunizations': 'Vacinação',
  'emr.insurance': 'Seguro',
  'emr.emergency': 'Contatos de Emergência',
  'emr.directives': 'Diretivas Antecipadas',
  'emr.noData': 'Nenhum prontuário médico disponível.',
  'emr.noRecord': 'Nenhum prontuário médico carregado.',
  'emr.noRecordHint': 'Seus dados médicos aparecerão aqui quando carregados.',

  // --- EMR detail strings ---
  'emr.dob': 'Data nasc.: ',
  'emr.mrn': 'Prontuário: ',
  'emr.nkda': 'SDCA (Sem Alergias Conhecidas a Medicamentos)',
  'emr.active': 'ativo',
  'emr.resolved': 'resolvido',
  'emr.inProgress': 'em andamento',
  'emr.notStarted': 'não iniciado',
  'emr.severity.mild': 'leve',
  'emr.severity.moderate': 'moderada',
  'emr.severity.severe': 'grave',
  'emr.severity.life-threatening': 'risco de vida',
  'emergency.call911': 'Isto parece ser uma emergência médica. Por favor ligue para o 911 agora mesmo. Não espere. Se alguém está perto de você, peça para ligar. Sua segurança vem primeiro.',
  'emergency.crisis': 'Eu ouço você, e fico feliz que me contou. Por favor ligue ou envie mensagem para 988 agora mesmo. É a Linha de Prevenção ao Suicídio e Crise. Eles estão disponíveis 24 horas por dia, 7 dias por semana, e podem ajudar. Você não está sozinho.',
  'emergency.callDoctor': 'Estou preocupado com o que você está descrevendo. Isso é algo que seu médico deve saber imediatamente. Por favor ligue para o consultório do seu médico o mais rápido possível. Se não conseguir falar com eles, vá ao pronto-socorro. Não espere para ver se piora.',
  'camera.color': 'Cor',
  'camera.shape': 'Forma',
  'camera.imprint': 'Inscrição',
  'emr.onset': 'Início: ',
  'emr.abnormal': 'anormal',
  'emr.allNormal': 'Tudo normal',
  'emr.ref': 'Ref: ',
  'emr.noVitals': 'Nenhum sinal vital registrado.',
  'emr.none': 'Nenhum',
  'emr.dose': 'Dose',
  'emr.other': 'Outros',

  // --- Lab badges ---
  'emr.lab.normal': 'Normal',
  'emr.lab.high': 'Alto',
  'emr.lab.low': 'Baixo',
  'emr.lab.criticalHigh': 'Crítico Alto',
  'emr.lab.criticalLow': 'Crítico Baixo',

  // --- Vital sign labels ---
  'emr.vital.bloodPressure': 'Pressão Arterial',
  'emr.vital.heartRate': 'Frequência Cardíaca',
  'emr.vital.temperature': 'Temperatura',
  'emr.vital.o2Saturation': 'Saturação O2',
  'emr.vital.weight': 'Peso',
  'emr.vital.bmi': 'IMC',
  'emr.vital.respRate': 'Freq. Respiratória',
  'emr.vital.painLevel': 'Nível de Dor',

  // --- Discharge summary ---
  'emr.hospitalCourse': 'Evolução Hospitalar',
  'emr.dischargeCondition': 'Condição na Alta',
  'emr.warningSigns': 'Sinais de Alerta',

  // --- Care plan ---
  'emr.goals': 'metas',
  'emr.current': 'Atual: ',
  'emr.target': 'Meta: ',
  'emr.achieved': 'alcançado',

  // --- Insurance fields ---
  'emr.ins.provider': 'Convênio',
  'emr.ins.plan': 'Plano',
  'emr.ins.memberId': 'ID do Membro',
  'emr.ins.groupNumber': 'Nro. do Grupo',
  'emr.ins.effective': 'Vigente desde',
  'emr.ins.copayPrimary': 'Copagamento (Primário)',
  'emr.ins.copaySpecialist': 'Copagamento (Especialista)',
  'emr.ins.copayRx': 'Copagamento (Rx)',

  // --- Emergency contacts ---
  'emr.primaryContact': 'Principal',
  'emr.callContact': 'Ligar para {name}',

  // --- Advance directives ---
  'emr.codeStatus': 'Código de Status',
  'emr.livingWill': 'Testamento Vital',
  'emr.powerOfAttorney': 'Procuração',
  'emr.poaName': 'Nome do Procurador',
  'emr.poaPhone': 'Telefone do Procurador',
  'emr.lastReviewed': 'Última Revisão',
  'emr.yes': 'Sim',
  'emr.no': 'Não',

  'chat.welcomeWithMeds': 'Sou a Dra. AfterCare, sua médica pessoal com IA. Tenho acesso ao seu prontuário completo — {n} medicamento{s}, resultados de exames, diagnósticos, alergias e plano de cuidados. Pergunte-me o que precisar.',
  'chat.welcomeNoMeds': 'Sou a Dra. AfterCare, sua médica pessoal com IA. Pergunte-me sobre medicamentos, efeitos colaterais, interações ou sua saúde.',
  'chat.voiceNotUnderstood': 'Não entendi. Por favor, tente novamente.',
  'chat.audioUnavailable': 'Áudio indisponível',
  'chat.quick.whatDoes': 'O que {drug} faz?',
  'chat.quick.sideEffects': 'Efeitos colaterais de {drug}?',
  'chat.quick.howTake': 'Como tomo {drug}?',
  'chat.quick.missedDose': 'E se eu perder uma dose de {drug}?',
  'chat.quick.whenTake': 'Quando tomo {drug}?',
  'chat.quick.alcohol': 'Posso beber álcool com {drug}?',
  'chat.suggest.myMeds': 'O que significa meu HbA1c?',
  'chat.suggest.sideEffects': 'Por que estou tomando Metformina?',
  'chat.suggest.alcohol': 'Quais são minhas alergias?',
  'chat.suggest.missedDose': 'Quando é minha próxima consulta?',
  'medList.frequency.once': 'Uma vez ao dia',
  'medList.frequency.twice': 'Duas vezes ao dia',
  'medList.frequency.thrice': '3 vezes ao dia',
  'medList.frequency.four': '4 vezes ao dia',
  'medList.frequency.everyOther': 'Dia sim, dia não',
  'medList.frequency.weekly': 'Semanal',
  'medList.frequency.asNeeded': 'Conforme necessário',
  'drugInfo.alsoKnownAs': 'Também conhecido como: {brands}',
  'drugInfo.tapEnlarge': 'Toque para ampliar',
  'drugInfo.tapClose': 'Toque em qualquer lugar para fechar',
  'model_downloading': 'Baixando assistente de IA...',
  'model_download_once': 'Isso acontece apenas uma vez.',
  'model_ready': 'Assistente de IA pronto',
  'model_ready_detail': 'Suas perguntas agora serão respondidas por IA.',
  'model_download_failed': 'Falha no download da IA',
  'model_fallback': 'Respostas modelo serão usadas.',
  'llm.retry': 'Tentar Novamente',
  'purpose.painSwelling': 'Dor e inchaço',
  'purpose.heartProtection': 'Proteção do coração',
  'purpose.bloodClots': 'Coágulos sanguíneos',
  'purpose.fluidRemoval': 'Remoção de fluidos',
  'purpose.inflammation': 'Inflamação',
  'purpose.breathing': 'Respiração',
  'voice.noMedsForDose': 'Voce ainda nao tem medicamentos configurados. Gostaria de adicionar alguns?',
  'voice.allDoneToday': 'Voce completou todas as suas doses de hoje. Otimo trabalho! Suas proximas doses comecam amanha de manha.',
  'voice.takeNow': 'Esta na hora de tomar seu {drug} agora.',
  'voice.nextIn': 'Seu proximo medicamento e {drug}, em cerca de {n} minutos as {time}.',
  'voice.nextAt': 'Seu proximo medicamento e {drug} as {time}, em cerca de {n} hora{s}.',
  'voice.scheduleCount': 'Voce tem {n} medicamento{s} agendado{s} para hoje.',
  'voice.loggedTaken': 'Pronto! Registrei que voce tomou seu {drug}. Muito bem!',
  'voice.loggedSkipped': 'Tudo bem, anotei que voce pulou seu {drug}. Se estiver tendo dificuldades, converse com seu medico.',
  'voice.help': 'Aqui esta como posso ajudar. Voce pode me perguntar: O que tomo a seguir? Fale-me sobre um medicamento. Quais sao os efeitos colaterais? Posso comer certos alimentos com isso? Esqueci de tomar minha dose. Por que estou tomando isso? Voce tambem pode dizer: Tomei meu medicamento.',
  'voice.unknown': 'Desculpe, nao entendi. Voce pode perguntar: O que tomo a seguir? Fale-me sobre meus medicamentos. Ou diga Ajuda.',
  'voice.sideEffects': 'Os efeitos colaterais comuns de {drug} incluem: {effects}. Se algum deles incomodar ou nao passar, converse com seu medico.',
  'voice.noSideEffects': 'Nao tenho informacoes especificas sobre efeitos colaterais de {drug}. Consulte seu farmaceutico ou medico.',
  'voice.missedDose': 'Se voce perdeu uma dose de {drug}: {advice}',
  'voice.missedDoseFallback': 'Se voce perdeu uma dose de {drug}, tome assim que lembrar. Mas se ja estiver quase na hora da proxima dose, pule a que esqueceu. Nunca tome dose dupla.',
  'voice.drugNotFound': 'Nao encontrei informacoes sobre {drug}. Poderia tentar dizer o nome novamente?',
  'voice.whichMed': 'Sobre qual medicamento voce esta perguntando? Apenas diga o nome e eu buscarei.',
  'voice.stopTaking': 'Por favor nao pare de tomar nenhum medicamento sem conversar com seu medico primeiro. Parar de repente pode causar problemas.',
  'voice.alcohol': 'Geralmente e recomendado evitar alcool enquanto toma {drug}. Consulte seu medico para orientacao especifica.',
  'voice.gettingReady': 'Estou me preparando para ajuda-lo com isso. Por favor, tente novamente em um momento.',
  'voice.drugInfoHowToTake': 'Veja como tomar: {instructions}',
  'voice.foodInteractions': 'Para {drug}, esteja atento a estas interacoes com alimentos: {foods}.',
  'voice.noFoodInteractions': 'Nao tenho informacoes especificas sobre interacoes alimentares para {drug}. Em geral, siga as instrucoes do seu farmaceutico.',
  'voice.storage': 'Para {drug}: {info}',
  'voice.storageFallback': 'Guarde {drug} em temperatura ambiente, longe de umidade e calor. Mantenha no recipiente original com a tampa bem fechada.',
  'voice.warnings': 'Advertencias importantes para {drug}: {warnings}. Se tiver alguma preocupacao, entre em contato com seu medico.',
  'voice.noWarnings': 'Nao tenho advertencias especificas para {drug}. Sempre siga as instrucoes do seu medico.',
  'voice.whyTaking': 'Voce esta tomando {drug} porque {reason}',
  'voice.whyTakingFallback': '{drug} foi receitado pelo seu medico para ajudar com sua condicao. Se quiser saber mais sobre por que esta tomando, pergunte ao seu medico ou farmaceutico.',
  'voice.howWorks': 'Veja como {drug} funciona: {description}',
  'voice.howWorksFallback': 'Nao tenho informacoes detalhadas sobre como {drug} funciona no seu corpo. Seu farmaceutico seria a melhor pessoa para perguntar.',
  'voice.graceful': 'Otima pergunta sobre {topic}. Nao tenho informacoes detalhadas sobre isso agora, mas seu farmaceutico ou medico seria a melhor pessoa para perguntar. Posso ajudar com mais alguma coisa sobre seus medicamentos?',
  'voice.didntHear': 'Nao ouvi nada. Poderia tentar novamente?',
  'voice.youSaid': 'Voce disse: "{text}"',
  'voice.somethingWrong': 'Desculpe, algo deu errado. Por favor tente perguntar novamente.',
  'voice.thatMedication': 'esse medicamento',
  'voice.yourMedication': 'seu medicamento',
  'voice.whichMedFood': 'Sobre qual medicamento voce pergunta em relacao a interacoes com alimentos?',
  'voice.whichMedStorage': 'Para qual medicamento voce deseja instrucoes de armazenamento?',
  'voice.whichMedInteractions': 'Sobre qual medicamento voce pergunta em relacao a interacoes?',
  'voice.whichMedWhy': 'Sobre qual medicamento voce tem curiosidade?',
  'voice.whichMedHow': 'Sobre qual medicamento voce gostaria de saber?',
  'voice.whichMedPillId': 'Preciso saber qual pilula voce esta olhando. Pode me dizer o nome na pilula?',
  'voice.pillIdentify': '{drug} e uma {shape} {color}{imprint}. Coincide com o que voce esta olhando?',
  'voice.pill': 'pilula',
  'voice.pillImprint': ' com "{imprint}" impresso',
  'voice.driving': 'Alguns medicamentos podem causar sonolencia. Verifique o rotulo do seu {drug} ou pergunte ao seu farmaceutico se e seguro dirigir enquanto toma.',
  'voice.genericVsBrand': '{drug} e o nome generico. As marcas incluem: {brands}. Eles contem o mesmo ingrediente ativo e funcionam da mesma forma.',
  'voice.addMedication': 'Para adicionar um novo medicamento, va para a aba Configuracoes. Seu medico ou farmaceutico pode ajudar a configurar novos medicamentos no aplicativo.',
  'voice.topicOverdose': 'preocupacoes com overdose',
  'voice.topicPregnancy': 'seguranca de medicamentos durante a gravidez',
  'voice.topicAlcohol': 'interacoes com alcool',
  'voice.topicDriving': 'dirigir enquanto toma medicamentos',
  'voice.topicGenericVsBrand': 'medicamentos genericos vs de marca',
  'voice.topicCost': 'custos de medicamentos',
  'voice.andJoiner': ' e ',
  'voice.scheduleItem': '{drug} as {times}.',

  // --- Conversation state machine (voice.conv.*) ---
  'voice.conv.cancelled': 'Tudo bem, cancelado.',
  'voice.conv.logTakenConfirm': 'Entendido. Registrando que voce tomou seu {drug}.',
  'voice.conv.logSkippedConfirm': 'Entendido. Registrando que voce pulou seu {drug}.',
  'voice.conv.confirmed': 'Confirmado.',
  'voice.conv.showEmr': 'Abrindo seu prontuario de saude.',
  'voice.conv.labQuestion': 'Vou verificar seus resultados de laboratorio.',
  'voice.conv.emrDiagnosis': 'Aqui estao seus diagnosticos.',
  'voice.conv.emrFollowUp': 'Vou verificar suas proximas consultas.',
  'voice.conv.emrDischargeInfo': 'Aqui estao suas informacoes de alta.',
  'voice.conv.nextDose': 'Vou verificar sua proxima dose.',
  'voice.conv.whichMedInfo': 'Sobre qual medicamento voce gostaria de saber?',
  'voice.conv.drugInfo': 'Buscando informacoes sobre {drug}.',
  'voice.conv.whichMedSideEffects': 'De qual medicamento voce pergunta sobre efeitos colaterais?',
  'voice.conv.sideEffectsNav': 'Aqui estao os efeitos colaterais de {drug}.',
  'voice.conv.whichMedInteractions': 'De qual medicamento voce gostaria de verificar interacoes?',
  'voice.conv.interactionsNav': 'Verificando interacoes de {drug}.',
  'voice.conv.whichMedFoodInteractions': 'De qual medicamento voce pergunta sobre interacoes com alimentos?',
  'voice.conv.foodInteractionsNav': 'Aqui estao as interacoes com alimentos de {drug}.',
  'voice.conv.whichMedTaken': 'Qual medicamento voce tomou?',
  'voice.conv.confirmTaken': 'Voce disse que tomou seu {drug}? Diga sim para confirmar.',
  'voice.conv.whichMedSkipped': 'Qual medicamento voce esta pulando?',
  'voice.conv.confirmSkipped': 'Voce quer pular seu {drug}? Diga sim para confirmar.',
  'voice.conv.pillShowDrug': 'Mostrando como {drug} se parece.',
  'voice.conv.pillCamera': 'Abrindo a camera de identificacao de comprimidos.',
  'voice.conv.showSchedule': 'Aqui esta seu horario de medicamentos.',
  'voice.conv.addMedication': 'Abrindo a configuracao de medicamentos.',
  'voice.conv.help': 'Aqui esta o que posso fazer. Diga "proxima dose" para ver seu proximo medicamento. Diga "me fale sobre" seguido do nome do medicamento para detalhes. Diga "tomei meu" seguido do medicamento para registrar uma dose. Diga "pular" para marcar uma dose como pulada. Diga "horario" para ver todos os seus medicamentos. Diga "efeitos colaterais" ou "interacoes" para informacoes de seguranca. Diga "adicionar" para configurar um novo medicamento. Voce tambem pode me perguntar coisas como "por que estou tomando isso," "e se eu perder uma dose," "posso beber alcool com isso," "posso dirigir com isso," "como devo guardar isso," ou "o generico e igual." Estou aqui para ajuda-lo a entender seus medicamentos.',
  'voice.conv.unknown': 'Nao entendi bem. Diga "ajuda" para ouvir o que posso fazer.',
  'voice.conv.whichMedWhyTaking': 'Sobre qual medicamento voce pergunta? Diga o nome e posso dizer para que e comumente usado.',
  'voice.conv.drugNotInDb': 'Nao tenho {drug} no meu banco de dados agora. Seu farmaceutico pode explicar para que e receitado.',
  'voice.conv.noDescriptionOnFile': 'Nao tenho uma descricao detalhada de {drug} em arquivo',
  'voice.conv.whyTaking': '{drug} e comumente receitado para o seguinte. {description}. Seu medico receitou isso com base nas suas necessidades de saude especificas. Se voce tem perguntas sobre por que esta tomando, e uma otima conversa para ter na proxima consulta.',
  'voice.conv.whichMedMissed': 'Qual medicamento voce esqueceu? Diga o nome e posso buscar as orientacoes.',
  'voice.conv.missedDoseInfo': 'Aqui estao as orientacoes para uma dose esquecida de {drug}. {advice}. Se voce nao tem certeza do que fazer, seu farmaceutico pode ajudar com uma ligacao rapida.',
  'voice.conv.missedDoseFallback': 'Nao tenho instrucoes especificas para dose esquecida de {drug}. Como regra geral, tome assim que lembrar, a menos que esteja quase na hora da proxima dose. Nao duplique a dose. Se nao tiver certeza, ligue para seu farmaceutico.',
  'voice.conv.whichMedConcern': 'Sobre qual medicamento voce esta preocupado? Diga o nome e o sintoma, e verificarei.',
  'voice.conv.drugNotInDbConcern': 'Nao tenho {drug} no meu banco de dados. Se voce esta sentindo algo que o preocupa, entre em contato com seu medico ou farmaceutico.',
  'voice.conv.symptomKnown': '{symptom} e um efeito colateral conhecido de {drug}. Isso geralmente e leve e pode melhorar a medida que seu corpo se adapta. Se for grave ou nao desaparecer, entre em contato com seu medico. E uma boa pergunta para fazer.',
  'voice.conv.symptomUnknown': '{symptom} nao e um efeito colateral comumente listado de {drug}. Pode nao ter relacao, mas vale a pena mencionar ao seu medico na proxima consulta. Se parecer grave, nao espere.',
  'voice.conv.sideEffectsList': 'Os efeitos colaterais mais comuns de {drug} incluem {effects}. Se voce esta sentindo algo especifico, diga o sintoma e posso verificar. E se algo parecer grave, entre em contato com seu medico imediatamente.',
  'voice.conv.noSideEffectsList': 'Nao tenho uma lista de efeitos colaterais de {drug} em arquivo. Seu farmaceutico pode orientar sobre o que observar.',
  'voice.conv.whichMedHowWorks': 'Qual medicamento voce gostaria de entender melhor? Diga o nome e explicarei como funciona.',
  'voice.conv.howWorksFallback': 'Nao tenho uma explicacao detalhada de como {drug} funciona. Seu farmaceutico e um otimo recurso para entender seus medicamentos.',
  'voice.conv.howWorks': 'Veja como {drug} funciona. {description}. Se quiser entender mais sobre como ajuda voce especificamente, seu medico pode explicar com base no seu historico de saude.',
  'voice.conv.whichMedStorage': 'De qual medicamento voce pergunta sobre armazenamento?',
  'voice.conv.storageInfo': 'Veja como guardar {drug}. {info}. O armazenamento adequado ajuda a garantir que seu medicamento continue eficaz.',
  'voice.conv.storageFallback': 'Nao tenho instrucoes especificas de armazenamento para {drug}. A maioria dos medicamentos deve ser mantida em temperatura ambiente, longe de umidade e luz solar direta. Verifique o rotulo ou pergunte ao seu farmaceutico.',
  'voice.conv.yourMedication': 'seu medicamento',
  'voice.conv.overdose': 'Se voce acha que tomou demais de {drug}, ligue para o Centro de Controle de Envenenamento imediatamente no 1-800-222-1222. Se voce se sentir muito mal ou tiver dificuldade para respirar, ligue para o 911. Nao espere os sintomas aparecerem. E sempre melhor ligar e estar seguro.',
  'voice.conv.whichMedPregnancy': 'Sobre qual medicamento voce pergunta? Esta e uma pergunta importante, e quero dar a informacao correta.',
  'voice.conv.pregnancyWarning': 'Veja o que o rotulo diz sobre {drug} e gravidez. {warning}. Por favor, fale com seu medico sobre isso o mais rapido possivel. Eles podem ajuda-lo a pesar os beneficios e riscos para sua situacao.',
  'voice.conv.pregnancyFallback': 'Fale com seu medico sobre tomar {drug} durante a gravidez ou amamentacao. Esta e uma conversa importante para ter o mais rapido possivel. Seu medico pode ajudar a decidir o que e mais seguro para voce e seu bebe.',
  'voice.conv.whichMedAlcohol': 'Sobre qual medicamento voce pergunta? Posso verificar se ha interacoes com alcool.',
  'voice.conv.alcoholInteraction': 'Veja o que voce deve saber sobre alcool e {drug}. {info}. Se tiver perguntas sobre quanto e seguro, seu farmaceutico pode dar orientacao pratica.',
  'voice.conv.alcoholWarning': 'Ha uma advertencia sobre alcool com {drug}. {warning}. Fale com seu farmaceutico se quiser mais detalhes sobre o que isso significa para voce.',
  'voice.conv.alcoholFallback': 'Consulte seu farmaceutico sobre beber alcool com {drug}. Muitos medicamentos podem interagir com o alcool de formas nao obvias. Eles podem dar orientacao clara.',
  'voice.conv.whichMedDriving': 'Sobre qual medicamento voce pergunta? Posso verificar se pode afetar sua capacidade de dirigir.',
  'voice.conv.drivingCaution': '{drug} pode causar sonolencia ou tontura. Tenha cuidado ao dirigir ou operar maquinas ate saber como afeta voce. Isso e especialmente importante quando voce comeca a tomar ou apos uma mudanca de dose.',
  'voice.conv.drivingOk': '{drug} normalmente nao e conhecido por afetar a direcao, mas cada pessoa responde de forma diferente. Tenha cautela ate saber como afeta voce, especialmente quando comeca a tomar. Se sentir sonolencia ou tontura, nao dirija.',
  'voice.conv.anyMedication': 'qualquer medicamento',
  'voice.conv.stopTaking': 'Nunca pare de tomar {drug} sem falar com seu medico primeiro. Alguns medicamentos precisam ser reduzidos gradualmente. Parar de repente pode causar efeitos de abstinencia ou sua condicao pode piorar. Se o custo ou os efeitos colaterais sao o motivo pelo qual esta pensando em parar, diga ao seu medico. Pode haver outras opcoes.',
  'voice.conv.whichMedGeneric': 'Sobre qual medicamento voce pergunta? Posso dizer sobre as versoes generica e de marca.',
  'voice.conv.genericVsBrand': 'Medicamentos genericos e de marca contem o mesmo ingrediente ativo e funcionam da mesma forma. A FDA exige que os genericos sejam igualmente seguros e eficazes. {drug} e o mesmo medicamento que {brands}. Os comprimidos podem parecer diferentes em cor ou forma, mas o medicamento e o mesmo. Se tiver preocupacoes, seu farmaceutico pode explicar as diferencas.',
  'voice.conv.genericVsBrandFallback': 'Medicamentos genericos e de marca contem o mesmo ingrediente ativo e funcionam da mesma forma. A FDA exige que os genericos sejam igualmente seguros e eficazes. Os comprimidos podem parecer diferentes, mas o medicamento e o mesmo. Seu farmaceutico pode mostrar exatamente o que esperar.',
  'voice.conv.cost': 'Nao tenho informacoes de precos, mas seu farmaceutico pode ajuda-lo a encontrar o melhor preco. Pergunte sobre opcoes genericas, cupons do fabricante ou programas de assistencia ao paciente. Muitos fabricantes de medicamentos oferecem programas de economia que seu farmaceutico pode ajuda-lo a encontrar.',

  'chat.gettingReady': 'Estou me preparando para ajuda-lo com isso. Por favor, tente novamente em um momento.',
};

const ht: StringTable = {
  'nav.home': 'Akèy',
  'nav.medications': 'Medikaman',
  'nav.schedule': 'Orè',
  'nav.ask': 'Mande',
  'nav.records': 'Dosye',
  'home.tagline': 'Gid ou pou geri lakay',
  'home.greeting.morning': 'Bonjou',
  'home.greeting.afternoon': 'Bonswa',
  'home.greeting.evening': 'Bonswa',
  'home.greeting.night': 'Bòn nwit',
  'home.doses': 'dòz',
  'home.dosesTaken': '{taken} nan {total} dòz pran jodi a',
  'home.tookIt': '✓ Mwen pran li',
  'home.undo': 'Defèt',
  'home.upNext': 'Pwochen',
  'home.now': 'Kounye a',
  'home.yourMedications': '{n} Medikaman Ou Yo',
  'home.askAnything': 'Mande m nenpòt bagay',
  'home.voiceHint': '"Ki sa mwen pran apre?" oswa "Pale m de Metfòmin"',
  'home.howPossible': 'Kijan Sa Posib?',
  'home.healthRecord': 'Dosye Medikal Mwen',
  'home.healthRecordDesc': 'Wè dyagnostik, lab ak plan swen',
  'home.selectLanguage': 'Lang',
  'greeting.withName': '{greeting}, {name}. Byenveni nan Bridgepoint AfterCare. Medikaman ou yo pare. Ou ka mande m nenpòt bagay sou medikaman ou yo.',
  'greeting.withoutName': '{greeting}. Byenveni nan Bridgepoint AfterCare, gid ou pou geri lakay. Ou ka mande m nenpòt bagay sou medikaman ou yo.',
  'overlay.tapToBegin': 'Touche nenpòt kote pou kòmanse',
  'medList.title': 'Medikaman Mwen Yo',
  'medList.empty': 'Pa gen medikaman ajoute ankò.',
  'medList.addMedication': 'Ajoute Medikaman',
  'medList.activeLabel': 'Medikaman aktif yo',
  'medList.addLabel': 'Ajoute yon nouvo medikaman',
  'schedule.title': 'Orè Jodi a',
  'schedule.empty': 'Pa gen medikaman pwograme pou jodi a.',
  'schedule.morning': 'Maten',
  'schedule.afternoon': 'Aprèmidi',
  'schedule.evening': 'Aswè',
  'schedule.bedtime': 'Lè dòmi',
  'schedule.markTaken': '✓ Make kòm Pran',
  'schedule.timelineLabel': 'Orè medikaman yo',
  'schedule.markTakenLabel': 'Make {drug} kòm pran',
  'schedule.iTookThis': 'Mwen Pran Li',
  'schedule.skip': 'Pase',
  'schedule.skipDose': 'Pase dòz {drug}',
  'schedule.noMedsToday': 'Pa gen medikaman pwograme pou jodi a.',
  'visual.pillAlt': 'Grenn {name}: {color} {shape}',
  'visual.doesThisLookRight': 'Èske sa sanble kòrèk?',
  'visual.pillCard': 'Kat grenn pou {name}',
  'visual.pillId': 'Idantifikasyon grenn pou {name}',
  'schedule.statusLabel': 'Estati: {status}',
  'timer.timeToTake': 'Lè pou pran {drug}',
  'timer.snoozedTitle': 'Rapèl Medikaman (Ranvwaye)',
  'timer.snoozedBody': 'Ou te ranvwaye rapèl sa a 15 minit de sa.',
  'voice.holdToSpeak': 'Kenbe pou pale',
  'voice.listening': 'Ap tande, lage pou fini',
  'voice.processing': 'Ap trete pawòl ou',
  'voice.errorTryAgain': 'Yon bagay pa t mache. Tape pou eseye ankò.',
  'home.tapToStart': 'Tape nenpòt kote pou kòmanse',
  'nav.mainNavigation': 'Navigasyon prensipal',
  'notify.notifications': 'Notifikasyon',
  'notify.dismiss': 'Retire notifikasyon',
  'install.title': 'Enstale AfterCare',
  'install.subtitle': 'Jwenn medikaman ou san entènèt, nenpòt lè',
  'install.button': 'Enstale',
  'install.ariaLabel': 'Enstale aplikasyon AfterCare',
  'install.dismiss': 'Fèmen avi enstalasyon',
  'install.iosStep1Prefix': '1. Tape bouton ',
  'install.iosShare': 'Pataje',
  'install.iosInSafari': ' nan Safari',
  'install.iosStep2Prefix': '2. Desann epi tape ',
  'install.iosAddToHome': '"Ajoute nan Ekran Akèy"',
  'home.allRightsReserved': 'Tout Dwa Rezève',
  'chat.title': 'Espesyalis Medikal IA Ou',
  'chat.subtitle': 'Mande sou medikaman, tès laboratwa, dyagnostik oswa plan swen ou',
  'chat.placeholder': 'Mande sou sante ou...',
  'chat.send': 'Voye',
  'chat.holdToSpeak': 'Kenbe pou pale',
  'chat.thinking': 'Ap reflechi...',
  'chat.llmLoading': 'Asistan IA ap chaje. Ou ka poze kesyon senp kounye a.',
  'chat.llmReady': 'Asistan IA pare. Mwen ka reponn kesyon konplèks sou medikaman ou.',
  'chat.aiDisclaimer': 'Repons jenerasyon IA. Toujou konfime ak doktè ou.',
  'chat.lookingUp': 'Ap chèche...',

  // --- Chat response strings (template fallback system) ---
  'chat.noMedsOnFile': 'Ou poko gen medikaman nan dosye ou. Mande famasyen oswa doktè ou pou ede ou ajoute yo.',
  'chat.takingCount': 'Ou ap pran {count} medikaman kounye a{s}:',
  'chat.whichSideEffects': 'Ki medikaman ou ta renmen konnen efè segondè yo? Touche youn nan medikaman ou yo anwo a, oswa di non an.',
  'chat.drugNotFound': 'Mwen pa t jwenn yon medikaman ki rele "{drug}". Tanpri verifye òtograf la oswa eseye non jenerik la.',
  'chat.noSideEffectsListed': 'Pa gen efè segondè ki make pou {drug}. Sa pa vle di pa genyen -- mande famasyen ou.',
  'chat.sideEffectsOf': 'Efè segondè ki pi komen pou {drug}:',
  'chat.sideEffectsAdvice': 'Si nenpòt efè segondè grav oswa pa disparèt, kontakte doktè ou.',
  'chat.drugNotFoundShort': 'Mwen pa t jwenn yon medikaman ki rele "{drug}". Tanpri verifye òtograf la.',
  'chat.interactionWarning': 'AVÈTISMAN: {drugA} ak {drugB} gen yon enteraksyon yo konnen.',
  'chat.severity': 'Gravite: {level}',
  'chat.interactionAdvice': 'Pale ak doktè oswa famasyen ou anvan ou konbine medikaman sa yo.',
  'chat.noInteractionFound': 'Nou pa jwenn okenn enteraksyon ant {drugA} ak {drugB} nan baz done nou an.',
  'chat.noInteractionDisclaimer': 'Sa pa garanti sekirite. Toujou konfime ak famasyen ou.',
  'chat.foodInteractionWarning': 'AVÈTISMAN: {drug} gen yon enteraksyon yo konnen ak {food}.',
  'chat.foodInteractionAdvice': 'Pale ak doktè oswa famasyen ou pou konsèy.',
  'chat.noFoodInteraction': 'Nou pa jwenn okenn enteraksyon {food} pou {drug} nan baz done nou an.',
  'chat.askPharmacist': 'Si ou pa sèten, mande famasyen ou.',
  'chat.someMedsInteractFood': 'AVÈTISMAN: Kèk nan medikaman ou yo gen enteraksyon ak {food}:',
  'chat.noFoodInteractionAny': 'Nou pa jwenn okenn enteraksyon {food} pou okenn medikaman ou ap pran kounye a.',
  'chat.whichDescription': 'Ki medikaman ou ta renmen konnen? Touche youn anwo a oswa di non an.',
  'chat.noDescription': 'Pa gen deskripsyon disponib pou medikaman sa a.',
  'chat.whichDosage': 'Ki medikaman? Touche youn anwo a oswa di "kòman mwen pran metfòmin?"',
  'chat.noDosageInstructions': 'Pa gen enstriksyon dozaj disponib. Tanpri konsilte famasyen ou.',
  'chat.howToTake': 'Kòman pou pran {drug}:',
  'chat.whichMissedDose': 'Ki medikaman ou te bliye pran? Touche youn anwo a oswa di non an.',
  'chat.noMissedDoseGuidance': 'Pa gen konsèy espesifik pou doz ou te bliye. Kontakte famasyen ou pou konsèy.',
  'chat.ifMissedDose': 'Si ou te bliye yon doz {drug}:',
  'chat.noMedsSchedule': 'Ou poko gen medikaman nan dosye ou.',
  'chat.yourSchedule': 'Orè medikaman ou:',
  'chat.scheduleFrequency': 'Frekans: {frequency}',
  'chat.scheduleLabel': 'Orè: {times}',
  'chat.drugNotInList': '"{drug}" pa nan lis medikaman ou. Ou ka mande "ki medikaman mwen yo?" pou wè sa ki nan dosye ou.',
  'chat.unknownHelp': 'Mwen se espesyalis medikal pèsonèl ou ak IA. Mwen ka ede ou ak medikaman ou, rezilta lab, dyagnostik, alèji, ak plan swen ou. Eseye mande:\n\n  - "Kisa HbA1c mwen vle di?"\n  - "Poukisa mwen ap pran Metfòmin?"\n  - "Ki alèji mwen genyen?"\n  - "Ki lè pwochen randevou mwen?"\n  - "Pale m de enstriksyon soti lopital mwen"\n  - "Ki efè segondè Lisinopril?"\n  - "Èske mwen ka bwè alkòl ak medikaman mwen yo?"',
  'chat.basedOn': 'Baze sou: {sources}',
  'chat.ariaConversation': 'Mesaj konvèsasyon',
  'chat.ariaHoldToSpeak': 'Kenbe pou pale',
  'chat.ariaInputPlaceholder': 'Tape yon kesyon sou medikaman ou',
  'chat.ariaUserMessage': 'Mesaj ou',
  'chat.ariaAssistantMessage': 'Repons asistan',
  'chat.ariaReplay': 'Rejwe repons sa a',
  'chat.andMore': '...ak {count} anplis.',

  // --- Drug info ---
  'drugInfo.back': '← Retounen',
  'drugInfo.backAriaLabel': 'Retounen nan lis medikaman yo',
  'drugInfo.overviewAriaLabel': 'Rezime medikaman {drug}',
  'drugInfo.readAllAriaLabel': 'Li tout enfòmasyon medikaman an fò',
  'drugInfo.description': 'Deskripsyon',
  'drugInfo.sideEffects': 'Efè Segondè',
  'drugInfo.warnings': 'Avètisman',
  'drugInfo.speakAriaLabel': 'Li {section} fò',
  'drugInfo.expandAriaLabel': 'Elaji seksyon {section}',
  'drugInfo.collapseAriaLabel': 'Fèmen seksyon {section}',
  'drugInfo.tts.howToTake': 'Kijan pou pran: ',
  'drugInfo.tts.sideEffects': 'Efè segondè yo gen ladan: ',
  'drugInfo.tts.warnings': 'Avètisman: ',
  'drugInfo.tts.foodInteractions': 'Entèraksyon ak manje: ',
  'drugInfo.tts.storage': 'Konsèvasyon: ',
  'drugInfo.tts.missedDose': 'Si ou bliye yon dòz: ',
  'drugInfo.tts.noInstructions': 'Pa gen enstriksyon disponib.',

  // --- Setup ---
  'setup.step1.title': 'Ki medikaman ou pran?',
  'setup.step1.hint': 'Tape non an oswa kenbe bouton mikwofòn nan pou di li.',
  'setup.step1.placeholder': 'egz. Metfòmin, Lisinopril...',
  'setup.step2.title': 'Èske se medikaman ou a?',
  'setup.step2.yes': 'Wi, sa kòrèk',
  'setup.step2.no': 'Non, chèche ankò',
  'setup.step3.title': 'Konbyen fwa ou pran li?',
  'setup.step3.once': 'Yon fwa pa jou',
  'setup.step3.twice': 'De fwa pa jou',
  'setup.step3.thrice': '3 fwa pa jou',
  'setup.step3.four': '4 fwa pa jou',
  'setup.step3.everyOther': 'Chak de jou',
  'setup.step3.weekly': 'Yon fwa pa semèn',
  'setup.step3.asNeeded': 'Lè ou bezwen',
  'setup.step4.title': 'Ki lè ou pran li?',
  'setup.step4.asNeededNote': 'Piske ou pran li lè ou bezwen, pa gen orè ki nesesè.',
  'setup.step4.continue': 'Kontinye',
  'setup.step4.hint': 'Chwazi yon lè predéfini oswa mete yon lè pèsonalize.',
  'setup.step4.morning': 'Maten (8:00 AM)',
  'setup.step4.noon': 'Midi (12:00 PM)',
  'setup.step4.afternoon': 'Aprèmidi (2:00 PM)',
  'setup.step4.evening': 'Aswè (6:00 PM)',
  'setup.step4.bedtime': 'Lè dòmi (10:00 PM)',
  'setup.step4.custom': 'Lè pèsonalize:',
  'setup.step5.title': 'Enstriksyon espesyal?',
  'setup.step5.hint': 'Pa egzanp: "Pran ak manje" oswa "Pran sou vant vid"',
  'setup.step5.placeholder': 'egz. Pran ak manje, evite ponplemous...',
  'setup.step5.skip': 'Pase',
  'setup.step6.title': 'Konfime Medikaman Ou',
  'setup.step6.medication': 'Medikaman',
  'setup.step6.strength': 'Fòs',
  'setup.step6.frequency': 'Frekans',
  'setup.step6.times': 'Orè',
  'setup.step6.instructions': 'Enstriksyon',
  'setup.step6.notSpecified': 'Pa presize',
  'setup.step6.save': 'Anrejistre Medikaman',
  'setup.step6.saving': 'Ap anrejistre...',
  'setup.step6.edit': 'Modifye Detay',
  'setup.progress': 'Etap {n} nan {total}',
  'setup.back': '← Retounen',
  'setup.cancel': 'Anile',
  'setup.noResults': 'Pa jwenn medikaman. Eseye yon lòt fason ekri.',
  'setup.success': '{drug} ajoute!',
  'setup.successSpeak': '{drug} ajoute nan medikaman ou yo.',
  'setup.error': 'Echèk nan anrejistre medikaman an. Tanpri eseye ankò.',
  'setup.dose': 'Dòz {n}',
  'setup.step1.speak': 'Ki medikaman ou pran? Di non an oswa tape li.',
  'setup.step2.speak': 'Èske {drug} se medikaman ou?',
  'setup.step3.speak': 'Konbyen fwa ou pran {drug}?',
  'setup.step4.speak': 'Ki lè ou pran medikaman ou?',
  'setup.step5.speak': 'Èske ou gen enstriksyon espesyal pou medikaman sa a? Ou ka pase etap sa a.',
  'setup.step6.speak': 'Tanpri revize epi konfime detay medikaman ou.',
  'setup.step6.asNeeded': 'Lè ou bezwen',
  'setup.step6.freqOnce': 'Yon fwa pa jou',
  'setup.step6.freqTwice': 'De fwa pa jou',
  'setup.step6.freqThrice': '3 fwa pa jou',
  'setup.step6.freqFour': '4 fwa pa jou',
  'setup.step6.freqEveryOther': 'Chak de jou',
  'setup.step6.freqWeekly': 'Chak semèn',
  'setup.step6.freqAsNeeded': 'Lè ou bezwen',
  'setup.backAriaLabel': 'Retounen nan etap anvan an',
  'setup.cancelAriaLabel': 'Anile konfigirasyon medikaman',
  'setup.searchResults': 'Rezilta rechèch',

  // --- Camera ---
  'camera.title': 'Idantifye yon Grenn',
  'camera.instruction': 'Vize kamera ou sou grenn nan epi touche bouton kaptire a.',
  'camera.starting': 'Ap kòmanse kamera...',
  'camera.ready': 'Kamera pare. Mete grenn nan nan mitan.',
  'camera.noAccess': 'Pa kapab jwenn kamera. Tanpri verifye pèmisyon yo.',
  'camera.capture': 'Kaptire',
  'camera.captureAgain': 'Kaptire Ankò',
  'camera.analyzing': 'Ap analize...',
  'camera.looksLike': 'Sa sanble ak:',
  'camera.couldNotIdentify': 'Pa kapab idantifye grenn sa a.',
  'camera.viewDrugInfo': 'Wè Enfòmasyon Konplè',
  'camera.disclaimer': 'Idantifikasyon sa a se pou referans sèlman. Toujou verifye ak famasyen ou.',
  'camera.tips': 'Konsèy pou pi bon rezilta:',
  'camera.tip1': 'Mete grenn nan sou yon sifas klè epi plat',
  'camera.tip2': 'Asire bon limyè',
  'camera.tip3': 'Kenbe kamera a estab epi pre',
  'camera.tip4': 'Montre nenpòt mak oswa enpresyon',
  'camera.analyzingStatus': 'Ap analize imaj grenn nan...',
  'camera.identifiedStatus': 'Grenn idantifye. Gade rezilta anba a.',
  'camera.couldNotIdentifyStatus': 'Pa kapab idantifye grenn nan. Eseye ak pi bon limyè.',
  'camera.timeoutStatus': 'Analiz la ekspire. Eseye ankò.',
  'camera.noAccessSpeak': 'Pa kapab jwenn kamera a. Tanpri verifye pèmisyon kamera ou.',
  'camera.noAccessNotify': 'Aksè nan kamera refize oswa pa disponib.',
  'camera.looksLikeSpeak': 'Sa sanble ak {drug} ak {confidence} pousan konfyans.',
  'camera.couldNotIdentifySpeak': 'Mwen pa kapab idantifye grenn sa a. Tanpri eseye ankò ak pi bon limyè.',
  'camera.confidence': '{n}% konfyans',
  'camera.capturedAlt': 'Imaj grenn medikaman kaptire',

  'error.somethingWrong': 'Yon bagay pa t mache',
  'error.goHome': 'Ale Akèy',
  'purpose.bloodSugar': 'Sik nan san',
  'purpose.bloodPressure': 'Tansyon',
  'purpose.cholesterol': 'Kolestewòl',
  'emr.title': 'Dosye Medikal',
  'emr.allergies': 'Alèji',
  'emr.diagnoses': 'Dyagnostik',
  'emr.labResults': 'Rezilta Lab',
  'emr.vitals': 'Sin Vital',
  'emr.discharge': 'Rezime Soti Lopital',
  'emr.careGoals': 'Objektif Swen',
  'emr.appointments': 'Randevou',
  'emr.immunizations': 'Vaksen',
  'emr.insurance': 'Asirans',
  'emr.emergency': 'Kontak Ijans',
  'emr.directives': 'Direksyon Alavans',
  'emr.noData': 'Pa gen dosye medikal disponib.',
  'emr.noRecord': 'Pa gen dosye medikal chaje.',
  'emr.noRecordHint': 'Done medikal ou yo ap parèt isit la lè yo chaje.',

  // --- EMR detail strings ---
  'emr.dob': 'Dat nesans: ',
  'emr.mrn': 'Nimewo dosye: ',
  'emr.nkda': 'Pa Gen Alèji Medikaman Konnen',
  'emr.active': 'aktif',
  'emr.resolved': 'rezoud',
  'emr.inProgress': 'ap fèt',
  'emr.notStarted': 'poko kòmanse',
  'emr.severity.mild': 'lejè',
  'emr.severity.moderate': 'modere',
  'emr.severity.severe': 'grav',
  'emr.severity.life-threatening': 'danje pou lavi',
  'emergency.call911': 'Sa sanble ak yon ijans medikal. Tanpri rele 911 kounye a menm. Pa tann. Si yon moun pre ou, mande yo rele. Sekirite ou vini an premye.',
  'emergency.crisis': 'Mwen tande ou, e mwen kontan ou di m. Tanpri rele oswa voye mesaj nan 988 kounye a menm. Sa se Liy Prevansyon Swisid ak Kriz. Yo disponib tout jounen ak tout lannwit, epi yo ka ede ou. Ou pa poukont ou.',
  'emergency.callDoctor': 'Mwen enkyete sou sa w ap dekri a. Sa se yon bagay doktè ou ta dwe konnen touswit. Tanpri rele biwo doktè ou pi vit posib. Si ou pa ka jwenn yo, tanpri ale nan swen ijan. Pa tann pou wè si li vin pi mal.',
  'camera.color': 'Koulè',
  'camera.shape': 'Fòm',
  'camera.imprint': 'Mak',
  'emr.onset': 'Kòmansman: ',
  'emr.abnormal': 'anòmal',
  'emr.allNormal': 'Tout nòmal',
  'emr.ref': 'Ref: ',
  'emr.noVitals': 'Pa gen sin vital anrejistre.',
  'emr.none': 'Okenn',
  'emr.dose': 'Dòz',
  'emr.other': 'Lòt',

  // --- Lab badges ---
  'emr.lab.normal': 'Nòmal',
  'emr.lab.high': 'Wo',
  'emr.lab.low': 'Ba',
  'emr.lab.criticalHigh': 'Kritik Wo',
  'emr.lab.criticalLow': 'Kritik Ba',

  // --- Vital sign labels ---
  'emr.vital.bloodPressure': 'Tansyon',
  'emr.vital.heartRate': 'Batman Kè',
  'emr.vital.temperature': 'Tanperati',
  'emr.vital.o2Saturation': 'Saturasyon O2',
  'emr.vital.weight': 'Pwa',
  'emr.vital.bmi': 'IMC',
  'emr.vital.respRate': 'Frekans Respirasyon',
  'emr.vital.painLevel': 'Nivo Doulè',

  // --- Discharge summary ---
  'emr.hospitalCourse': 'Evolisyon Lopital',
  'emr.dischargeCondition': 'Kondisyon nan Soti',
  'emr.warningSigns': 'Sin Avètisman pou Siveye',

  // --- Care plan ---
  'emr.goals': 'objektif',
  'emr.current': 'Aktyèl: ',
  'emr.target': 'Objektif: ',
  'emr.achieved': 'reyalize',

  // --- Insurance fields ---
  'emr.ins.provider': 'Founisè',
  'emr.ins.plan': 'Plan',
  'emr.ins.memberId': 'ID Manm',
  'emr.ins.groupNumber': 'Nimewo Gwoup',
  'emr.ins.effective': 'An vigè',
  'emr.ins.copayPrimary': 'Kopeman (Prensipal)',
  'emr.ins.copaySpecialist': 'Kopeman (Espesyalis)',
  'emr.ins.copayRx': 'Kopeman (Rx)',

  // --- Emergency contacts ---
  'emr.primaryContact': 'Prensipal',
  'emr.callContact': 'Rele {name}',

  // --- Advance directives ---
  'emr.codeStatus': 'Kòd Estati',
  'emr.livingWill': 'Testaman Vivan',
  'emr.powerOfAttorney': 'Pouvwa Notarye',
  'emr.poaName': 'Non Moun Otorize',
  'emr.poaPhone': 'Telefòn Moun Otorize',
  'emr.lastReviewed': 'Dènye Revizyon',
  'emr.yes': 'Wi',
  'emr.no': 'Non',

  'home.minutes': '{n} min',
  'home.hours': '{n}h',
  'home.hoursMinutes': '{h}h {m}m',
  'schedule.missed': 'Manke',
  'schedule.upcoming': 'Pwochen',
  'schedule.taken': 'Pran',
  'chat.welcomeWithMeds': 'Mwen se Dòk AfterCare, espesyalis medikal IA ou. Mwen gen aksè nan dosye sante konplè ou — {n} medikaman{s}, rezilta lab, dyagnostik, alèji ak plan swen. Mande m sa ou bezwen.',
  'chat.welcomeNoMeds': 'Mwen se Dòk AfterCare, espesyalis medikal IA ou. Mande m sou medikaman, efè segondè, entèraksyon oswa sante ou.',
  'chat.error': 'Padon, yon bagay pa t mache. Tanpri eseye ankò.',
  'chat.disclaimer': 'Tanpri konfime ak doktè ou oswa famasyen ou anvan ou fè nenpòt chanjman nan swen ou.',
  'chat.voiceNotUnderstood': 'Mwen pa t tande sa. Tanpri eseye ankò.',
  'chat.audioUnavailable': 'Odyo pa disponib',
  'chat.quick.whatDoes': 'Ki sa {drug} fè?',
  'chat.quick.sideEffects': 'Efè segondè {drug}?',
  'chat.quick.howTake': 'Kijan mwen pran {drug}?',
  'chat.quick.missedDose': 'Ki sa k pase si mwen pa pran {drug}?',
  'chat.quick.whenTake': 'Ki lè mwen pran {drug}?',
  'chat.quick.alcohol': 'Èske mwen ka bwè alkòl ak {drug}?',
  'chat.suggest.myMeds': 'Ki sa HbA1c mwen vle di?',
  'chat.suggest.sideEffects': 'Poukisa mwen ap pran Metfòmin?',
  'chat.suggest.alcohol': 'Ki alèji mwen genyen?',
  'chat.suggest.missedDose': 'Ki lè pwochen randevou mwen?',
  'medList.emptyHint': 'Touche "Ajoute Medikaman" pou kòmanse.',
  'medList.frequency.once': 'Yon fwa pa jou',
  'medList.frequency.twice': 'De fwa pa jou',
  'medList.frequency.thrice': '3 fwa pa jou',
  'medList.frequency.four': '4 fwa pa jou',
  'medList.frequency.everyOther': 'Chak de jou',
  'medList.frequency.weekly': 'Chak semèn',
  'medList.frequency.asNeeded': 'Lè ou bezwen',
  'drugInfo.notFound': 'Enfòmasyon medikaman pa jwenn.',
  'drugInfo.alsoKnownAs': 'Konnen tou kòm: {brands}',
  'drugInfo.readAll': 'Li tout enfòmasyon',
  'drugInfo.howToTake': 'Kijan pou Pran',
  'drugInfo.foodInteractions': 'Entèraksyon ak Manje',
  'drugInfo.storage': 'Konsèvasyon',
  'drugInfo.missedDose': 'Dòz Manke',
  'drugInfo.noInfo': 'Pa gen enfòmasyon disponib.',
  'drugInfo.speakThis': 'Li sa',
  'drugInfo.tapEnlarge': 'Touche pou agrandi',
  'drugInfo.tapClose': 'Touche nenpòt kote pou fèmen',
  'model_downloading': 'Ap telechaje asistan IA...',
  'model_download_once': 'Sa rive yon sèl fwa.',
  'model_ready': 'Asistan IA pare',
  'model_ready_detail': 'Kesyon ou yo ap reponn pa IA kounye a.',
  'model_download_failed': 'Telechajman IA echwe',
  'model_fallback': 'Repons modèl ap itilize.',
  'llm.retry': 'Eseye Ankò',
  'error.tryAgain': 'Tanpri eseye ankò oswa retounen nan paj akèy la.',
  'error.voiceNotReady': 'Sistèm vwa a poko pare. Tanpri tann.',
  'error.micAccess': 'Pa ka jwenn mikwofòn nan.',
  'error.notUnderstood': 'Pa ka konprann. Tanpri eseye ankò.',
  'purpose.stomachAcid': 'Asid nan vant',
  'purpose.nervePain': 'Doulè nè',
  'purpose.moodAnxiety': 'Imè ak enkyetid',
  'purpose.thyroid': 'Tiwoyid',
  'purpose.painRelief': 'Soulajman doulè',
  'purpose.painSwelling': 'Doulè ak anflamasyon',
  'purpose.heartProtection': 'Pwoteksyon kè',
  'purpose.bloodClots': 'Kayo san',
  'purpose.fluidRemoval': 'Retire likid',
  'purpose.inflammation': 'Anflamasyon',
  'purpose.breathing': 'Respirasyon',
  'voice.noMedsForDose': 'Ou pa gen medikaman konfigire ankò. Èske ou ta renmen ajoute kèk?',
  'voice.allDoneToday': 'Ou fin pran tout dòz ou yo pou jodi a. Trè byen! Pwochen dòz ou yo kòmanse demen maten.',
  'voice.takeNow': 'Li lè pou ou pran {drug} ou kounye a.',
  'voice.nextIn': 'Pwochen medikaman ou se {drug}, nan anviwon {n} minit a {time}.',
  'voice.nextAt': 'Pwochen medikaman ou se {drug} a {time}, anviwon {n} èdtan.',
  'voice.scheduleCount': 'Ou gen {n} medikaman pwograme pou jodi a.',
  'voice.loggedTaken': 'Dakò! Mwen anrejistre ke ou pran {drug} ou. Byen fè!',
  'voice.loggedSkipped': 'Dakò, mwen note ke ou sote {drug} ou. Si ou gen pwoblèm, pale ak doktè ou.',
  'voice.help': 'Men sa mwen ka ede ou ak. Ou ka mande m: Ki sa mwen pran apre? Pale m de yon medikaman. Ki efè segondè yo? Èske mwen ka manje sèten manje ak sa? Mwen bliye pran dòz mwen. Poukisa mwen ap pran sa?',
  'voice.unknown': 'Padon, mwen pa t konprann sa. Ou ka mande m: Ki sa mwen pran apre? Pale m de medikaman mwen yo. Oswa di Èd.',
  'voice.sideEffects': 'Efè segondè komen {drug} gen ladan: {effects}. Si nenpòt nan yo deranje ou oswa pa ale, pale ak doktè ou.',
  'voice.noSideEffects': 'Mwen pa gen enfòmasyon espesifik sou efè segondè {drug}. Tcheke ak famasyen oswa doktè ou.',
  'voice.missedDose': 'Si ou manke yon dòz {drug}: {advice}',
  'voice.missedDoseFallback': 'Si ou manke yon dòz {drug}, pran li kou ou sonje. Men si li prèske lè pou pwochen dòz ou, sote sa ou te manke a. Pa janm pran doub dòz.',
  'voice.drugNotFound': 'Mwen pa t ka jwenn enfòmasyon sou {drug}. Èske ou ka eseye di non an ankò?',
  'voice.whichMed': 'Sou ki medikaman ou ap mande? Di non an epi mwen ap chèche li.',
  'voice.stopTaking': 'Tanpri pa sispann pran okenn medikaman san pale ak doktè ou anvan. Sispann bridsoukou ka lakòz pwoblèm.',
  'voice.alcohol': 'Jeneralman yo rekòmande evite alkòl pandan ou ap pran {drug}. Tcheke ak doktè ou.',
  'voice.gettingReady': 'Mwen ap prepare m pou ede ou ak sa. Tanpri eseye ankò nan yon ti moman.',
  'voice.drugInfoHowToTake': 'Men kijan pou ou pran li: {instructions}',
  'voice.foodInteractions': 'Pou {drug}, fè atansyon ak enteraksyon manje sa yo: {foods}.',
  'voice.noFoodInteractions': 'Mwen pa gen enfòmasyon espesifik sou enteraksyon manje pou {drug}. An jeneral, swiv enstriksyon famasyen ou te ba ou.',
  'voice.storage': 'Pou {drug}: {info}',
  'voice.storageFallback': 'Estoke {drug} nan tanperati chanm, lwen imidite ak chalè. Kenbe li nan veso orijinal la ak kouvèti a byen fèmen.',
  'voice.warnings': 'Avètisman enpòtan pou {drug}: {warnings}. Si ou gen nenpòt enkyetid, tanpri kontakte doktè ou.',
  'voice.noWarnings': 'Mwen pa gen avètisman espesifik pou {drug}. Toujou swiv enstriksyon doktè ou.',
  'voice.whyTaking': 'Ou ap pran {drug} paske {reason}',
  'voice.whyTakingFallback': 'Doktè ou te preskri {drug} pou ede ak kondisyon ou. Si ou ta renmen konnen plis sou poukisa ou ap pran li, tanpri mande doktè oswa famasyen ou.',
  'voice.howWorks': 'Men kijan {drug} fonksyone: {description}',
  'voice.howWorksFallback': 'Mwen pa gen enfòmasyon detaye sou kijan {drug} fonksyone nan kò ou. Famasyen ou ta dwe pi bon moun pou mande sou sa.',
  'voice.graceful': 'Sa se yon bon kesyon sou {topic}. Mwen pa gen enfòmasyon detaye sou sa kounye a, men famasyen oswa doktè ou ta dwe pi bon moun pou mande. Èske gen lòt bagay sou medikaman ou yo mwen ka ede ou ak?',
  'voice.didntHear': 'Mwen pa t tande anyen. Èske ou ka eseye ankò?',
  'voice.youSaid': 'Ou te di: "{text}"',
  'voice.somethingWrong': 'Padon, yon bagay mal pase. Tanpri eseye mande ankò.',
  'voice.thatMedication': 'medikaman sa a',
  'voice.yourMedication': 'medikaman ou',
  'voice.whichMedFood': 'Ki medikaman ou ap mande sou enteraksyon manje pou li?',
  'voice.whichMedStorage': 'Pou ki medikaman ou ta renmen enstriksyon estokaj?',
  'voice.whichMedInteractions': 'Ki medikaman ou ap mande sou enteraksyon pou li?',
  'voice.whichMedWhy': 'Ki medikaman ou ap mande sou li?',
  'voice.whichMedHow': 'Ki medikaman ou ta renmen konnen sou li?',
  'voice.whichMedPillId': 'Mwen ta bezwen konnen ki grenn ou ap gade. Èske ou ka di m non ki sou grenn nan?',
  'voice.pillIdentify': '{drug} se yon {shape} {color}{imprint}. Èske sa sanble ak sa ou ap gade?',
  'voice.pill': 'grenn',
  'voice.pillImprint': ' ak "{imprint}" sou li',
  'voice.driving': 'Kèk medikaman ka lakòz anvi dòmi. Tcheke etikèt {drug} ou oswa mande famasyen ou si li san danje pou kondui pandan ou ap pran li.',
  'voice.genericVsBrand': '{drug} se non jenerik la. Non mak yo gen ladan: {brands}. Yo gen menm engredyan aktif la e yo fonksyone menm jan.',
  'voice.addMedication': 'Pou ajoute yon nouvo medikaman, ale nan onglet Paramèt. Doktè oswa famasyen ou ka ede ou konfigire nouvo medikaman nan aplikasyon an.',
  'voice.topicOverdose': 'enkyetid sou sipèdòz',
  'voice.topicPregnancy': 'sekirite medikaman pandan gwosès',
  'voice.topicAlcohol': 'enteraksyon ak alkòl',
  'voice.topicDriving': 'kondui pandan ou ap pran medikaman',
  'voice.topicGenericVsBrand': 'medikaman jenerik kont mak',
  'voice.topicCost': 'pri medikaman',
  'voice.andJoiner': ' ak ',
  'voice.scheduleItem': '{drug} a {times}.',

  // --- Conversation state machine (voice.conv.*) ---
  'voice.conv.cancelled': 'Oke, anile.',
  'voice.conv.logTakenConfirm': 'Dakò. Mwen ap anrejistre ke ou te pran {drug} ou.',
  'voice.conv.logSkippedConfirm': 'Dakò. Mwen ap anrejistre ke ou te sote {drug} ou.',
  'voice.conv.confirmed': 'Konfime.',
  'voice.conv.showEmr': 'Mwen ap ouvri dosye sante ou.',
  'voice.conv.labQuestion': 'Kite m gade rezilta laboratwa ou yo.',
  'voice.conv.emrDiagnosis': 'Men dyagnostik ou yo.',
  'voice.conv.emrFollowUp': 'Kite m tcheke randevou ou yo ki ap vini.',
  'voice.conv.emrDischargeInfo': 'Men enfòmasyon soti lopital ou.',
  'voice.conv.nextDose': 'Kite m tcheke pwochen dòz ou.',
  'voice.conv.whichMedInfo': 'Sou ki medikaman ou ta renmen konnen?',
  'voice.conv.drugInfo': 'Mwen ap chèche enfòmasyon sou {drug}.',
  'voice.conv.whichMedSideEffects': 'Sou ki medikaman ou ap mande konsènan efè segondè?',
  'voice.conv.sideEffectsNav': 'Men efè segondè pou {drug}.',
  'voice.conv.whichMedInteractions': 'Sou ki medikaman ou ta renmen tcheke enteraksyon?',
  'voice.conv.interactionsNav': 'Mwen ap tcheke enteraksyon pou {drug}.',
  'voice.conv.whichMedFoodInteractions': 'Sou ki medikaman ou ap mande konsènan enteraksyon ak manje?',
  'voice.conv.foodInteractionsNav': 'Men enteraksyon ak manje pou {drug}.',
  'voice.conv.whichMedTaken': 'Ki medikaman ou te pran?',
  'voice.conv.confirmTaken': 'Ou di ou te pran {drug} ou? Di wi pou konfime.',
  'voice.conv.whichMedSkipped': 'Ki medikaman ou ap sote?',
  'voice.conv.confirmSkipped': 'Ou vle sote {drug} ou? Di wi pou konfime.',
  'voice.conv.pillShowDrug': 'Mwen ap montre ou kijan {drug} sanble.',
  'voice.conv.pillCamera': 'Mwen ap ouvri kamera idantifikasyon grenn.',
  'voice.conv.showSchedule': 'Men orè medikaman ou.',
  'voice.conv.addMedication': 'Mwen ap ouvri konfigirasyon medikaman.',
  'voice.conv.help': 'Men sa mwen ka fè. Di "pwochen dòz" pou wè pwochen medikaman ou. Di "pale m de" swivi pa non medikaman an pou detay. Di "mwen te pran" swivi pa medikaman an pou anrejistre yon dòz. Di "sote" pou make yon dòz kòm sote. Di "orè" pou wè tout medikaman ou yo. Di "efè segondè" oswa "enteraksyon" pou enfòmasyon sekirite. Di "ajoute" pou konfigire yon nouvo medikaman. Ou ka mande m tou bagay tankou "poukisa mwen ap pran sa," "ki sa ki rive si mwen manke yon dòz," "èske mwen ka bwè alkòl ak sa," "èske mwen ka kondui ak sa," "kijan mwen dwe sere sa," oswa "èske jenerik la se menm bagay." Mwen la pou ede ou konprann medikaman ou yo.',
  'voice.conv.unknown': 'Mwen pa t byen konprann sa. Di "èd" pou tande sa mwen ka fè.',
  'voice.conv.whichMedWhyTaking': 'Sou ki medikaman ou ap mande? Di non an epi mwen ka di ou pou ki sa li itilize souvan.',
  'voice.conv.drugNotInDb': 'Mwen pa gen {drug} nan baz done mwen kounye a. Famasyen ou ka eksplike pou ki sa li preskri.',
  'voice.conv.noDescriptionOnFile': 'Mwen pa gen yon deskripsyon detaye pou {drug} nan dosye',
  'voice.conv.whyTaking': '{drug} souvan preskri pou sa ki annapre yo. {description}. Doktè ou te preskri sa a baze sou bezwen sante espesifik ou yo. Si ou gen kesyon sou poukisa ou ap pran li, se yon bon konvèsasyon pou gen nan pwochen vizit ou.',
  'voice.conv.whichMedMissed': 'Ki medikaman ou te manke? Di m non an epi mwen ka chèche konsèy yo.',
  'voice.conv.missedDoseInfo': 'Men konsèy pou yon dòz manke de {drug}. {advice}. Si ou pa sèten kisa pou fè, famasyen ou ka ede ou ak yon ti apèl.',
  'voice.conv.missedDoseFallback': 'Mwen pa gen enstriksyon espesifik pou dòz manke de {drug}. Kòm règ jeneral, pran li kou ou sonje sof si li prèske lè pou pwochen dòz ou. Pa doble dòz la. Si ou pa sèten, rele famasyen ou.',
  'voice.conv.whichMedConcern': 'Sou ki medikaman ou enkyete? Di m non an ak sentòm nan, epi mwen ap tcheke.',
  'voice.conv.drugNotInDbConcern': 'Mwen pa gen {drug} nan baz done mwen. Si ou ap eksperyanse yon bagay ki enkyete ou, kontakte doktè oswa famasyen ou.',
  'voice.conv.symptomKnown': '{symptom} se yon efè segondè ke yo konnen de {drug}. Sa a jeneralman lejè epi ka amelyore lè kò ou adapte. Si li grav oswa li pa ale, kontakte doktè ou. Se yon bon kesyon pou poze.',
  'voice.conv.symptomUnknown': '{symptom} pa yon efè segondè ki souvan nan lis pou {drug}. Li ta ka pa gen rapò, men li vo lapèn mansyone bay doktè ou nan pwochen vizit ou. Si li sanble grav, pa tann.',
  'voice.conv.sideEffectsList': 'Efè segondè ki pi komen nan {drug} gen ladan {effects}. Si ou ap eksperyanse yon bagay espesifik, di m sentòm nan epi mwen ka tcheke. E si nenpòt bagay sanble grav, kontakte doktè ou touswit.',
  'voice.conv.noSideEffectsList': 'Mwen pa gen yon lis efè segondè pou {drug} nan dosye. Famasyen ou ka gide ou sou kisa pou siveye.',
  'voice.conv.whichMedHowWorks': 'Ki medikaman ou ta renmen konprann pi byen? Di non an epi mwen ap eksplike kijan li fonksyone.',
  'voice.conv.howWorksFallback': 'Mwen pa gen yon eksplikasyon detaye sou kijan {drug} fonksyone. Famasyen ou se yon gwo resous pou konprann medikaman ou yo.',
  'voice.conv.howWorks': 'Men kijan {drug} fonksyone. {description}. Si ou vle konprann plis sou kijan li ede ou espesifikman, doktè ou ka eksplike baze sou istwa sante ou.',
  'voice.conv.whichMedStorage': 'Sou ki medikaman ou ap mande konsènan estokaj?',
  'voice.conv.storageInfo': 'Men kijan pou sere {drug}. {info}. Estokaj apwopriye ede asire medikaman ou rete efikas.',
  'voice.conv.storageFallback': 'Mwen pa gen enstriksyon espesifik pou estokaj {drug}. Pifò medikaman dwe kenbe nan tanperati chanm, lwen imidite ak limyè solèy dirèk. Tcheke etikèt la oswa mande famasyen ou pou asire ou.',
  'voice.conv.yourMedication': 'medikaman ou',
  'voice.conv.overdose': 'Si ou panse ou te pran twòp {drug}, rele Kontwòl Pwazon touswit nan 1-800-222-1222. Si ou santi ou trè malad oswa gen pwoblèm pou respire, rele 911. Pa tann sentòm parèt. Li toujou pi bon pou rele epi asire sekirite ou.',
  'voice.conv.whichMedPregnancy': 'Sou ki medikaman ou ap mande? Sa a se yon kesyon enpòtan, epi mwen vle ba ou enfòmasyon ki kòrèk.',
  'voice.conv.pregnancyWarning': 'Men sa etikèt la di sou {drug} ak gwosès. {warning}. Tanpri pale ak doktè ou sou sa pi vit posib. Yo ka ede ou peze avantaj ak risk pou sitiyasyon ou.',
  'voice.conv.pregnancyFallback': 'Pale ak doktè ou sou pran {drug} pandan gwosès oswa bay tete. Sa a se yon konvèsasyon enpòtan pou gen pi vit posib. Doktè ou ka ede ou deside kisa ki pi an sekirite pou ou ak bebe ou.',
  'voice.conv.whichMedAlcohol': 'Sou ki medikaman ou ap mande? Mwen ka tcheke si gen enteraksyon ak alkòl.',
  'voice.conv.alcoholInteraction': 'Men sa ou dwe konnen sou alkòl ak {drug}. {info}. Si ou gen kesyon sou konbyen ki san danje, famasyen ou ka ba ou konsèy pratik.',
  'voice.conv.alcoholWarning': 'Gen yon avètisman sou alkòl ak {drug}. {warning}. Pale ak famasyen ou si ou vle plis detay sou sa sa vle di pou ou.',
  'voice.conv.alcoholFallback': 'Tcheke ak famasyen ou sou bwè alkòl ak {drug}. Anpil medikaman ka gen enteraksyon ak alkòl nan fason ki pa evidan. Yo ka ba ou konsèy klè.',
  'voice.conv.whichMedDriving': 'Sou ki medikaman ou ap mande? Mwen ka tcheke si li ka afekte kapasite ou pou kondui.',
  'voice.conv.drivingCaution': '{drug} ka lakòz anvi dòmi oswa vètij. Fè atansyon lè ou kondui oswa itilize machin jiskaske ou konnen kijan li afekte ou. Sa a espesyalman enpòtan lè ou fèk kòmanse pran li oswa apre yon chanjman dòz.',
  'voice.conv.drivingOk': '{drug} nòmalman pa konnen pou afekte konduit, men chak moun reponn diferaman. Fè atansyon jiskaske ou konnen kijan li afekte ou, espesyalman lè ou fèk kòmanse pran li. Si ou santi anvi dòmi oswa vètij, pa kondui.',
  'voice.conv.anyMedication': 'nenpòt medikaman',
  'voice.conv.stopTaking': 'Pa janm sispann pran {drug} san pale ak doktè ou anvan. Gen medikaman ki bezwen redui piti piti. Sispann bridsoukou ka lakòz efè sevraje oswa kondisyon ou ka vin pi mal. Si pri oswa efè segondè se rezon ou ap panse sispann, di doktè ou. Ka gen lòt opsyon.',
  'voice.conv.whichMedGeneric': 'Sou ki medikaman ou ap mande? Mwen ka di ou sou vèsyon jenerik ak mak la.',
  'voice.conv.genericVsBrand': 'Medikaman jenerik ak medikaman mak gen menm engredyan aktif la epi fonksyone menm jan. FDA egzije jenerik yo pou yo egalman san danje ak efikas. {drug} se menm medikaman ak {brands}. Grenn yo ka sanble diferan an koulè oswa fòm, men medikaman andedan an se menm nan. Si ou gen enkyetid, famasyen ou ka eksplike diferans yo.',
  'voice.conv.genericVsBrandFallback': 'Medikaman jenerik ak medikaman mak gen menm engredyan aktif la epi fonksyone menm jan. FDA egzije jenerik yo pou yo egalman san danje ak efikas. Grenn yo ka sanble diferan, men medikaman an se menm nan. Famasyen ou ka montre ou egzakteman kisa pou atann.',
  'voice.conv.cost': 'Mwen pa gen enfòmasyon sou pri, men famasyen ou ka ede ou jwenn pi bon pri a. Mande yo sou opsyon jenerik, koupon manifakti, oswa pwogram asistans pasyan. Anpil manifakti medikaman ofri pwogram ekonomi ke famasyen ou ka ede ou jwenn.',

  'chat.gettingReady': 'Mwen ap prepare m pou ede ou ak sa. Tanpri eseye ankò nan yon ti moman.',
};

const fr: StringTable = {
  'nav.home': 'Accueil',
  'nav.medications': 'Médicaments',
  'nav.schedule': 'Horaire',
  'nav.ask': 'Demander',
  'nav.records': 'Dossier',
  'home.tagline': 'Votre guide pour guérir à la maison',
  'home.greeting.morning': 'Bonjour',
  'home.greeting.afternoon': 'Bon après-midi',
  'home.greeting.evening': 'Bonsoir',
  'home.greeting.night': 'Bonne nuit',
  'home.doses': 'doses',
  'home.dosesTaken': '{taken} sur {total} doses prises aujourd\'hui',
  'home.tookIt': '✓ Pris',
  'home.undo': 'Annuler',
  'home.upNext': 'Suivant',
  'home.now': 'Maintenant',
  'home.yourMedications': 'Vos {n} Médicaments',
  'home.askAnything': 'Demandez-moi n\'importe quoi',
  'home.voiceHint': '"Que dois-je prendre?" ou "Parlez-moi du Metformine"',
  'home.howPossible': 'Comment Est-Ce Possible?',
  'home.healthRecord': 'Mon Dossier Médical',
  'home.healthRecordDesc': 'Voir diagnostics, analyses et plan de soins',
  'home.selectLanguage': 'Langue',
  'greeting.withName': '{greeting}, {name}. Bienvenue à Bridgepoint AfterCare. Vos médicaments sont prêts. Vous pouvez me poser n\'importe quelle question sur vos médicaments.',
  'greeting.withoutName': '{greeting}. Bienvenue à Bridgepoint AfterCare, votre guide pour guérir à la maison. Vous pouvez me poser n\'importe quelle question sur vos médicaments.',
  'overlay.tapToBegin': 'Touchez n\'importe où pour commencer',
  'medList.title': 'Mes Médicaments',
  'medList.empty': 'Aucun médicament ajouté pour l\'instant.',
  'medList.addMedication': 'Ajouter un Médicament',
  'medList.activeLabel': 'Médicaments actifs',
  'medList.addLabel': 'Ajouter un nouveau médicament',
  'schedule.title': 'Horaire d\'Aujourd\'hui',
  'schedule.empty': 'Aucun médicament prévu pour aujourd\'hui.',
  'schedule.morning': 'Matin',
  'schedule.afternoon': 'Après-midi',
  'schedule.evening': 'Soir',
  'schedule.bedtime': 'Coucher',
  'schedule.markTaken': '✓ Marquer comme Pris',
  'schedule.timelineLabel': 'Calendrier des médicaments',
  'schedule.markTakenLabel': 'Marquer {drug} comme pris',
  'schedule.iTookThis': 'Je l\'ai Pris',
  'schedule.skip': 'Passer',
  'schedule.skipDose': 'Passer la dose de {drug}',
  'schedule.noMedsToday': 'Aucun médicament prévu pour aujourd\'hui.',
  'visual.pillAlt': 'Comprimé de {name}: {color} {shape}',
  'visual.doesThisLookRight': 'Est-ce que cela semble correct?',
  'visual.pillCard': 'Fiche du comprimé de {name}',
  'visual.pillId': 'Identification du comprimé de {name}',
  'schedule.statusLabel': 'Statut: {status}',
  'timer.timeToTake': 'C\'est l\'heure de prendre {drug}',
  'timer.snoozedTitle': 'Rappel de Médicament (Reporté)',
  'timer.snoozedBody': 'Vous avez reporté ce rappel il y a 15 minutes.',
  'voice.holdToSpeak': 'Maintenez pour parler',
  'voice.listening': 'Écoute en cours, relâchez pour terminer',
  'voice.processing': 'Traitement de votre parole',
  'voice.errorTryAgain': 'Quelque chose s\'est mal passé. Appuyez pour réessayer.',
  'home.tapToStart': 'Appuyez n\'importe où pour commencer',
  'nav.mainNavigation': 'Navigation principale',
  'notify.notifications': 'Notifications',
  'notify.dismiss': 'Fermer la notification',
  'install.title': 'Installer AfterCare',
  'install.subtitle': 'Accédez à vos médicaments hors ligne, à tout moment',
  'install.button': 'Installer',
  'install.ariaLabel': 'Installer l\'application AfterCare',
  'install.dismiss': 'Fermer l\'invite d\'installation',
  'install.iosStep1Prefix': '1. Appuyez sur le bouton ',
  'install.iosShare': 'Partager',
  'install.iosInSafari': ' dans Safari',
  'install.iosStep2Prefix': '2. Faites défiler vers le bas et appuyez sur ',
  'install.iosAddToHome': '"Ajouter à l\'écran d\'accueil"',
  'home.allRightsReserved': 'Tous Droits Réservés',
  'chat.title': 'Votre Spécialiste Médical IA',
  'chat.subtitle': 'Posez des questions sur vos médicaments, analyses, diagnostics ou plan de soins',
  'chat.placeholder': 'Posez une question sur votre santé...',
  'chat.send': 'Envoyer',
  'chat.holdToSpeak': 'Maintenez pour parler',
  'chat.thinking': 'Réflexion...',
  'chat.llmLoading': "L'assistant IA se charge. Vous pouvez poser des questions simples maintenant.",
  'chat.llmReady': "L'assistant IA est prêt. Je peux répondre à des questions complexes sur vos médicaments.",
  'chat.aiDisclaimer': 'Réponse générée par IA. Confirmez toujours avec votre médecin.',
  'chat.lookingUp': 'Recherche en cours...',

  // --- Chat response strings (template fallback system) ---
  'chat.noMedsOnFile': 'Vous n\'avez pas encore de médicaments enregistrés. Demandez à votre pharmacien ou médecin de vous aider à les ajouter.',
  'chat.takingCount': 'Vous prenez actuellement {count} médicament{s} :',
  'chat.whichSideEffects': 'De quel médicament souhaitez-vous connaître les effets secondaires ? Touchez l\'un de vos médicaments ci-dessus ou dites le nom.',
  'chat.drugNotFound': 'Je n\'ai pas trouvé de médicament appelé "{drug}". Vérifiez l\'orthographe ou essayez le nom générique.',
  'chat.noSideEffectsListed': 'Aucun effet secondaire n\'est répertorié pour {drug}. Cela ne signifie pas qu\'il n\'y en a pas -- demandez à votre pharmacien.',
  'chat.sideEffectsOf': 'Effets secondaires courants de {drug} :',
  'chat.sideEffectsAdvice': 'Si un effet secondaire est grave ou ne disparaît pas, contactez votre médecin.',
  'chat.drugNotFoundShort': 'Je n\'ai pas trouvé de médicament appelé "{drug}". Vérifiez l\'orthographe.',
  'chat.interactionWarning': 'ATTENTION : {drugA} et {drugB} ont une interaction connue.',
  'chat.severity': 'Gravité : {level}',
  'chat.interactionAdvice': 'Parlez à votre médecin ou pharmacien avant de combiner ces médicaments.',
  'chat.noInteractionFound': 'Aucune interaction connue n\'a été trouvée entre {drugA} et {drugB} dans notre base de données.',
  'chat.noInteractionDisclaimer': 'Cela ne garantit pas la sécurité. Confirmez toujours avec votre pharmacien.',
  'chat.foodInteractionWarning': 'ATTENTION : {drug} a une interaction connue avec {food}.',
  'chat.foodInteractionAdvice': 'Parlez à votre médecin ou pharmacien pour des conseils.',
  'chat.noFoodInteraction': 'Aucune interaction de {food} connue n\'a été trouvée pour {drug} dans notre base de données.',
  'chat.askPharmacist': 'En cas de doute, demandez à votre pharmacien.',
  'chat.someMedsInteractFood': 'ATTENTION : Certains de vos médicaments interagissent avec {food} :',
  'chat.noFoodInteractionAny': 'Aucune interaction de {food} connue n\'a été trouvée pour vos médicaments actuels.',
  'chat.whichDescription': 'De quel médicament souhaitez-vous en savoir plus ? Touchez-en un ci-dessus ou dites le nom.',
  'chat.noDescription': 'Aucune description disponible pour ce médicament.',
  'chat.whichDosage': 'Quel médicament ? Touchez-en un ci-dessus ou dites "comment prendre la metformine ?"',
  'chat.noDosageInstructions': 'Aucune instruction de dosage disponible. Veuillez consulter votre pharmacien.',
  'chat.howToTake': 'Comment prendre {drug} :',
  'chat.whichMissedDose': 'De quel médicament avez-vous oublié une dose ? Touchez-en un ci-dessus ou dites le nom.',
  'chat.noMissedDoseGuidance': 'Aucun conseil spécifique pour une dose oubliée n\'est disponible. Contactez votre pharmacien.',
  'chat.ifMissedDose': 'Si vous avez oublié une dose de {drug} :',
  'chat.noMedsSchedule': 'Vous n\'avez pas encore de médicaments enregistrés.',
  'chat.yourSchedule': 'Votre horaire de médicaments :',
  'chat.scheduleFrequency': 'Fréquence : {frequency}',
  'chat.scheduleLabel': 'Horaire : {times}',
  'chat.drugNotInList': '"{drug}" ne figure pas dans votre liste de médicaments. Vous pouvez demander "quels sont mes médicaments ?" pour voir ce qui est enregistré.',
  'chat.unknownHelp': 'Je suis votre spécialiste médical personnel avec IA. Je peux vous aider avec vos médicaments, résultats d\'analyses, diagnostics, allergies et plan de soins. Essayez de demander :\n\n  - "Que signifie mon HbA1c ?"\n  - "Pourquoi je prends la Metformine ?"\n  - "Quelles sont mes allergies ?"\n  - "Quand est mon prochain rendez-vous ?"\n  - "Parlez-moi de mes instructions de sortie"\n  - "Quels sont les effets secondaires du Lisinopril ?"\n  - "Puis-je boire de l\'alcool avec mes médicaments ?"',
  'chat.basedOn': 'Basé sur : {sources}',
  'chat.ariaConversation': 'Messages de conversation',
  'chat.ariaHoldToSpeak': 'Maintenez pour parler',
  'chat.ariaInputPlaceholder': 'Tapez une question sur vos médicaments',
  'chat.ariaUserMessage': 'Votre message',
  'chat.ariaAssistantMessage': 'Réponse de l\'assistant',
  'chat.ariaReplay': 'Réécouter cette réponse',
  'chat.andMore': '...et {count} de plus.',

  // --- Drug info ---
  'drugInfo.back': '← Retour',
  'drugInfo.backAriaLabel': 'Retour à la liste des médicaments',
  'drugInfo.overviewAriaLabel': 'Aperçu du médicament {drug}',
  'drugInfo.readAllAriaLabel': 'Lire toutes les informations du médicament à voix haute',
  'drugInfo.description': 'Description',
  'drugInfo.sideEffects': 'Effets Secondaires',
  'drugInfo.warnings': 'Avertissements',
  'drugInfo.speakAriaLabel': 'Lire {section} à voix haute',
  'drugInfo.expandAriaLabel': 'Développer la section {section}',
  'drugInfo.collapseAriaLabel': 'Réduire la section {section}',
  'drugInfo.tts.howToTake': 'Comment prendre: ',
  'drugInfo.tts.sideEffects': 'Les effets secondaires comprennent: ',
  'drugInfo.tts.warnings': 'Avertissements: ',
  'drugInfo.tts.foodInteractions': 'Interactions alimentaires: ',
  'drugInfo.tts.storage': 'Conservation: ',
  'drugInfo.tts.missedDose': 'Si vous oubliez une dose: ',
  'drugInfo.tts.noInstructions': 'Aucune instruction disponible.',

  // --- Setup ---
  'setup.step1.title': 'Quel médicament prenez-vous?',
  'setup.step1.hint': 'Tapez le nom ou maintenez le bouton du microphone pour le dire.',
  'setup.step1.placeholder': 'ex. Metformine, Lisinopril...',
  'setup.step2.title': 'Est-ce votre médicament?',
  'setup.step2.yes': 'Oui, c\'est correct',
  'setup.step2.no': 'Non, chercher à nouveau',
  'setup.step3.title': 'À quelle fréquence le prenez-vous?',
  'setup.step3.once': 'Une fois par jour',
  'setup.step3.twice': 'Deux fois par jour',
  'setup.step3.thrice': '3 fois par jour',
  'setup.step3.four': '4 fois par jour',
  'setup.step3.everyOther': 'Tous les deux jours',
  'setup.step3.weekly': 'Une fois par semaine',
  'setup.step3.asNeeded': 'Si nécessaire',
  'setup.step4.title': 'À quelle heure le prenez-vous?',
  'setup.step4.asNeededNote': 'Comme il est pris si nécessaire, aucun horaire n\'est requis.',
  'setup.step4.continue': 'Continuer',
  'setup.step4.hint': 'Sélectionnez un horaire prédéfini ou définissez une heure personnalisée.',
  'setup.step4.morning': 'Matin (8h00)',
  'setup.step4.noon': 'Midi (12h00)',
  'setup.step4.afternoon': 'Après-midi (14h00)',
  'setup.step4.evening': 'Soir (18h00)',
  'setup.step4.bedtime': 'Coucher (22h00)',
  'setup.step4.custom': 'Heure personnalisée:',
  'setup.step5.title': 'Instructions spéciales?',
  'setup.step5.hint': 'Par exemple: "Prendre avec de la nourriture" ou "Prendre à jeun"',
  'setup.step5.placeholder': 'ex. Prendre avec de la nourriture, éviter le pamplemousse...',
  'setup.step5.skip': 'Passer',
  'setup.step6.title': 'Confirmez Votre Médicament',
  'setup.step6.medication': 'Médicament',
  'setup.step6.strength': 'Dosage',
  'setup.step6.frequency': 'Fréquence',
  'setup.step6.times': 'Horaires',
  'setup.step6.instructions': 'Instructions',
  'setup.step6.notSpecified': 'Non spécifié',
  'setup.step6.save': 'Enregistrer le Médicament',
  'setup.step6.saving': 'Enregistrement...',
  'setup.step6.edit': 'Modifier les Détails',
  'setup.progress': 'Étape {n} sur {total}',
  'setup.back': '← Retour',
  'setup.cancel': 'Annuler',
  'setup.noResults': 'Aucun médicament trouvé. Essayez une autre orthographe.',
  'setup.success': '{drug} a été ajouté!',
  'setup.successSpeak': '{drug} a été ajouté à vos médicaments.',
  'setup.error': 'Échec de l\'enregistrement du médicament. Veuillez réessayer.',
  'setup.dose': 'Dose {n}',
  'setup.step1.speak': 'Quel médicament prenez-vous? Dites le nom ou tapez-le.',
  'setup.step2.speak': 'Est-ce que {drug} est votre médicament?',
  'setup.step3.speak': 'À quelle fréquence prenez-vous {drug}?',
  'setup.step4.speak': 'À quelle heure prenez-vous votre médicament?',
  'setup.step5.speak': 'Avez-vous des instructions spéciales pour ce médicament? Vous pouvez passer cette étape.',
  'setup.step6.speak': 'Veuillez vérifier et confirmer les détails de votre médicament.',
  'setup.step6.asNeeded': 'Si nécessaire',
  'setup.step6.freqOnce': 'Une fois par jour',
  'setup.step6.freqTwice': 'Deux fois par jour',
  'setup.step6.freqThrice': '3 fois par jour',
  'setup.step6.freqFour': '4 fois par jour',
  'setup.step6.freqEveryOther': 'Tous les deux jours',
  'setup.step6.freqWeekly': 'Hebdomadaire',
  'setup.step6.freqAsNeeded': 'Si nécessaire',
  'setup.backAriaLabel': 'Retour à l\'étape précédente',
  'setup.cancelAriaLabel': 'Annuler la configuration du médicament',
  'setup.searchResults': 'Résultats de recherche',

  // --- Camera ---
  'camera.title': 'Identifier un Comprimé',
  'camera.instruction': 'Pointez votre caméra vers le comprimé et touchez le bouton de capture.',
  'camera.starting': 'Démarrage de la caméra...',
  'camera.ready': 'Caméra prête. Positionnez le comprimé au centre.',
  'camera.noAccess': 'Impossible d\'accéder à la caméra. Vérifiez les permissions.',
  'camera.capture': 'Capturer',
  'camera.captureAgain': 'Capturer à Nouveau',
  'camera.analyzing': 'Analyse...',
  'camera.looksLike': 'Ceci ressemble à:',
  'camera.couldNotIdentify': 'Impossible d\'identifier ce comprimé.',
  'camera.viewDrugInfo': 'Voir les Informations Complètes',
  'camera.disclaimer': 'Cette identification est à titre indicatif uniquement. Vérifiez toujours avec votre pharmacien.',
  'camera.tips': 'Conseils pour de meilleurs résultats:',
  'camera.tip1': 'Placez le comprimé sur une surface claire et plate',
  'camera.tip2': 'Assurez un bon éclairage',
  'camera.tip3': 'Tenez la caméra stable et proche',
  'camera.tip4': 'Montrez les inscriptions ou marquages',
  'camera.analyzingStatus': 'Analyse de l\'image du comprimé...',
  'camera.identifiedStatus': 'Comprimé identifié. Voir les résultats ci-dessous.',
  'camera.couldNotIdentifyStatus': 'Impossible d\'identifier le comprimé. Essayez avec un meilleur éclairage.',
  'camera.timeoutStatus': 'L\'analyse a expiré. Essayez à nouveau.',
  'camera.noAccessSpeak': 'Impossible d\'accéder à la caméra. Veuillez vérifier les permissions de votre caméra.',
  'camera.noAccessNotify': 'Accès à la caméra refusé ou indisponible.',
  'camera.looksLikeSpeak': 'Ceci ressemble à {drug} avec {confidence} pour cent de confiance.',
  'camera.couldNotIdentifySpeak': 'Je n\'ai pas pu identifier ce comprimé. Veuillez réessayer avec un meilleur éclairage.',
  'camera.confidence': '{n}% de confiance',
  'camera.capturedAlt': 'Image capturée du comprimé',

  'error.somethingWrong': 'Quelque chose s\'est mal passé',
  'error.goHome': 'Aller à l\'Accueil',
  'purpose.bloodSugar': 'Glycémie',
  'purpose.bloodPressure': 'Tension artérielle',
  'purpose.cholesterol': 'Cholestérol',
  'emr.title': 'Dossier Médical',
  'emr.allergies': 'Allergies',
  'emr.diagnoses': 'Diagnostics',
  'emr.labResults': 'Résultats de Laboratoire',
  'emr.vitals': 'Signes Vitaux',
  'emr.discharge': 'Résumé de Sortie',
  'emr.careGoals': 'Objectifs de Soins',
  'emr.appointments': 'Rendez-vous',
  'emr.immunizations': 'Vaccinations',
  'emr.insurance': 'Assurance',
  'emr.emergency': "Contacts d'Urgence",
  'emr.directives': 'Directives Anticipées',
  'emr.noData': 'Aucun dossier médical disponible.',
  'emr.noRecord': 'Aucun dossier médical chargé.',
  'emr.noRecordHint': 'Vos données médicales apparaîtront ici une fois chargées.',

  // --- EMR detail strings ---
  'emr.dob': 'Date naiss. : ',
  'emr.mrn': 'NDP : ',
  'emr.nkda': 'AAMC (Aucune Allergie Médicamenteuse Connue)',
  'emr.active': 'actif',
  'emr.resolved': 'résolu',
  'emr.inProgress': 'en cours',
  'emr.notStarted': 'non commencé',
  'emr.severity.mild': 'légère',
  'emr.severity.moderate': 'modérée',
  'emr.severity.severe': 'sévère',
  'emr.severity.life-threatening': 'potentiellement mortelle',
  'emergency.call911': 'Cela ressemble à une urgence médicale. Veuillez appeler le 911 maintenant. N\'attendez pas. Si quelqu\'un est près de vous, demandez-lui d\'appeler. Votre sécurité passe en premier.',
  'emergency.crisis': 'Je vous entends, et je suis content que vous m\'en ayez parlé. Veuillez appeler ou envoyer un message au 988 maintenant. C\'est la Ligne de Prévention du Suicide et de Crise. Ils sont disponibles jour et nuit, et ils peuvent vous aider. Vous n\'êtes pas seul.',
  'emergency.callDoctor': 'Ce que vous décrivez m\'inquiète. C\'est quelque chose que votre médecin devrait savoir immédiatement. Veuillez appeler le cabinet de votre médecin dès que possible. Si vous ne pouvez pas les joindre, allez aux urgences. N\'attendez pas pour voir si ça s\'aggrave.',
  'camera.color': 'Couleur',
  'camera.shape': 'Forme',
  'camera.imprint': 'Inscription',
  'emr.onset': 'Début : ',
  'emr.abnormal': 'anormal',
  'emr.allNormal': 'Tout normal',
  'emr.ref': 'Réf : ',
  'emr.noVitals': 'Aucun signe vital enregistré.',
  'emr.none': 'Aucun',
  'emr.dose': 'Dose',
  'emr.other': 'Autre',

  // --- Lab badges ---
  'emr.lab.normal': 'Normal',
  'emr.lab.high': 'Élevé',
  'emr.lab.low': 'Bas',
  'emr.lab.criticalHigh': 'Critique Élevé',
  'emr.lab.criticalLow': 'Critique Bas',

  // --- Vital sign labels ---
  'emr.vital.bloodPressure': 'Pression Artérielle',
  'emr.vital.heartRate': 'Fréquence Cardiaque',
  'emr.vital.temperature': 'Température',
  'emr.vital.o2Saturation': 'Saturation O2',
  'emr.vital.weight': 'Poids',
  'emr.vital.bmi': 'IMC',
  'emr.vital.respRate': 'Fréq. Respiratoire',
  'emr.vital.painLevel': 'Niveau de Douleur',

  // --- Discharge summary ---
  'emr.hospitalCourse': 'Évolution Hospitalière',
  'emr.dischargeCondition': 'État à la Sortie',
  'emr.warningSigns': 'Signes d\'Alerte à Surveiller',

  // --- Care plan ---
  'emr.goals': 'objectifs',
  'emr.current': 'Actuel : ',
  'emr.target': 'Objectif : ',
  'emr.achieved': 'atteint',

  // --- Insurance fields ---
  'emr.ins.provider': 'Assureur',
  'emr.ins.plan': 'Formule',
  'emr.ins.memberId': 'N° d\'Adhérent',
  'emr.ins.groupNumber': 'N° de Groupe',
  'emr.ins.effective': 'En vigueur',
  'emr.ins.copayPrimary': 'Ticket Modérateur (Généraliste)',
  'emr.ins.copaySpecialist': 'Ticket Modérateur (Spécialiste)',
  'emr.ins.copayRx': 'Ticket Modérateur (Rx)',

  // --- Emergency contacts ---
  'emr.primaryContact': 'Principal',
  'emr.callContact': 'Appeler {name}',

  // --- Advance directives ---
  'emr.codeStatus': 'Statut de Réanimation',
  'emr.livingWill': 'Directives Anticipées',
  'emr.powerOfAttorney': 'Personne de Confiance',
  'emr.poaName': 'Nom de la Personne de Confiance',
  'emr.poaPhone': 'Tél. Personne de Confiance',
  'emr.lastReviewed': 'Dernière Révision',
  'emr.yes': 'Oui',
  'emr.no': 'Non',

  'home.minutes': '{n} min',
  'home.hours': '{n}h',
  'home.hoursMinutes': '{h}h {m}m',
  'schedule.missed': 'Manqué',
  'schedule.upcoming': 'A venir',
  'schedule.taken': 'Pris',
  'chat.welcomeWithMeds': 'Je suis Dr. AfterCare, votre spécialiste médical IA. J\'ai accès à votre dossier complet — {n} médicament{s}, résultats d\'analyses, diagnostics, allergies et plan de soins. Posez-moi vos questions.',
  'chat.welcomeNoMeds': 'Je suis Dr. AfterCare, votre spécialiste médical IA. Posez-moi des questions sur les médicaments, effets secondaires, interactions ou votre santé.',
  'chat.error': 'Désolé, quelque chose s\'est mal passé. Veuillez réessayer.',
  'chat.disclaimer': 'Veuillez confirmer avec votre médecin ou pharmacien avant de modifier votre traitement.',
  'chat.voiceNotUnderstood': 'Je n\'ai pas compris. Veuillez réessayer.',
  'chat.audioUnavailable': 'Audio indisponible',
  'chat.quick.whatDoes': 'Que fait {drug}?',
  'chat.quick.sideEffects': 'Effets secondaires de {drug}?',
  'chat.quick.howTake': 'Comment prendre {drug}?',
  'chat.quick.missedDose': 'Si j\'oublie une dose de {drug}?',
  'chat.quick.whenTake': 'Quand prendre {drug}?',
  'chat.quick.alcohol': 'Puis-je boire de l\'alcool avec {drug}?',
  'chat.suggest.myMeds': 'Que signifie mon HbA1c?',
  'chat.suggest.sideEffects': 'Pourquoi est-ce que je prends Metformine?',
  'chat.suggest.alcohol': 'Quelles sont mes allergies?',
  'chat.suggest.missedDose': 'Quand est mon prochain rendez-vous?',
  'medList.emptyHint': 'Touchez "Ajouter un Médicament" pour commencer.',
  'medList.frequency.once': 'Une fois par jour',
  'medList.frequency.twice': 'Deux fois par jour',
  'medList.frequency.thrice': '3 fois par jour',
  'medList.frequency.four': '4 fois par jour',
  'medList.frequency.everyOther': 'Un jour sur deux',
  'medList.frequency.weekly': 'Hebdomadaire',
  'medList.frequency.asNeeded': 'Selon besoin',
  'drugInfo.notFound': 'Information du médicament non trouvée.',
  'drugInfo.alsoKnownAs': 'Aussi connu sous: {brands}',
  'drugInfo.readAll': 'Lire toutes les informations',
  'drugInfo.howToTake': 'Comment Prendre',
  'drugInfo.foodInteractions': 'Interactions Alimentaires',
  'drugInfo.storage': 'Conservation',
  'drugInfo.missedDose': 'Dose Oubliée',
  'drugInfo.noInfo': 'Aucune information disponible.',
  'drugInfo.speakThis': 'Lire ceci',
  'drugInfo.tapEnlarge': 'Touchez pour agrandir',
  'drugInfo.tapClose': 'Touchez n\'importe où pour fermer',
  'model_downloading': 'Téléchargement de l\'assistant IA...',
  'model_download_once': 'Cela n\'arrive qu\'une seule fois.',
  'model_ready': 'Assistant IA prêt',
  'model_ready_detail': 'Vos questions seront maintenant traitées par l\'IA.',
  'model_download_failed': 'Échec du téléchargement IA',
  'model_fallback': 'Des réponses modèle seront utilisées.',
  'llm.retry': 'Réessayer',
  'error.tryAgain': 'Veuillez réessayer ou retourner à l\'écran d\'accueil.',
  'error.voiceNotReady': 'Le système vocal n\'est pas encore prêt. Veuillez patienter.',
  'error.micAccess': 'Impossible d\'accéder au microphone.',
  'error.notUnderstood': 'Impossible de comprendre. Veuillez réessayer.',
  'purpose.stomachAcid': 'Acidité gastrique',
  'purpose.nervePain': 'Douleur nerveuse',
  'purpose.moodAnxiety': 'Humeur et anxiété',
  'purpose.thyroid': 'Thyroïde',
  'purpose.painRelief': 'Soulagement de la douleur',
  'purpose.painSwelling': 'Douleur et gonflement',
  'purpose.heartProtection': 'Protection cardiaque',
  'purpose.bloodClots': 'Caillots sanguins',
  'purpose.fluidRemoval': 'Élimination des fluides',
  'purpose.inflammation': 'Inflammation',
  'purpose.breathing': 'Respiration',
  'voice.noMedsForDose': 'Vous n\'avez pas encore de medicaments configures. Souhaitez-vous en ajouter?',
  'voice.allDoneToday': 'Vous avez termine toutes vos doses pour aujourd\'hui. Bravo! Vos prochaines doses commencent demain matin.',
  'voice.takeNow': 'C\'est l\'heure de prendre votre {drug} maintenant.',
  'voice.nextIn': 'Votre prochain medicament est {drug}, dans environ {n} minutes a {time}.',
  'voice.nextAt': 'Votre prochain medicament est {drug} a {time}, dans environ {n} heure{s}.',
  'voice.scheduleCount': 'Vous avez {n} medicament{s} prevu{s} aujourd\'hui.',
  'voice.loggedTaken': 'C\'est note! J\'ai enregistre que vous avez pris votre {drug}. Bien joue!',
  'voice.loggedSkipped': 'D\'accord, j\'ai note que vous avez saute votre {drug}. Si vous avez des difficultes, parlez-en a votre medecin.',
  'voice.help': 'Voici comment je peux vous aider. Vous pouvez me demander: Que dois-je prendre ensuite? Parlez-moi d\'un medicament. Quels sont les effets secondaires? Puis-je manger certains aliments avec ca? J\'ai oublie ma dose. Pourquoi je prends ca? Vous pouvez aussi dire: J\'ai pris mon medicament.',
  'voice.unknown': 'Desole, je n\'ai pas compris. Vous pouvez demander: Que dois-je prendre ensuite? Parlez-moi de mes medicaments. Ou dites Aide.',
  'voice.sideEffects': 'Les effets secondaires courants de {drug} comprennent: {effects}. Si l\'un d\'eux vous derange ou persiste, parlez-en a votre medecin.',
  'voice.noSideEffects': 'Je n\'ai pas d\'informations specifiques sur les effets secondaires de {drug}. Consultez votre pharmacien ou medecin.',
  'voice.missedDose': 'Si vous avez manque une dose de {drug}: {advice}',
  'voice.missedDoseFallback': 'Si vous avez manque une dose de {drug}, prenez-la des que vous vous en souvenez. Mais s\'il est presque l\'heure de la prochaine dose, sautez celle que vous avez oubliee. Ne prenez jamais une double dose.',
  'voice.drugNotFound': 'Je n\'ai pas trouve d\'informations sur {drug}. Pourriez-vous essayer de redire le nom?',
  'voice.whichMed': 'De quel medicament parlez-vous? Dites simplement le nom et je le chercherai.',
  'voice.stopTaking': 'Veuillez ne pas arreter de prendre un medicament sans en parler d\'abord a votre medecin. Un arret soudain peut parfois causer des problemes.',
  'voice.alcohol': 'Il est generalement recommande d\'eviter l\'alcool en prenant {drug}. Consultez votre medecin pour des conseils specifiques.',
  'voice.gettingReady': 'Je me prepare a vous aider. Veuillez reessayer dans un instant.',
  'voice.drugInfoHowToTake': 'Voici comment le prendre: {instructions}',
  'voice.foodInteractions': 'Pour {drug}, soyez attentif a ces interactions alimentaires: {foods}.',
  'voice.noFoodInteractions': 'Je n\'ai pas d\'informations specifiques sur les interactions alimentaires pour {drug}. En general, suivez les instructions de votre pharmacien.',
  'voice.storage': 'Pour {drug}: {info}',
  'voice.storageFallback': 'Conservez {drug} a temperature ambiante, a l\'abri de l\'humidite et de la chaleur. Gardez-le dans son contenant d\'origine avec le couvercle bien ferme.',
  'voice.warnings': 'Avertissements importants pour {drug}: {warnings}. Si vous avez des preoccupations, veuillez contacter votre medecin.',
  'voice.noWarnings': 'Je n\'ai pas d\'avertissements specifiques pour {drug}. Suivez toujours les instructions de votre medecin.',
  'voice.whyTaking': 'Vous prenez {drug} parce que {reason}',
  'voice.whyTakingFallback': '{drug} a ete prescrit par votre medecin pour vous aider avec votre condition. Si vous souhaitez en savoir plus, demandez a votre medecin ou pharmacien.',
  'voice.howWorks': 'Voici comment {drug} fonctionne: {description}',
  'voice.howWorksFallback': 'Je n\'ai pas d\'informations detaillees sur le fonctionnement de {drug} dans votre corps. Votre pharmacien serait la meilleure personne a consulter.',
  'voice.graceful': 'C\'est une excellente question sur {topic}. Je n\'ai pas d\'informations detaillees a ce sujet pour le moment, mais votre pharmacien ou medecin serait la meilleure personne a consulter. Y a-t-il autre chose concernant vos medicaments avec lequel je peux vous aider?',
  'voice.didntHear': 'Je n\'ai rien entendu. Pourriez-vous reessayer?',
  'voice.youSaid': 'Vous avez dit: "{text}"',
  'voice.somethingWrong': 'Desole, quelque chose s\'est mal passe. Veuillez reessayer votre question.',
  'voice.thatMedication': 'ce medicament',
  'voice.yourMedication': 'votre medicament',
  'voice.whichMedFood': 'De quel medicament demandez-vous les interactions alimentaires?',
  'voice.whichMedStorage': 'Pour quel medicament souhaitez-vous les instructions de conservation?',
  'voice.whichMedInteractions': 'De quel medicament demandez-vous les interactions?',
  'voice.whichMedWhy': 'De quel medicament vous interrogez-vous?',
  'voice.whichMedHow': 'De quel medicament aimeriez-vous en savoir plus?',
  'voice.whichMedPillId': 'J\'aurais besoin de savoir quelle pilule vous regardez. Pouvez-vous me dire le nom sur la pilule?',
  'voice.pillIdentify': '{drug} est une {shape} {color}{imprint}. Cela correspond-il a ce que vous regardez?',
  'voice.pill': 'pilule',
  'voice.pillImprint': ' avec "{imprint}" inscrit dessus',
  'voice.driving': 'Certains medicaments peuvent provoquer de la somnolence. Verifiez l\'etiquette de votre {drug} ou demandez a votre pharmacien s\'il est prudent de conduire en le prenant.',
  'voice.genericVsBrand': '{drug} est le nom generique. Les noms de marque comprennent: {brands}. Ils contiennent le meme principe actif et fonctionnent de la meme maniere.',
  'voice.addMedication': 'Pour ajouter un nouveau medicament, allez dans l\'onglet Parametres. Votre medecin ou pharmacien peut vous aider a configurer de nouveaux medicaments dans l\'application.',
  'voice.topicOverdose': 'preoccupations de surdosage',
  'voice.topicPregnancy': 'securite des medicaments pendant la grossesse',
  'voice.topicAlcohol': 'interactions avec l\'alcool',
  'voice.topicDriving': 'conduite sous medicaments',
  'voice.topicGenericVsBrand': 'medicaments generiques vs de marque',
  'voice.topicCost': 'couts des medicaments',
  'voice.andJoiner': ' et ',
  'voice.scheduleItem': '{drug} a {times}.',

  // --- Conversation state machine (voice.conv.*) ---
  'voice.conv.cancelled': 'D\'accord, annule.',
  'voice.conv.logTakenConfirm': 'Compris. J\'enregistre que vous avez pris votre {drug}.',
  'voice.conv.logSkippedConfirm': 'Compris. J\'enregistre que vous avez saute votre {drug}.',
  'voice.conv.confirmed': 'Confirme.',
  'voice.conv.showEmr': 'J\'ouvre votre dossier de sante.',
  'voice.conv.labQuestion': 'Laissez-moi consulter vos resultats de laboratoire.',
  'voice.conv.emrDiagnosis': 'Voici vos diagnostics.',
  'voice.conv.emrFollowUp': 'Laissez-moi verifier vos prochains rendez-vous.',
  'voice.conv.emrDischargeInfo': 'Voici vos informations de sortie.',
  'voice.conv.nextDose': 'Laissez-moi verifier votre prochaine dose.',
  'voice.conv.whichMedInfo': 'Sur quel medicament aimeriez-vous en savoir plus?',
  'voice.conv.drugInfo': 'Je recherche des informations sur {drug}.',
  'voice.conv.whichMedSideEffects': 'Pour quel medicament demandez-vous les effets secondaires?',
  'voice.conv.sideEffectsNav': 'Voici les effets secondaires de {drug}.',
  'voice.conv.whichMedInteractions': 'Pour quel medicament aimeriez-vous verifier les interactions?',
  'voice.conv.interactionsNav': 'Je verifie les interactions de {drug}.',
  'voice.conv.whichMedFoodInteractions': 'Pour quel medicament demandez-vous les interactions alimentaires?',
  'voice.conv.foodInteractionsNav': 'Voici les interactions alimentaires de {drug}.',
  'voice.conv.whichMedTaken': 'Quel medicament avez-vous pris?',
  'voice.conv.confirmTaken': 'Vous dites avoir pris votre {drug}? Dites oui pour confirmer.',
  'voice.conv.whichMedSkipped': 'Quel medicament sautez-vous?',
  'voice.conv.confirmSkipped': 'Vous voulez sauter votre {drug}? Dites oui pour confirmer.',
  'voice.conv.pillShowDrug': 'Je vous montre a quoi ressemble {drug}.',
  'voice.conv.pillCamera': 'J\'ouvre la camera d\'identification de pilules.',
  'voice.conv.showSchedule': 'Voici votre horaire de medicaments.',
  'voice.conv.addMedication': 'J\'ouvre la configuration des medicaments.',
  'voice.conv.help': 'Voici ce que je peux faire. Dites "prochaine dose" pour voir votre prochain medicament. Dites "parle-moi de" suivi du nom du medicament pour les details. Dites "j\'ai pris mon" suivi du medicament pour enregistrer une dose. Dites "sauter" pour marquer une dose comme sautee. Dites "horaire" pour voir tous vos medicaments. Dites "effets secondaires" ou "interactions" pour les informations de securite. Dites "ajouter" pour configurer un nouveau medicament. Vous pouvez aussi me demander des choses comme "pourquoi je prends ca," "que faire si je manque une dose," "puis-je boire de l\'alcool avec ca," "puis-je conduire avec ca," "comment dois-je le conserver," ou "le generique est-il pareil." Je suis la pour vous aider a comprendre vos medicaments.',
  'voice.conv.unknown': 'Je n\'ai pas bien compris. Dites "aide" pour entendre ce que je peux faire.',
  'voice.conv.whichMedWhyTaking': 'De quel medicament parlez-vous? Dites le nom et je peux vous dire pour quoi il est couramment utilise.',
  'voice.conv.drugNotInDb': 'Je n\'ai pas {drug} dans ma base de donnees pour le moment. Votre pharmacien peut vous expliquer pourquoi il est prescrit.',
  'voice.conv.noDescriptionOnFile': 'Je n\'ai pas de description detaillee pour {drug} dans le dossier',
  'voice.conv.whyTaking': '{drug} est couramment prescrit pour ce qui suit. {description}. Votre medecin l\'a prescrit en fonction de vos besoins de sante specifiques. Si vous avez des questions sur pourquoi vous le prenez, c\'est une excellente conversation a avoir lors de votre prochain rendez-vous.',
  'voice.conv.whichMedMissed': 'Quel medicament avez-vous manque? Dites-moi le nom et je chercherai les instructions.',
  'voice.conv.missedDoseInfo': 'Voici les instructions pour une dose manquee de {drug}. {advice}. Si vous n\'etes pas sur de quoi faire, votre pharmacien peut vous aider avec un appel rapide.',
  'voice.conv.missedDoseFallback': 'Je n\'ai pas d\'instructions specifiques pour une dose manquee de {drug}. En regle generale, prenez-la des que vous vous en souvenez, sauf s\'il est presque l\'heure de votre prochaine dose. Ne doublez pas la dose. En cas de doute, appelez votre pharmacien.',
  'voice.conv.whichMedConcern': 'De quel medicament vous inquietez-vous? Dites-moi le nom et le symptome, et je verifierai.',
  'voice.conv.drugNotInDbConcern': 'Je n\'ai pas {drug} dans ma base de donnees. Si vous ressentez quelque chose qui vous inquiete, contactez votre medecin ou pharmacien.',
  'voice.conv.symptomKnown': '{symptom} est un effet secondaire connu de {drug}. C\'est generalement leger et peut s\'ameliorer a mesure que votre corps s\'adapte. Si c\'est severe ou ne disparait pas, contactez votre medecin. C\'est une bonne question a poser.',
  'voice.conv.symptomUnknown': '{symptom} n\'est pas un effet secondaire couramment liste pour {drug}. Ca pourrait ne pas etre lie, mais ca vaut la peine de le mentionner a votre medecin lors de votre prochain rendez-vous. Si ca semble grave, n\'attendez pas.',
  'voice.conv.sideEffectsList': 'Les effets secondaires les plus courants de {drug} incluent {effects}. Si vous ressentez quelque chose de specifique, dites-moi le symptome et je peux verifier. Et si quelque chose semble grave, contactez votre medecin immediatement.',
  'voice.conv.noSideEffectsList': 'Je n\'ai pas de liste d\'effets secondaires pour {drug} dans le dossier. Votre pharmacien peut vous guider sur ce qu\'il faut surveiller.',
  'voice.conv.whichMedHowWorks': 'Quel medicament aimeriez-vous mieux comprendre? Dites le nom et je vous expliquerai comment il fonctionne.',
  'voice.conv.howWorksFallback': 'Je n\'ai pas d\'explication detaillee sur le fonctionnement de {drug}. Votre pharmacien est une excellente ressource pour comprendre vos medicaments.',
  'voice.conv.howWorks': 'Voici comment {drug} fonctionne. {description}. Si vous voulez mieux comprendre comment il vous aide specifiquement, votre medecin peut vous expliquer en fonction de votre historique de sante.',
  'voice.conv.whichMedStorage': 'Pour quel medicament demandez-vous les instructions de conservation?',
  'voice.conv.storageInfo': 'Voici comment conserver {drug}. {info}. Un bon stockage aide a garantir que votre medicament reste efficace.',
  'voice.conv.storageFallback': 'Je n\'ai pas d\'instructions de conservation specifiques pour {drug}. La plupart des medicaments doivent etre conserves a temperature ambiante, a l\'abri de l\'humidite et de la lumiere directe du soleil. Verifiez l\'etiquette ou demandez a votre pharmacien.',
  'voice.conv.yourMedication': 'votre medicament',
  'voice.conv.overdose': 'Si vous pensez avoir pris trop de {drug}, appelez le Centre Antipoison immediatement au 1-800-222-1222. Si vous vous sentez tres mal ou avez du mal a respirer, appelez le 911. N\'attendez pas que les symptomes apparaissent. Il vaut toujours mieux appeler et etre en securite.',
  'voice.conv.whichMedPregnancy': 'De quel medicament parlez-vous? C\'est une question importante, et je veux vous donner la bonne information.',
  'voice.conv.pregnancyWarning': 'Voici ce que dit l\'etiquette sur {drug} et la grossesse. {warning}. Veuillez en parler a votre medecin des que possible. Ils peuvent vous aider a peser les avantages et les risques pour votre situation.',
  'voice.conv.pregnancyFallback': 'Parlez a votre medecin de la prise de {drug} pendant la grossesse ou l\'allaitement. C\'est une conversation importante a avoir des que possible. Votre medecin peut vous aider a decider ce qui est le plus sur pour vous et votre bebe.',
  'voice.conv.whichMedAlcohol': 'De quel medicament parlez-vous? Je peux verifier s\'il y a des interactions avec l\'alcool.',
  'voice.conv.alcoholInteraction': 'Voici ce que vous devez savoir sur l\'alcool et {drug}. {info}. Si vous avez des questions sur la quantite sans danger, votre pharmacien peut vous donner des conseils pratiques.',
  'voice.conv.alcoholWarning': 'Il y a un avertissement concernant l\'alcool avec {drug}. {warning}. Parlez a votre pharmacien si vous voulez plus de details sur ce que cela signifie pour vous.',
  'voice.conv.alcoholFallback': 'Consultez votre pharmacien au sujet de la consommation d\'alcool avec {drug}. De nombreux medicaments peuvent interagir avec l\'alcool de manieres non evidentes. Ils peuvent vous donner des conseils clairs.',
  'voice.conv.whichMedDriving': 'De quel medicament parlez-vous? Je peux verifier s\'il peut affecter votre capacite a conduire.',
  'voice.conv.drivingCaution': '{drug} peut causer de la somnolence ou des etourdissements. Soyez prudent en conduisant ou en utilisant des machines jusqu\'a ce que vous sachiez comment cela vous affecte. C\'est particulierement important lorsque vous commencez a le prendre ou apres un changement de dose.',
  'voice.conv.drivingOk': '{drug} n\'est generalement pas connu pour affecter la conduite, mais chaque personne reagit differemment. Soyez prudent jusqu\'a ce que vous sachiez comment cela vous affecte, surtout lorsque vous commencez a le prendre. Si vous vous sentez somnolent ou etourdi, ne conduisez pas.',
  'voice.conv.anyMedication': 'n\'importe quel medicament',
  'voice.conv.stopTaking': 'N\'arretez jamais de prendre {drug} sans en parler d\'abord a votre medecin. Certains medicaments doivent etre reduits progressivement. Un arret soudain pourrait provoquer des effets de sevrage ou aggraver votre etat. Si le cout ou les effets secondaires sont la raison pour laquelle vous pensez a arreter, dites-le a votre medecin. Il peut y avoir d\'autres options.',
  'voice.conv.whichMedGeneric': 'De quel medicament parlez-vous? Je peux vous renseigner sur les versions generique et de marque.',
  'voice.conv.genericVsBrand': 'Les medicaments generiques et de marque contiennent le meme principe actif et fonctionnent de la meme maniere. La FDA exige que les generiques soient tout aussi surs et efficaces. {drug} est le meme medicament que {brands}. Les pilules peuvent sembler differentes en couleur ou en forme, mais le medicament a l\'interieur est le meme. Si vous avez des inquietudes, votre pharmacien peut expliquer les differences.',
  'voice.conv.genericVsBrandFallback': 'Les medicaments generiques et de marque contiennent le meme principe actif et fonctionnent de la meme maniere. La FDA exige que les generiques soient tout aussi surs et efficaces. Les pilules peuvent sembler differentes, mais le medicament est le meme. Votre pharmacien peut vous montrer exactement a quoi vous attendre.',
  'voice.conv.cost': 'Je n\'ai pas d\'informations sur les prix, mais votre pharmacien peut vous aider a trouver le meilleur prix. Renseignez-vous sur les options generiques, les coupons du fabricant ou les programmes d\'assistance aux patients. De nombreux fabricants de medicaments proposent des programmes d\'economies que votre pharmacien peut vous aider a trouver.',

  'chat.gettingReady': 'Je me prepare a vous aider. Veuillez reessayer dans un instant.',
};

const ar: StringTable = {
  'nav.home': 'الرئيسية',
  'nav.medications': 'الأدوية',
  'nav.schedule': 'الجدول',
  'nav.ask': 'اسأل',
  'nav.records': 'السجلات',
  'home.tagline': 'دليلك للشفاء في المنزل',
  'home.greeting.morning': 'صباح الخير',
  'home.greeting.afternoon': 'مساء الخير',
  'home.greeting.evening': 'مساء الخير',
  'home.greeting.night': 'تصبح على خير',
  'home.doses': 'جرعات',
  'home.dosesTaken': '{taken} من {total} جرعة تم تناولها اليوم',
  'home.tookIt': '✓ تناولتها',
  'home.undo': 'تراجع',
  'home.upNext': 'التالي',
  'home.now': 'الآن',
  'home.yourMedications': 'أدويتك الـ {n}',
  'home.askAnything': 'اسألني أي شيء',
  'home.voiceHint': '"ماذا آخذ بعد ذلك؟" أو "أخبرني عن الميتفورمين"',
  'home.howPossible': 'كيف هذا ممكن؟',
  'home.healthRecord': 'سجلي الصحي',
  'home.healthRecordDesc': 'عرض التشخيصات والتحاليل وخطة الرعاية',
  'home.selectLanguage': 'اللغة',
  'greeting.withName': '{greeting}، {name}. مرحباً بك في بريدجبوينت أفتركير. أدويتك جاهزة. يمكنك سؤالي عن أي شيء يخص أدويتك.',
  'greeting.withoutName': '{greeting}. مرحباً بك في بريدجبوينت أفتركير، دليلك للشفاء في المنزل. يمكنك سؤالي عن أي شيء يخص أدويتك.',
  'overlay.tapToBegin': 'انقر في أي مكان للبدء',
  'medList.title': 'أدويتي',
  'medList.empty': 'لم تتم إضافة أدوية بعد.',
  'medList.addMedication': 'إضافة دواء',
  'medList.activeLabel': 'الأدوية النشطة',
  'medList.addLabel': 'إضافة دواء جديد',
  'schedule.title': 'جدول اليوم',
  'schedule.empty': 'لا توجد أدوية مجدولة لليوم.',
  'schedule.morning': 'الصباح',
  'schedule.afternoon': 'بعد الظهر',
  'schedule.evening': 'المساء',
  'schedule.bedtime': 'وقت النوم',
  'schedule.markTaken': '✓ تم التناول',
  'schedule.timelineLabel': 'جدول مواعيد الأدوية',
  'schedule.markTakenLabel': 'تحديد {drug} كتم تناوله',
  'schedule.iTookThis': 'تناولت هذا',
  'schedule.skip': 'تخطي',
  'schedule.skipDose': 'تخطي جرعة {drug}',
  'schedule.noMedsToday': 'لا توجد أدوية مجدولة لليوم.',
  'visual.pillAlt': 'حبة {name}: {color} {shape}',
  'visual.doesThisLookRight': 'هل يبدو هذا صحيحاً؟',
  'visual.pillCard': 'بطاقة حبة {name}',
  'visual.pillId': 'تعريف حبة {name}',
  'schedule.statusLabel': 'الحالة: {status}',
  'timer.timeToTake': 'حان وقت تناول {drug}',
  'timer.snoozedTitle': 'تذكير بالدواء (مؤجل)',
  'timer.snoozedBody': 'أجّلت هذا التذكير منذ 15 دقيقة.',
  'voice.holdToSpeak': 'اضغط مع الاستمرار للتحدث',
  'voice.listening': 'جاري الاستماع، حرر للإنهاء',
  'voice.processing': 'جاري معالجة كلامك',
  'voice.errorTryAgain': 'حدث خطأ ما. اضغط للمحاولة مرة أخرى.',
  'home.tapToStart': 'اضغط في أي مكان للبدء',
  'nav.mainNavigation': 'التنقل الرئيسي',
  'notify.notifications': 'الإشعارات',
  'notify.dismiss': 'إغلاق الإشعار',
  'install.title': 'تثبيت AfterCare',
  'install.subtitle': 'الوصول إلى أدويتك بدون إنترنت، في أي وقت',
  'install.button': 'تثبيت',
  'install.ariaLabel': 'تثبيت تطبيق AfterCare',
  'install.dismiss': 'إغلاق طلب التثبيت',
  'install.iosStep1Prefix': '1. اضغط على زر ',
  'install.iosShare': 'مشاركة',
  'install.iosInSafari': ' في Safari',
  'install.iosStep2Prefix': '2. مرر لأسفل واضغط على ',
  'install.iosAddToHome': '"إضافة إلى الشاشة الرئيسية"',
  'home.allRightsReserved': 'جميع الحقوق محفوظة',
  'chat.title': 'أخصائيك الطبي بالذكاء الاصطناعي',
  'chat.subtitle': 'اسأل عن الأدوية أو التحاليل أو التشخيصات أو خطة الرعاية',
  'chat.placeholder': 'اسأل عن صحتك...',
  'chat.send': 'إرسال',
  'chat.holdToSpeak': 'اضغط مع الاستمرار للتحدث',
  'chat.thinking': 'جاري التفكير...',
  'chat.llmLoading': 'مساعد الذكاء الاصطناعي قيد التحميل. يمكنك طرح أسئلة بسيطة الآن.',
  'chat.llmReady': 'مساعد الذكاء الاصطناعي جاهز. يمكنني الإجابة على أسئلة معقدة حول أدويتك.',
  'chat.aiDisclaimer': 'استجابة مولدة بالذكاء الاصطناعي. تأكد دائمًا مع طبيبك.',
  'chat.lookingUp': 'جاري البحث...',

  // --- Chat response strings (template fallback system) ---
  'chat.noMedsOnFile': 'ليس لديك أدوية مسجلة بعد. اطلب من الصيدلي أو الطبيب مساعدتك في إضافتها.',
  'chat.takingCount': 'أنت تتناول حالياً {count} دواء{s}:',
  'chat.whichSideEffects': 'ما الدواء الذي تريد معرفة آثاره الجانبية؟ اضغط على أحد أدويتك أعلاه أو قل الاسم.',
  'chat.drugNotFound': 'لم أتمكن من العثور على دواء يسمى "{drug}". يرجى التحقق من الإملاء أو تجربة الاسم العام.',
  'chat.noSideEffectsListed': 'لا توجد آثار جانبية مسجلة لـ {drug}. هذا لا يعني عدم وجودها -- اسأل الصيدلي.',
  'chat.sideEffectsOf': 'الآثار الجانبية الشائعة لـ {drug}:',
  'chat.sideEffectsAdvice': 'إذا كان أي أثر جانبي شديداً أو لم يختفِ، اتصل بطبيبك.',
  'chat.drugNotFoundShort': 'لم أتمكن من العثور على دواء يسمى "{drug}". يرجى التحقق من الإملاء.',
  'chat.interactionWarning': 'تحذير: {drugA} و{drugB} لديهما تفاعل معروف.',
  'chat.severity': 'الشدة: {level}',
  'chat.interactionAdvice': 'تحدث مع طبيبك أو الصيدلي قبل الجمع بين هذه الأدوية.',
  'chat.noInteractionFound': 'لم يتم العثور على تفاعل معروف بين {drugA} و{drugB} في قاعدة بياناتنا.',
  'chat.noInteractionDisclaimer': 'هذا لا يضمن السلامة. تأكد دائماً مع الصيدلي.',
  'chat.foodInteractionWarning': 'تحذير: {drug} لديه تفاعل معروف مع {food}.',
  'chat.foodInteractionAdvice': 'تحدث مع طبيبك أو الصيدلي للحصول على إرشادات.',
  'chat.noFoodInteraction': 'لم يتم العثور على تفاعل معروف لـ {food} مع {drug} في قاعدة بياناتنا.',
  'chat.askPharmacist': 'في حالة الشك، اسأل الصيدلي.',
  'chat.someMedsInteractFood': 'تحذير: بعض أدويتك تتفاعل مع {food}:',
  'chat.noFoodInteractionAny': 'لم يتم العثور على تفاعلات معروفة لـ {food} مع أي من أدويتك الحالية.',
  'chat.whichDescription': 'ما الدواء الذي تريد معرفة المزيد عنه؟ اضغط على أحدها أعلاه أو قل الاسم.',
  'chat.noDescription': 'لا يوجد وصف متاح لهذا الدواء.',
  'chat.whichDosage': 'أي دواء؟ اضغط على أحدها أعلاه أو قل "كيف آخذ الميتفورمين؟"',
  'chat.noDosageInstructions': 'لا توجد تعليمات جرعة متاحة. يرجى استشارة الصيدلي.',
  'chat.howToTake': 'كيفية تناول {drug}:',
  'chat.whichMissedDose': 'أي دواء فاتتك جرعته؟ اضغط على أحدها أعلاه أو قل الاسم.',
  'chat.noMissedDoseGuidance': 'لا تتوفر إرشادات محددة للجرعة الفائتة. اتصل بالصيدلي للحصول على نصيحة.',
  'chat.ifMissedDose': 'إذا فاتتك جرعة من {drug}:',
  'chat.noMedsSchedule': 'ليس لديك أدوية مسجلة بعد.',
  'chat.yourSchedule': 'جدول أدويتك:',
  'chat.scheduleFrequency': 'التكرار: {frequency}',
  'chat.scheduleLabel': 'الجدول: {times}',
  'chat.drugNotInList': '"{drug}" ليس في قائمة أدويتك. يمكنك أن تسأل "ما هي أدويتي؟" لمعرفة ما هو مسجل.',
  'chat.unknownHelp': 'أنا أخصائيك الطبي الشخصي بالذكاء الاصطناعي. يمكنني مساعدتك بأدويتك ونتائج التحاليل والتشخيصات والحساسيات وخطة الرعاية. جرب أن تسأل:\n\n  - "ماذا يعني HbA1c الخاص بي؟"\n  - "لماذا آخذ الميتفورمين؟"\n  - "ما هي حساسياتي؟"\n  - "متى موعدي القادم؟"\n  - "أخبرني عن تعليمات الخروج"\n  - "ما هي الآثار الجانبية للينوبريل؟"\n  - "هل يمكنني شرب الكحول مع أدويتي؟"',
  'chat.basedOn': 'بناءً على: {sources}',
  'chat.ariaConversation': 'رسائل المحادثة',
  'chat.ariaHoldToSpeak': 'اضغط مع الاستمرار للتحدث',
  'chat.ariaInputPlaceholder': 'اكتب سؤالاً عن أدويتك',
  'chat.ariaUserMessage': 'رسالتك',
  'chat.ariaAssistantMessage': 'رد المساعد',
  'chat.ariaReplay': 'إعادة تشغيل هذا الرد',
  'chat.andMore': '...و{count} المزيد.',

  // --- Drug info ---
  'drugInfo.back': 'رجوع ←',
  'drugInfo.backAriaLabel': 'العودة إلى قائمة الأدوية',
  'drugInfo.overviewAriaLabel': 'نظرة عامة على الدواء {drug}',
  'drugInfo.readAllAriaLabel': 'قراءة جميع معلومات الدواء بصوت عالٍ',
  'drugInfo.description': 'الوصف',
  'drugInfo.sideEffects': 'الآثار الجانبية',
  'drugInfo.warnings': 'تحذيرات',
  'drugInfo.speakAriaLabel': 'قراءة {section} بصوت عالٍ',
  'drugInfo.expandAriaLabel': 'توسيع قسم {section}',
  'drugInfo.collapseAriaLabel': 'طي قسم {section}',
  'drugInfo.tts.howToTake': 'كيفية التناول: ',
  'drugInfo.tts.sideEffects': 'الآثار الجانبية تشمل: ',
  'drugInfo.tts.warnings': 'تحذيرات: ',
  'drugInfo.tts.foodInteractions': 'التفاعلات الغذائية: ',
  'drugInfo.tts.storage': 'التخزين: ',
  'drugInfo.tts.missedDose': 'إذا فاتتك جرعة: ',
  'drugInfo.tts.noInstructions': 'لا توجد تعليمات متاحة.',

  // --- Setup ---
  'setup.step1.title': 'ما الدواء الذي تتناوله؟',
  'setup.step1.hint': 'اكتب الاسم أو اضغط مع الاستمرار على زر الميكروفون لنطقه.',
  'setup.step1.placeholder': 'مثال: ميتفورمين، ليسينوبريل...',
  'setup.step2.title': 'هل هذا دواؤك؟',
  'setup.step2.yes': 'نعم، هذا صحيح',
  'setup.step2.no': 'لا، ابحث مجدداً',
  'setup.step3.title': 'كم مرة تتناوله؟',
  'setup.step3.once': 'مرة واحدة يومياً',
  'setup.step3.twice': 'مرتين يومياً',
  'setup.step3.thrice': '3 مرات يومياً',
  'setup.step3.four': '4 مرات يومياً',
  'setup.step3.everyOther': 'كل يومين',
  'setup.step3.weekly': 'مرة في الأسبوع',
  'setup.step3.asNeeded': 'عند الحاجة',
  'setup.step4.title': 'في أي وقت تتناوله؟',
  'setup.step4.asNeededNote': 'بما أنه يؤخذ عند الحاجة، لا يلزم تحديد أوقات.',
  'setup.step4.continue': 'متابعة',
  'setup.step4.hint': 'اختر وقتاً محدداً مسبقاً أو حدد وقتاً مخصصاً.',
  'setup.step4.morning': 'الصباح (8:00 ص)',
  'setup.step4.noon': 'الظهر (12:00 م)',
  'setup.step4.afternoon': 'بعد الظهر (2:00 م)',
  'setup.step4.evening': 'المساء (6:00 م)',
  'setup.step4.bedtime': 'وقت النوم (10:00 م)',
  'setup.step4.custom': 'وقت مخصص:',
  'setup.step5.title': 'تعليمات خاصة؟',
  'setup.step5.hint': 'مثال: "تناوله مع الطعام" أو "تناوله على معدة فارغة"',
  'setup.step5.placeholder': 'مثال: تناوله مع الطعام، تجنب الجريب فروت...',
  'setup.step5.skip': 'تخطي',
  'setup.step6.title': 'أكد دواءك',
  'setup.step6.medication': 'الدواء',
  'setup.step6.strength': 'الجرعة',
  'setup.step6.frequency': 'التكرار',
  'setup.step6.times': 'الأوقات',
  'setup.step6.instructions': 'التعليمات',
  'setup.step6.notSpecified': 'غير محدد',
  'setup.step6.save': 'حفظ الدواء',
  'setup.step6.saving': 'جاري الحفظ...',
  'setup.step6.edit': 'تعديل التفاصيل',
  'setup.progress': 'الخطوة {n} من {total}',
  'setup.back': 'رجوع ←',
  'setup.cancel': 'إلغاء',
  'setup.noResults': 'لم يتم العثور على أدوية. جرب تهجئة مختلفة.',
  'setup.success': 'تمت إضافة {drug}!',
  'setup.successSpeak': 'تمت إضافة {drug} إلى أدويتك.',
  'setup.error': 'فشل في حفظ الدواء. يرجى المحاولة مرة أخرى.',
  'setup.dose': 'الجرعة {n}',
  'setup.step1.speak': 'ما الدواء الذي تتناوله؟ انطق الاسم أو اكتبه.',
  'setup.step2.speak': 'هل {drug} هو دواؤك؟',
  'setup.step3.speak': 'كم مرة تتناول {drug}؟',
  'setup.step4.speak': 'في أي وقت تتناول دواءك؟',
  'setup.step5.speak': 'هل لديك تعليمات خاصة لهذا الدواء؟ يمكنك تخطي هذه الخطوة.',
  'setup.step6.speak': 'يرجى مراجعة وتأكيد تفاصيل دوائك.',
  'setup.step6.asNeeded': 'عند الحاجة',
  'setup.step6.freqOnce': 'مرة واحدة يومياً',
  'setup.step6.freqTwice': 'مرتين يومياً',
  'setup.step6.freqThrice': '3 مرات يومياً',
  'setup.step6.freqFour': '4 مرات يومياً',
  'setup.step6.freqEveryOther': 'كل يومين',
  'setup.step6.freqWeekly': 'أسبوعياً',
  'setup.step6.freqAsNeeded': 'عند الحاجة',
  'setup.backAriaLabel': 'العودة إلى الخطوة السابقة',
  'setup.cancelAriaLabel': 'إلغاء إعداد الدواء',
  'setup.searchResults': 'نتائج البحث',

  // --- Camera ---
  'camera.title': 'تعريف حبة دواء',
  'camera.instruction': 'وجّه الكاميرا نحو الحبة واضغط على زر الالتقاط.',
  'camera.starting': 'جاري تشغيل الكاميرا...',
  'camera.ready': 'الكاميرا جاهزة. ضع الحبة في المنتصف.',
  'camera.noAccess': 'تعذر الوصول إلى الكاميرا. يرجى التحقق من الأذونات.',
  'camera.capture': 'التقاط',
  'camera.captureAgain': 'التقاط مجدداً',
  'camera.analyzing': 'جاري التحليل...',
  'camera.looksLike': 'يبدو أن هذا:',
  'camera.couldNotIdentify': 'تعذر تعريف هذه الحبة.',
  'camera.viewDrugInfo': 'عرض المعلومات الكاملة',
  'camera.disclaimer': 'هذا التعريف للمرجع فقط. تحقق دائماً مع الصيدلي.',
  'camera.tips': 'نصائح لنتائج أفضل:',
  'camera.tip1': 'ضع الحبة على سطح مسطح وفاتح اللون',
  'camera.tip2': 'تأكد من الإضاءة الجيدة',
  'camera.tip3': 'أمسك الكاميرا ثابتة وقريبة',
  'camera.tip4': 'أظهر أي نقوش أو علامات',
  'camera.analyzingStatus': 'جاري تحليل صورة الحبة...',
  'camera.identifiedStatus': 'تم تعريف الحبة. انظر النتائج أدناه.',
  'camera.couldNotIdentifyStatus': 'تعذر تعريف الحبة. حاول بإضاءة أفضل.',
  'camera.timeoutStatus': 'انتهت مهلة التحليل. حاول مرة أخرى.',
  'camera.noAccessSpeak': 'تعذر الوصول إلى الكاميرا. يرجى التحقق من أذونات الكاميرا.',
  'camera.noAccessNotify': 'تم رفض الوصول إلى الكاميرا أو أنها غير متاحة.',
  'camera.looksLikeSpeak': 'يبدو أن هذا {drug} بنسبة ثقة {confidence} بالمئة.',
  'camera.couldNotIdentifySpeak': 'لم أتمكن من تعريف هذه الحبة. يرجى المحاولة مرة أخرى بإضاءة أفضل.',
  'camera.confidence': '{n}% ثقة',
  'camera.capturedAlt': 'صورة ملتقطة للحبة',

  'error.somethingWrong': 'حدث خطأ ما',
  'error.goHome': 'الذهاب للرئيسية',
  'purpose.bloodSugar': 'سكر الدم',
  'purpose.bloodPressure': 'ضغط الدم',
  'purpose.cholesterol': 'الكوليسترول',
  'emr.title': 'السجل الطبي',
  'emr.allergies': 'الحساسيات',
  'emr.diagnoses': 'التشخيصات',
  'emr.labResults': 'نتائج المختبر',
  'emr.vitals': 'العلامات الحيوية',
  'emr.discharge': 'ملخص الخروج',
  'emr.careGoals': 'أهداف الرعاية',
  'emr.appointments': 'المواعيد',
  'emr.immunizations': 'التطعيمات',
  'emr.insurance': 'التأمين',
  'emr.emergency': 'جهات اتصال الطوارئ',
  'emr.directives': 'التوجيهات المسبقة',
  'emr.noData': 'لا توجد بيانات سجل طبي.',
  'emr.noRecord': 'لم يتم تحميل سجل طبي.',
  'emr.noRecordHint': 'ستظهر بياناتك الطبية هنا بعد تحميلها.',

  // --- EMR detail strings ---
  'emr.dob': 'تاريخ الميلاد: ',
  'emr.mrn': 'رقم الملف: ',
  'emr.nkda': 'لا توجد حساسية دوائية معروفة',
  'emr.active': 'نشط',
  'emr.resolved': 'تم حله',
  'emr.inProgress': 'قيد التنفيذ',
  'emr.notStarted': 'لم يبدأ',
  'emr.severity.mild': 'خفيفة',
  'emr.severity.moderate': 'متوسطة',
  'emr.severity.severe': 'شديدة',
  'emr.severity.life-threatening': 'مهددة للحياة',
  'emergency.call911': 'يبدو أن هذه حالة طوارئ طبية. يرجى الاتصال بـ 911 الآن فوراً. لا تنتظر. إذا كان هناك شخص بالقرب منك، اطلب منه الاتصال. سلامتك تأتي أولاً.',
  'emergency.crisis': 'أسمعك، وأنا سعيد أنك أخبرتني. يرجى الاتصال أو إرسال رسالة نصية إلى 988 الآن فوراً. هذا هو خط منع الانتحار والأزمات. هم متاحون على مدار الساعة، ويمكنهم المساعدة. أنت لست وحدك.',
  'emergency.callDoctor': 'أنا قلق بشأن ما تصفه. هذا شيء يجب أن يعرفه طبيبك فوراً. يرجى الاتصال بعيادة طبيبك في أقرب وقت ممكن. إذا لم تتمكن من الوصول إليهم، يرجى الذهاب إلى الرعاية العاجلة. لا تنتظر لترى إذا ساء الوضع.',
  'camera.color': 'اللون',
  'camera.shape': 'الشكل',
  'camera.imprint': 'النقش',
  'emr.onset': 'البداية: ',
  'emr.abnormal': 'غير طبيعي',
  'emr.allNormal': 'الكل طبيعي',
  'emr.ref': 'المرجع: ',
  'emr.noVitals': 'لا توجد علامات حيوية مسجلة.',
  'emr.none': 'لا يوجد',
  'emr.dose': 'جرعة',
  'emr.other': 'أخرى',

  // --- Lab badges ---
  'emr.lab.normal': 'طبيعي',
  'emr.lab.high': 'مرتفع',
  'emr.lab.low': 'منخفض',
  'emr.lab.criticalHigh': 'مرتفع حرج',
  'emr.lab.criticalLow': 'منخفض حرج',

  // --- Vital sign labels ---
  'emr.vital.bloodPressure': 'ضغط الدم',
  'emr.vital.heartRate': 'معدل ضربات القلب',
  'emr.vital.temperature': 'الحرارة',
  'emr.vital.o2Saturation': 'تشبع الأكسجين',
  'emr.vital.weight': 'الوزن',
  'emr.vital.bmi': 'مؤشر كتلة الجسم',
  'emr.vital.respRate': 'معدل التنفس',
  'emr.vital.painLevel': 'مستوى الألم',

  // --- Discharge summary ---
  'emr.hospitalCourse': 'مسار العلاج بالمستشفى',
  'emr.dischargeCondition': 'حالة الخروج',
  'emr.warningSigns': 'علامات تحذيرية يجب مراقبتها',

  // --- Care plan ---
  'emr.goals': 'أهداف',
  'emr.current': 'الحالي: ',
  'emr.target': 'المستهدف: ',
  'emr.achieved': 'تم تحقيقه',

  // --- Insurance fields ---
  'emr.ins.provider': 'شركة التأمين',
  'emr.ins.plan': 'الخطة',
  'emr.ins.memberId': 'رقم العضوية',
  'emr.ins.groupNumber': 'رقم المجموعة',
  'emr.ins.effective': 'ساري من',
  'emr.ins.copayPrimary': 'المشاركة (أساسي)',
  'emr.ins.copaySpecialist': 'المشاركة (أخصائي)',
  'emr.ins.copayRx': 'المشاركة (أدوية)',

  // --- Emergency contacts ---
  'emr.primaryContact': 'رئيسي',
  'emr.callContact': 'اتصل بـ {name}',

  // --- Advance directives ---
  'emr.codeStatus': 'حالة الإنعاش',
  'emr.livingWill': 'الوصية الحية',
  'emr.powerOfAttorney': 'التوكيل الطبي',
  'emr.poaName': 'اسم الوكيل',
  'emr.poaPhone': 'هاتف الوكيل',
  'emr.lastReviewed': 'آخر مراجعة',
  'emr.yes': 'نعم',
  'emr.no': 'لا',

  'home.minutes': '{n} د',
  'home.hours': '{n}س',
  'home.hoursMinutes': '{h}س {m}د',
  'schedule.missed': 'فائت',
  'schedule.upcoming': 'قادم',
  'schedule.taken': 'تم التناول',
  'chat.welcomeWithMeds': 'أنا دكتور أفتركير، أخصائيك الطبي بالذكاء الاصطناعي. لدي وصول لسجلك الطبي الكامل — {n} دواء، نتائج التحاليل، التشخيصات، الحساسيات وخطة الرعاية. اسألني ما تحتاج.',
  'chat.welcomeNoMeds': 'أنا دكتور أفتركير، أخصائيك الطبي بالذكاء الاصطناعي. اسألني عن الأدوية، الآثار الجانبية، التفاعلات أو صحتك.',
  'chat.error': 'عذراً، حدث خطأ ما. يرجى المحاولة مرة أخرى.',
  'chat.disclaimer': 'يرجى التأكد من طبيبك أو الصيدلي قبل إجراء أي تغييرات على رعايتك.',
  'chat.voiceNotUnderstood': 'لم أسمع ذلك. يرجى المحاولة مرة أخرى.',
  'chat.audioUnavailable': 'الصوت غير متاح',
  'chat.quick.whatDoes': 'ماذا يفعل {drug}؟',
  'chat.quick.sideEffects': 'الآثار الجانبية لـ {drug}؟',
  'chat.quick.howTake': 'كيف أتناول {drug}؟',
  'chat.quick.missedDose': 'ماذا لو فاتتني جرعة {drug}؟',
  'chat.quick.whenTake': 'متى أتناول {drug}؟',
  'chat.quick.alcohol': 'هل يمكنني شرب الكحول مع {drug}؟',
  'chat.suggest.myMeds': 'ماذا يعني HbA1c الخاص بي؟',
  'chat.suggest.sideEffects': 'لماذا أتناول الميتفورمين؟',
  'chat.suggest.alcohol': 'ما هي حساسياتي؟',
  'chat.suggest.missedDose': 'متى موعدي القادم؟',
  'medList.emptyHint': 'انقر على "إضافة دواء" للبدء.',
  'medList.frequency.once': 'مرة يومياً',
  'medList.frequency.twice': 'مرتين يومياً',
  'medList.frequency.thrice': '3 مرات يومياً',
  'medList.frequency.four': '4 مرات يومياً',
  'medList.frequency.everyOther': 'يوم بعد يوم',
  'medList.frequency.weekly': 'أسبوعياً',
  'medList.frequency.asNeeded': 'حسب الحاجة',
  'drugInfo.notFound': 'لم يتم العثور على معلومات الدواء.',
  'drugInfo.alsoKnownAs': 'يُعرف أيضاً بـ: {brands}',
  'drugInfo.readAll': 'قراءة جميع المعلومات',
  'drugInfo.howToTake': 'كيفية التناول',
  'drugInfo.foodInteractions': 'تفاعلات مع الطعام',
  'drugInfo.storage': 'التخزين',
  'drugInfo.missedDose': 'جرعة فائتة',
  'drugInfo.noInfo': 'لا توجد معلومات متاحة.',
  'drugInfo.speakThis': 'اقرأ هذا',
  'drugInfo.tapEnlarge': 'انقر للتكبير',
  'drugInfo.tapClose': 'انقر في أي مكان للإغلاق',
  'model_downloading': 'جاري تحميل مساعد الذكاء الاصطناعي...',
  'model_download_once': 'يحدث هذا مرة واحدة فقط.',
  'model_ready': 'مساعد الذكاء الاصطناعي جاهز',
  'model_ready_detail': 'سيتم الآن الرد على أسئلتك بواسطة الذكاء الاصطناعي.',
  'model_download_failed': 'فشل تحميل الذكاء الاصطناعي',
  'model_fallback': 'سيتم استخدام ردود النموذج بدلاً من ذلك.',
  'llm.retry': 'حاول مرة أخرى',
  'error.tryAgain': 'يرجى المحاولة مرة أخرى أو العودة إلى الشاشة الرئيسية.',
  'error.voiceNotReady': 'نظام الصوت ليس جاهزاً بعد. يرجى الانتظار.',
  'error.micAccess': 'لا يمكن الوصول إلى الميكروفون.',
  'error.notUnderstood': 'لم يتم الفهم. يرجى المحاولة مرة أخرى.',
  'purpose.stomachAcid': 'حموضة المعدة',
  'purpose.nervePain': 'ألم الأعصاب',
  'purpose.moodAnxiety': 'المزاج والقلق',
  'purpose.thyroid': 'الغدة الدرقية',
  'purpose.painRelief': 'تخفيف الألم',
  'purpose.painSwelling': 'الألم والتورم',
  'purpose.heartProtection': 'حماية القلب',
  'purpose.bloodClots': 'جلطات الدم',
  'purpose.fluidRemoval': 'إزالة السوائل',
  'purpose.inflammation': 'الالتهاب',
  'purpose.breathing': 'التنفس',
  'voice.noMedsForDose': 'ليس لديك أدوية مضبوطة بعد. هل تريد إضافة بعضها؟',
  'voice.allDoneToday': 'لقد أكملت جميع جرعاتك لليوم. أحسنت! جرعاتك القادمة تبدأ غداً صباحاً.',
  'voice.takeNow': 'حان وقت تناول {drug} الآن.',
  'voice.nextIn': 'دواؤك القادم هو {drug}، بعد حوالي {n} دقيقة في {time}.',
  'voice.nextAt': 'دواؤك القادم هو {drug} في {time}، بعد حوالي {n} ساعة.',
  'voice.scheduleCount': 'لديك {n} دواء مجدول لليوم.',
  'voice.loggedTaken': 'تم! سجلت أنك تناولت {drug}. أحسنت!',
  'voice.loggedSkipped': 'حسناً، لاحظت أنك تخطيت {drug}. إذا كنت تواجه صعوبة، تحدث مع طبيبك.',
  'voice.help': 'إليك ما يمكنني مساعدتك به. يمكنك أن تسألني: ماذا آخذ بعد ذلك؟ أخبرني عن دواء. ما هي الآثار الجانبية؟ هل يمكنني تناول أطعمة معينة مع هذا؟ نسيت تناول جرعتي. لماذا أتناول هذا؟',
  'voice.unknown': 'عذراً، لم أفهم ذلك. يمكنك السؤال: ماذا آخذ بعد ذلك؟ أخبرني عن أدويتي. أو قل مساعدة.',
  'voice.sideEffects': 'الآثار الجانبية الشائعة لـ {drug} تشمل: {effects}. إذا أزعجك أي منها أو لم تختفِ، تحدث مع طبيبك.',
  'voice.noSideEffects': 'ليس لدي معلومات محددة عن الآثار الجانبية لـ {drug}. استشر الصيدلي أو الطبيب.',
  'voice.missedDose': 'إذا فاتتك جرعة {drug}: {advice}',
  'voice.missedDoseFallback': 'إذا فاتتك جرعة {drug}، تناولها فور تذكرك. لكن إذا اقترب موعد الجرعة التالية، تجاوز الجرعة الفائتة. لا تتناول جرعة مضاعفة أبداً.',
  'voice.drugNotFound': 'لم أتمكن من العثور على معلومات عن {drug}. هل يمكنك محاولة قول الاسم مرة أخرى؟',
  'voice.whichMed': 'عن أي دواء تسأل؟ قل الاسم وسأبحث عنه.',
  'voice.stopTaking': 'يرجى عدم التوقف عن تناول أي دواء دون التحدث مع طبيبك أولاً. التوقف المفاجئ قد يسبب مشاكل.',
  'voice.alcohol': 'يُنصح عموماً بتجنب الكحول أثناء تناول {drug}. استشر طبيبك.',
  'voice.gettingReady': 'أنا أستعد لمساعدتك في ذلك. يرجى المحاولة مرة أخرى بعد لحظة.',
  'voice.drugInfoHowToTake': 'إليك كيفية تناوله: {instructions}',
  'voice.foodInteractions': 'بالنسبة لـ {drug}، انتبه لتفاعلات الطعام هذه: {foods}.',
  'voice.noFoodInteractions': 'ليس لدي معلومات محددة عن تفاعلات الطعام لـ {drug}. بشكل عام، اتبع تعليمات الصيدلي.',
  'voice.storage': 'بالنسبة لـ {drug}: {info}',
  'voice.storageFallback': 'احفظ {drug} في درجة حرارة الغرفة، بعيداً عن الرطوبة والحرارة. احتفظ به في العبوة الأصلية مع إغلاق الغطاء بإحكام.',
  'voice.warnings': 'تحذيرات مهمة لـ {drug}: {warnings}. إذا كانت لديك أي مخاوف، يرجى الاتصال بطبيبك.',
  'voice.noWarnings': 'ليس لدي تحذيرات محددة لـ {drug}. اتبع دائماً تعليمات طبيبك.',
  'voice.whyTaking': 'أنت تتناول {drug} لأن {reason}',
  'voice.whyTakingFallback': 'وصف لك طبيبك {drug} للمساعدة في حالتك. إذا كنت تريد معرفة المزيد عن سبب تناولك له، يرجى سؤال طبيبك أو الصيدلي.',
  'voice.howWorks': 'إليك كيف يعمل {drug}: {description}',
  'voice.howWorksFallback': 'ليس لدي معلومات مفصلة عن كيفية عمل {drug} في جسمك. الصيدلي سيكون أفضل شخص لسؤاله عن هذا.',
  'voice.graceful': 'هذا سؤال رائع عن {topic}. ليس لدي معلومات مفصلة عن ذلك الآن، لكن الصيدلي أو الطبيب سيكون أفضل شخص لسؤاله. هل هناك شيء آخر عن أدويتك يمكنني مساعدتك به؟',
  'voice.didntHear': 'لم أسمع شيئاً. هل يمكنك المحاولة مرة أخرى؟',
  'voice.youSaid': 'قلت: "{text}"',
  'voice.somethingWrong': 'عذراً، حدث خطأ ما. يرجى محاولة السؤال مرة أخرى.',
  'voice.thatMedication': 'ذلك الدواء',
  'voice.yourMedication': 'دواؤك',
  'voice.whichMedFood': 'عن أي دواء تسأل بخصوص تفاعلات الطعام؟',
  'voice.whichMedStorage': 'لأي دواء تريد تعليمات التخزين؟',
  'voice.whichMedInteractions': 'عن أي دواء تسأل بخصوص التفاعلات الدوائية؟',
  'voice.whichMedWhy': 'عن أي دواء تتساءل؟',
  'voice.whichMedHow': 'عن أي دواء تريد أن تعرف؟',
  'voice.whichMedPillId': 'أحتاج أن أعرف أي حبة تنظر إليها. هل يمكنك إخباري بالاسم المكتوب على الحبة؟',
  'voice.pillIdentify': '{drug} هي {shape} {color}{imprint}. هل يتطابق ذلك مع ما تنظر إليه؟',
  'voice.pill': 'حبة',
  'voice.pillImprint': ' مكتوب عليها "{imprint}"',
  'voice.driving': 'بعض الأدوية قد تسبب النعاس. تحقق من ملصق {drug} أو اسأل الصيدلي إذا كان من الآمن القيادة أثناء تناوله.',
  'voice.genericVsBrand': '{drug} هو الاسم العام. الأسماء التجارية تشمل: {brands}. تحتوي على نفس المادة الفعالة وتعمل بنفس الطريقة.',
  'voice.addMedication': 'لإضافة دواء جديد، اذهب إلى تبويب الإعدادات. يمكن لطبيبك أو الصيدلي مساعدتك في إعداد أدوية جديدة في التطبيق.',
  'voice.topicOverdose': 'مخاوف الجرعة الزائدة',
  'voice.topicPregnancy': 'سلامة الأدوية أثناء الحمل',
  'voice.topicAlcohol': 'تفاعلات الكحول',
  'voice.topicDriving': 'القيادة أثناء تناول الأدوية',
  'voice.topicGenericVsBrand': 'الأدوية العامة مقابل ذات العلامة التجارية',
  'voice.topicCost': 'تكاليف الأدوية',
  'voice.andJoiner': ' و ',
  'voice.scheduleItem': '{drug} في الساعة {times}.',

  // --- Conversation state machine (voice.conv.*) ---
  'voice.conv.cancelled': 'حسنا، تم الإلغاء.',
  'voice.conv.logTakenConfirm': 'فهمت. أسجل أنك تناولت {drug}.',
  'voice.conv.logSkippedConfirm': 'فهمت. أسجل أنك تخطيت {drug}.',
  'voice.conv.confirmed': 'تم التأكيد.',
  'voice.conv.showEmr': 'أفتح سجلك الصحي.',
  'voice.conv.labQuestion': 'دعني أراجع نتائج مختبرك.',
  'voice.conv.emrDiagnosis': 'إليك تشخيصاتك.',
  'voice.conv.emrFollowUp': 'دعني أتحقق من مواعيدك القادمة.',
  'voice.conv.emrDischargeInfo': 'إليك معلومات خروجك من المستشفى.',
  'voice.conv.nextDose': 'دعني أتحقق من جرعتك القادمة.',
  'voice.conv.whichMedInfo': 'عن أي دواء تريد أن تعرف؟',
  'voice.conv.drugInfo': 'أبحث عن معلومات حول {drug}.',
  'voice.conv.whichMedSideEffects': 'عن أي دواء تسأل بخصوص الآثار الجانبية؟',
  'voice.conv.sideEffectsNav': 'إليك الآثار الجانبية لـ {drug}.',
  'voice.conv.whichMedInteractions': 'أي دواء تريد التحقق من تفاعلاته؟',
  'voice.conv.interactionsNav': 'أتحقق من تفاعلات {drug}.',
  'voice.conv.whichMedFoodInteractions': 'عن أي دواء تسأل بخصوص تفاعلات الطعام؟',
  'voice.conv.foodInteractionsNav': 'إليك تفاعلات الطعام لـ {drug}.',
  'voice.conv.whichMedTaken': 'أي دواء تناولت؟',
  'voice.conv.confirmTaken': 'هل قلت أنك تناولت {drug}؟ قل نعم للتأكيد.',
  'voice.conv.whichMedSkipped': 'أي دواء تتخطاه؟',
  'voice.conv.confirmSkipped': 'تريد تخطي {drug}؟ قل نعم للتأكيد.',
  'voice.conv.pillShowDrug': 'أعرض لك شكل {drug}.',
  'voice.conv.pillCamera': 'أفتح كاميرا تعريف الحبوب.',
  'voice.conv.showSchedule': 'إليك جدول أدويتك.',
  'voice.conv.addMedication': 'أفتح إعداد الأدوية.',
  'voice.conv.help': 'إليك ما يمكنني فعله. قل "الجرعة القادمة" لرؤية دوائك القادم. قل "أخبرني عن" متبوعا باسم الدواء للتفاصيل. قل "تناولت" متبوعا بالدواء لتسجيل جرعة. قل "تخطي" لتحديد جرعة كمتخطاة. قل "الجدول" لرؤية جميع أدويتك. قل "آثار جانبية" أو "تفاعلات" لمعلومات السلامة. قل "إضافة" لإعداد دواء جديد. يمكنك أيضا أن تسألني أشياء مثل "لماذا أتناول هذا" أو "ماذا لو فاتتني جرعة" أو "هل يمكنني شرب الكحول مع هذا" أو "هل يمكنني القيادة مع هذا" أو "كيف أخزن هذا" أو "هل الدواء العام نفسه." أنا هنا لمساعدتك على فهم أدويتك.',
  'voice.conv.unknown': 'لم أفهم ذلك جيدا. قل "مساعدة" لسماع ما يمكنني فعله.',
  'voice.conv.whichMedWhyTaking': 'عن أي دواء تسأل؟ قل الاسم ويمكنني إخبارك بما يستخدم له عادة.',
  'voice.conv.drugNotInDb': 'ليس لدي {drug} في قاعدة بياناتي الآن. يمكن للصيدلي أن يشرح لماذا تم وصفه.',
  'voice.conv.noDescriptionOnFile': 'ليس لدي وصف تفصيلي لـ {drug} في الملف',
  'voice.conv.whyTaking': '{drug} يوصف عادة لما يلي. {description}. وصفه طبيبك بناء على احتياجاتك الصحية المحددة. إذا كانت لديك أسئلة حول لماذا تتناوله، فهذه محادثة رائعة لإجرائها في زيارتك القادمة.',
  'voice.conv.whichMedMissed': 'أي دواء فاتك؟ أخبرني بالاسم وسأبحث عن الإرشادات.',
  'voice.conv.missedDoseInfo': 'إليك الإرشادات لجرعة فائتة من {drug}. {advice}. إذا لم تكن متأكدا ماذا تفعل، يمكن للصيدلي مساعدتك بمكالمة سريعة.',
  'voice.conv.missedDoseFallback': 'ليس لدي تعليمات محددة لجرعة فائتة من {drug}. كقاعدة عامة، تناولها فور تذكرك ما لم يكن قد حان وقت الجرعة التالية تقريبا. لا تضاعف الجرعة. إذا لم تكن متأكدا، اتصل بالصيدلي.',
  'voice.conv.whichMedConcern': 'عن أي دواء أنت قلق؟ أخبرني بالاسم والعرض، وسأتحقق.',
  'voice.conv.drugNotInDbConcern': 'ليس لدي {drug} في قاعدة بياناتي. إذا كنت تعاني من شيء يقلقك، تواصل مع طبيبك أو الصيدلي.',
  'voice.conv.symptomKnown': '{symptom} هو أثر جانبي معروف لـ {drug}. هذا عادة خفيف وقد يتحسن مع تكيف جسمك. إذا كان شديدا أو لم يختف، تواصل مع طبيبك. هذا سؤال جيد لطرحه.',
  'voice.conv.symptomUnknown': '{symptom} ليس أثرا جانبيا مدرجا عادة لـ {drug}. قد يكون غير ذي صلة، لكنه يستحق الذكر لطبيبك في زيارتك القادمة. إذا بدا خطيرا، لا تنتظر.',
  'voice.conv.sideEffectsList': 'الآثار الجانبية الأكثر شيوعا لـ {drug} تشمل {effects}. إذا كنت تعاني من شيء محدد، أخبرني بالعرض ويمكنني التحقق. وإذا بدا أي شيء خطيرا، تواصل مع طبيبك فورا.',
  'voice.conv.noSideEffectsList': 'ليس لدي قائمة آثار جانبية لـ {drug} في الملف. يمكن للصيدلي إرشادك حول ما يجب مراقبته.',
  'voice.conv.whichMedHowWorks': 'أي دواء تريد فهمه بشكل أفضل؟ قل الاسم وسأشرح كيف يعمل.',
  'voice.conv.howWorksFallback': 'ليس لدي شرح تفصيلي لكيفية عمل {drug}. الصيدلي مورد رائع لفهم أدويتك.',
  'voice.conv.howWorks': 'إليك كيف يعمل {drug}. {description}. إذا أردت فهم المزيد عن كيف يساعدك تحديدا، يمكن لطبيبك الشرح بناء على تاريخك الصحي.',
  'voice.conv.whichMedStorage': 'عن أي دواء تسأل بخصوص التخزين؟',
  'voice.conv.storageInfo': 'إليك كيفية تخزين {drug}. {info}. التخزين الصحيح يساعد على ضمان بقاء دوائك فعالا.',
  'voice.conv.storageFallback': 'ليس لدي تعليمات تخزين محددة لـ {drug}. معظم الأدوية يجب أن تحفظ في درجة حرارة الغرفة، بعيدا عن الرطوبة وأشعة الشمس المباشرة. تحقق من الملصق أو اسأل الصيدلي للتأكد.',
  'voice.conv.yourMedication': 'دوائك',
  'voice.conv.overdose': 'إذا كنت تعتقد أنك تناولت كمية زائدة من {drug}، اتصل بمركز مكافحة السموم فورا على 1-800-222-1222. إذا شعرت بتوعك شديد أو صعوبة في التنفس، اتصل بـ 911. لا تنتظر ظهور الأعراض. من الأفضل دائما الاتصال والتأكد من سلامتك.',
  'voice.conv.whichMedPregnancy': 'عن أي دواء تسأل؟ هذا سؤال مهم، وأريد أن أعطيك المعلومات الصحيحة.',
  'voice.conv.pregnancyWarning': 'إليك ما يقوله الملصق عن {drug} والحمل. {warning}. يرجى التحدث مع طبيبك حول هذا في أقرب وقت ممكن. يمكنهم مساعدتك في الموازنة بين الفوائد والمخاطر لحالتك.',
  'voice.conv.pregnancyFallback': 'تحدث مع طبيبك حول تناول {drug} أثناء الحمل أو الرضاعة. هذه محادثة مهمة يجب إجراؤها في أقرب وقت ممكن. يمكن لطبيبك مساعدتك في تحديد الأكثر أمانا لك ولطفلك.',
  'voice.conv.whichMedAlcohol': 'عن أي دواء تسأل؟ يمكنني التحقق مما إذا كانت هناك تفاعلات مع الكحول.',
  'voice.conv.alcoholInteraction': 'إليك ما يجب أن تعرفه عن الكحول و {drug}. {info}. إذا كانت لديك أسئلة حول الكمية الآمنة، يمكن للصيدلي إعطاؤك إرشادات عملية.',
  'voice.conv.alcoholWarning': 'هناك تحذير بشأن الكحول مع {drug}. {warning}. تحدث مع الصيدلي إذا أردت مزيدا من التفاصيل حول ما يعنيه هذا لك.',
  'voice.conv.alcoholFallback': 'استشر الصيدلي حول شرب الكحول مع {drug}. العديد من الأدوية يمكن أن تتفاعل مع الكحول بطرق غير واضحة. يمكنهم إعطاؤك إرشادات واضحة.',
  'voice.conv.whichMedDriving': 'عن أي دواء تسأل؟ يمكنني التحقق مما إذا كان قد يؤثر على قدرتك على القيادة.',
  'voice.conv.drivingCaution': '{drug} قد يسبب النعاس أو الدوار. كن حذرا عند القيادة أو تشغيل الآلات حتى تعرف كيف يؤثر عليك. هذا مهم بشكل خاص عند البدء في تناوله أو بعد تغيير الجرعة.',
  'voice.conv.drivingOk': '{drug} لا يعرف عادة بتأثيره على القيادة، لكن كل شخص يستجيب بشكل مختلف. كن حذرا حتى تعرف كيف يؤثر عليك، خاصة عند البدء في تناوله. إذا شعرت بالنعاس أو الدوار، لا تقد.',
  'voice.conv.anyMedication': 'أي دواء',
  'voice.conv.stopTaking': 'لا تتوقف أبدا عن تناول {drug} دون التحدث مع طبيبك أولا. بعض الأدوية تحتاج إلى تقليل تدريجي. التوقف المفاجئ قد يسبب أعراض انسحاب أو قد تسوء حالتك. إذا كانت التكلفة أو الآثار الجانبية هي السبب في تفكيرك بالتوقف، أخبر طبيبك. قد تكون هناك خيارات أخرى.',
  'voice.conv.whichMedGeneric': 'عن أي دواء تسأل؟ يمكنني إخبارك عن النسخ العامة وذات العلامة التجارية.',
  'voice.conv.genericVsBrand': 'الأدوية العامة وذات العلامة التجارية تحتوي على نفس المادة الفعالة وتعمل بنفس الطريقة. تتطلب إدارة الغذاء والدواء أن تكون الأدوية العامة آمنة وفعالة بنفس القدر. {drug} هو نفس الدواء مثل {brands}. قد تبدو الحبوب مختلفة في اللون أو الشكل، لكن الدواء بالداخل هو نفسه. إذا كانت لديك مخاوف، يمكن للصيدلي شرح الاختلافات.',
  'voice.conv.genericVsBrandFallback': 'الأدوية العامة وذات العلامة التجارية تحتوي على نفس المادة الفعالة وتعمل بنفس الطريقة. تتطلب إدارة الغذاء والدواء أن تكون الأدوية العامة آمنة وفعالة بنفس القدر. قد تبدو الحبوب مختلفة، لكن الدواء هو نفسه. يمكن للصيدلي أن يوضح لك بالضبط ما يمكن توقعه.',
  'voice.conv.cost': 'ليس لدي معلومات عن الأسعار، لكن الصيدلي يمكنه مساعدتك في إيجاد أفضل سعر. اسأل عن الخيارات العامة أو كوبونات الشركة المصنعة أو برامج مساعدة المرضى. العديد من شركات الأدوية تقدم برامج توفير يمكن للصيدلي مساعدتك في إيجادها.',

  'chat.gettingReady': 'أنا أستعد لمساعدتك في ذلك. يرجى المحاولة مرة أخرى بعد لحظة.',
};

export const STRINGS: Record<LangCode, StringTable> = { en, es, pt, ht, fr, ar };
